import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const API_BASE = (process.env.REACT_APP_API_URL||'').replace('/api','').replace(/\/$/,'');
/* ── const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001'; ── */
/* ── //const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api','') : ''; ── */
const fotoUrl = url => url ? (url.startsWith('http') ? url : `/${url.replace(/^\/+/,'')}`) : null;
const TIPO_MAP = {'PAT':'Patológico','NPAT':'No patológico','PER':'Personal','FAM':'Familiar','QRX':'Quirúrgico','GIN':'Gineco-Obst.','OTR':'Otros'};
const TIPO_ICON = {'PAT':'🩺','NPAT':'🚬','FAM':'👪','QRX':'🔪','GIN':'🌸','OTR':'📋','PER':'📋'};
// Catálogo de condiciones frecuentes para carga rápida por chips (un clic marca/desmarca).
// No reemplaza el texto libre: lo que no esté en la lista se agrega en "Otro" de cada categoría.
const CATALOGO_ANTEC = {
  PAT:  ['Hipertensión','Diabetes','Cardiopatía','Enf. valvular','Endocrinológico','Cáncer','Tuberculosis','Enf. infecciosa','Enf. hereditaria','Asma','Enf. renal','Hipotiroidismo'],
  NPAT: ['Tabaquismo','Alcohol','Drogas','Sedentarismo','Actividad física regular','Dieta especial','Café/cafeína'],
  FAM:  ['Hipertensión','Diabetes','Cardiopatía','Cáncer','Enf. hereditaria','Enf. mental','Tuberculosis'],
};
const calcEdad = fn => fn ? Math.floor((Date.now()-new Date(fn))/3.156e10)+' años' : '—';
const fmtF  = s => { if(!s)return'—'; const d=new Date(s); return d.toLocaleDateString('es-EC',{day:'2-digit',month:'short',year:'numeric'})+' · '+d.toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'}); };
const fmtD  = s => { if(!s)return'—'; const p=String(s).slice(0,10); const[y,m,d]=p.split('-'); return `${d}/${m}/${y}`; };
const agrupar = rs => { const m={}; rs.forEach(r=>{ const y=new Date(r.FECHA).getFullYear(); if(!m[y])m[y]=[]; m[y].push(r); }); return Object.entries(m).sort((a,b)=>b[0]-a[0]); };

/* ── Evaluación signos vitales ── */
const SV_RANGOS = {
  PRESION_ARTERIAL: { eval: v => {
    if(!v)return'na'; const[s,d]=v.split('/').map(Number); if(!s||!d)return'na';
    if(s>=180||d>=120)return'critico'; if(s>=140||d>=90)return'alto'; if(s<90||d<60)return'bajo'; return'ok';
  }, label:'P. Arterial', unit:'mmHg', icon:'ti-activity' },
  PULSO: { eval: v => {
    const n=parseFloat(v); if(!n)return'na';
    if(n>120||n<40)return'critico'; if(n>100||n<55)return'alto'; return'ok';
  }, label:'Pulso', unit:'bpm', icon:'ti-heart' },
  SATURACION_OXIGENO: { eval: v => {
    const n=parseFloat(v); if(!n)return'na';
    if(n<90)return'critico'; if(n<95)return'alto'; return'ok';
  }, label:'Sat O₂', unit:'%', icon:'ti-lungs' },
  PESO:       { eval:()=>'ok', label:'Peso',   unit:'kg',  icon:'ti-scale' },
  TALLA:      { eval:()=>'ok', label:'Talla',  unit:'cm',  icon:'ti-ruler' },
  TEMP_AXILAR:{ eval: v => {
    const n=parseFloat(v); if(!n)return'na';
    if(n>=39.5||n<35)return'critico'; if(n>=37.5||n<36)return'alto'; return'ok';
  }, label:'Temp.', unit:'°C', icon:'ti-thermometer' },
  RESPIRATORIA:{ eval: v => {
    const n=parseFloat(v); if(!n)return'na';
    if(n>28||n<8)return'critico'; if(n>20||n<12)return'alto'; return'ok';
  }, label:'Fr. Resp.', unit:'rpm', icon:'ti-wind' },
  // Sin evaluación de riesgo (los cortes de la OMS dependen del sexo, ej. hombre >102cm /
  // mujer >88cm) — se muestra el valor tal cual, igual que Peso/Talla.
  CINTURA_ABDOMINAL:{ eval:()=>'ok', label:'Cint. abdom.', unit:'cm', icon:'ti-tape-measure' },
};

/* ── IMC / peso ideal — calculados a partir de Peso+Talla ya registrados,
   sin columnas nuevas: solo lectura, útil para Medicina Interna/Obesidad ── */
const calcIMC = (peso, talla) => {
  const p = parseFloat(peso), t = parseFloat(talla);
  if (!p || !t) return null;
  return p / Math.pow(t / 100, 2);
};
const imcEstado = v => {
  if (v == null) return 'na';
  if (v < 18.5) return 'alto';   // bajo peso
  if (v < 25)   return 'ok';     // normal
  if (v < 30)   return 'alto';   // sobrepeso
  return 'critico';              // obesidad (I/II/III)
};
const imcEtiqueta = v => {
  if (v == null) return '';
  if (v < 18.5) return 'Bajo peso';
  if (v < 25)   return 'Normal';
  if (v < 30)   return 'Sobrepeso';
  if (v < 35)   return 'Obesidad I';
  if (v < 40)   return 'Obesidad II';
  return 'Obesidad III';
};
// Peso ideal de referencia = IMC 22 (punto medio del rango normal) para la talla del paciente.
const calcPesoIdeal = talla => {
  const t = parseFloat(talla);
  if (!t) return null;
  return 22 * Math.pow(t / 100, 2);
};

/* ── Dictado por voz de signos vitales — Web Speech API del navegador
   (gratis, sin OpenAI ni backend: todo corre en el cliente). Extrae
   valores de un texto dictado tipo "presión 120 80, pulso 72, peso 68".
   Es un reconocimiento por palabras clave (no entiende contexto como el
   asistente de IA de la nota completa) — mejor esfuerzo, no perfecto. ── */
const parseSignosDictado = (texto) => {
  const t = ' ' + texto.toLowerCase().replace(/[.,]/g, ' ') + ' ';
  const num = re => { const m = t.match(re); return m ? m[1].replace(',', '.') : null; };
  const out = {};
  const pa = t.match(/(?:presi[oó]n(?:\s+arterial)?|tensi[oó]n)\D{0,8}(\d{2,3})\D{1,6}(\d{2,3})/)
          || t.match(/(\d{2,3})\s*(?:\/|sobre)\s*(\d{2,3})/);
  if (pa) out.PRESION_ARTERIAL = `${pa[1]}/${pa[2]}`;
  const pulso = num(/(?:pulso|frecuencia card[ií]aca)\D{0,8}(\d{2,3}(?:[.,]\d+)?)/);
  if (pulso) out.PULSO = pulso;
  const resp = num(/(?:frecuencia respiratoria|respiratoria)\D{0,8}(\d{1,2}(?:[.,]\d+)?)/);
  if (resp) out.RESPIRATORIA = resp;
  const sat = num(/(?:saturaci[oó]n(?:\s+de\s+ox[ií]geno)?|spo\s*2|sat\s*o\s*2)\D{0,8}(\d{2,3}(?:[.,]\d+)?)/);
  if (sat) out.SATURACION_OXIGENO = sat;
  const peso = num(/peso\D{0,8}(\d{2,3}(?:[.,]\d+)?)/);
  if (peso) out.PESO = peso;
  const talla = num(/(?:talla|estatura)\D{0,8}(\d{2,3}(?:[.,]\d+)?)/);
  if (talla) out.TALLA = talla;
  const cint = num(/(?:cintura|per[ií]metro abdominal)\D{0,8}(\d{2,3}(?:[.,]\d+)?)/);
  if (cint) out.CINTURA_ABDOMINAL = cint;
  const temp = num(/temperatura\D{0,8}(\d{2}(?:[.,]\d+)?)/);
  if (temp) out.TEMP_AXILAR = temp;
  return out;
};

/* ── CSS — 100% variables del sistema ── */
const css = `
.hc-page { display:flex; flex-direction:column; height:calc(100vh - var(--topbar-h,52px)); }

/* ════ HEADER ════ */
.hc-hdr {
  background:var(--navy); padding:10px 16px;
  display:flex; align-items:center; gap:12px;
  flex-shrink:0; border-radius:12px 12px 0 0;
}
.hdr-avatar {
  width:44px; height:44px; border-radius:50%;
  background:var(--navy-lt); color:#fff;
  display:flex; align-items:center; justify-content:center;
  font-size:15px; font-weight:700; flex-shrink:0; overflow:hidden;
}
.hdr-avatar img { width:100%; height:100%; object-fit:cover; }
.hdr-info   { flex:1; min-width:0; }
.hdr-name   { font-size:14px; font-weight:700; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.hdr-sub    { font-size:10px; color:rgba(255,255,255,.5); margin-top:2px; }
.hdr-meta   { display:flex; gap:6px; flex-wrap:wrap; margin-top:5px; }
.hdr-chip   { font-size:9px; padding:2px 8px; border-radius:20px; border:0.5px solid rgba(255,255,255,.2); color:rgba(255,255,255,.7); white-space:nowrap; }
.hdr-chip.alg { background:rgba(176,48,48,.25); border-color:rgba(176,48,48,.5); color:#fca5a5; }
.hdr-actions { display:flex; flex-direction:column; gap:5px; align-items:flex-end; flex-shrink:0; }
.btn-nueva-hc { background:var(--navy-lt); color:#fff; border:none; border-radius:8px; padding:7px 14px; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap; }
.btn-nueva-hc:hover { background:var(--blue-mid); }
.hdr-btns-row { display:flex; gap:4px; }
.hdr-btn-sec { background:transparent; border:0.5px solid rgba(255,255,255,.25); color:rgba(255,255,255,.7); border-radius:7px; padding:5px 11px; font-size:11px; cursor:pointer; }
.hdr-btn-sec:hover { background:rgba(255,255,255,.1); color:#fff; }

/* databar + actbar mobile (ocultos en desktop) */
.hdr-databar { display:none; overflow-x:auto; scrollbar-width:none; gap:18px; padding:6px 14px; background:var(--navy-dk); flex-shrink:0; }
.hdr-databar::-webkit-scrollbar { display:none; }
.hdr-di      { flex-shrink:0; }
.hdr-di-l    { font-size:9px; color:rgba(255,255,255,.4); text-transform:uppercase; letter-spacing:.3px; }
.hdr-di-v    { font-size:11px; color:rgba(255,255,255,.85); font-weight:500; white-space:nowrap; }
.alg-chip-mb { font-size:9px; padding:1px 6px; border-radius:10px; background:rgba(176,48,48,.25); color:#fca5a5; border:0.5px solid rgba(176,48,48,.4); }
.hdr-actbar  { display:none; gap:6px; padding:6px 12px; background:var(--navy-dk); border-top:0.5px solid rgba(255,255,255,.07); flex-shrink:0; }
.hdr-act-btn { flex:1; padding:8px 4px; border-radius:8px; border:0.5px solid rgba(255,255,255,.15); background:transparent; color:rgba(255,255,255,.8); font-size:10px; cursor:pointer; }
.hdr-act-btn.pri { background:var(--navy-lt); border-color:var(--navy-lt); color:#fff; font-weight:600; }

/* ════ CUERPO ════ */
.hc-body { display:flex; flex-direction:column; flex:1; overflow:hidden; background:var(--bg); border:1px solid var(--border); border-top:none; border-radius:0 0 12px 12px; }

/* ════ BARRA "VER EVOLUCIONES" ════ */
.evo-bar { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 14px; border-bottom:1px solid var(--border); background:var(--surface); flex-shrink:0; }
.evo-bar-btn { display:flex; align-items:center; gap:6px; padding:6px 12px; font-size:12px; font-weight:600; border:1px solid var(--border); border-radius:8px; background:var(--bg); color:var(--text); cursor:pointer; }
.evo-bar-btn:hover { background:var(--surface2); }
.evo-bar-cur { font-size:12px; color:var(--text3); }

/* ════ PANEL DESLIZANTE DE EVOLUCIONES ════ */
.tl-ov     { position:fixed; inset:0; background:rgba(0,0,0,.35); z-index:400; display:flex; justify-content:flex-end; }
.tl-panel  { width:300px; max-width:88vw; height:100%; background:var(--surface); display:flex; flex-direction:column; box-shadow:-4px 0 16px rgba(0,0,0,.15); }
.tl-panel-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-bottom:1px solid var(--border); flex-shrink:0; }
.tl-panel-hdr span { font-size:13px; font-weight:700; color:var(--text); }
.tl-panel-close { background:none; border:none; cursor:pointer; color:var(--text3); font-size:16px; }

/* ════ TIMELINE (dentro del panel) ════ */
.hc-left  { display:flex; flex-direction:column; overflow:hidden; background:var(--surface); flex:1; }
.tl-srch  { padding:8px 10px; border-bottom:1px solid var(--border); }
.tl-srch input { width:100%; font-size:12px; padding:6px 10px; border:1px solid var(--border); border-radius:7px; background:var(--bg); color:var(--text); outline:none; }
.tl-srch input:focus { border-color:var(--navy-lt); }
.tl-cnt   { font-size:9px; color:var(--text3); padding:5px 12px 2px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
.tl-yr    { font-size:9px; font-weight:700; color:var(--text3); padding:6px 12px 2px; text-transform:uppercase; letter-spacing:.5px; }
.tl-list  { flex:1; overflow-y:auto; }
.tl-row   { padding:8px 12px; cursor:pointer; border-left:3px solid transparent; border-bottom:1px solid var(--bg); transition:background .12s; }
.tl-row:hover { background:var(--bg); }
.tl-row.sel   { border-left-color:var(--navy-lt); background:var(--blue-lt); }
.tl-date  { font-size:9px; color:var(--text3); }
.tl-doc   { font-size:11px; font-weight:600; color:var(--text); margin:2px 0; }
.tl-prev  { font-size:10px; color:var(--text2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px; }
.tl-tags  { display:flex; gap:3px; flex-wrap:wrap; margin-top:3px; }
.tl-tag   { font-size:9px; padding:1px 5px; border-radius:3px; background:var(--blue-lt); color:var(--navy); font-weight:600; }

/* ════ FILA PRINCIPAL (contenido + sidebar Lectura rápida) ════ */
.hc-main-row { display:flex; gap:12px; flex:1; overflow:hidden; padding:0; }

/* ════ SIDEBAR "LECTURA RÁPIDA" ════ */
.qr-panel   { width:280px; flex-shrink:0; overflow-y:auto; background:var(--surface); border-left:1px solid var(--border); }
.qr-hd      { background:var(--navy); padding:14px 14px 12px; display:flex; align-items:center; gap:9px; }
.qr-avatar  { width:32px; height:32px; border-radius:50%; background:rgba(255,255,255,.14); color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; overflow:hidden; }
.qr-avatar img { width:100%; height:100%; object-fit:cover; }
.qr-tag     { font-size:8.5px; color:#7eb8e8; text-transform:uppercase; letter-spacing:.6px; font-weight:700; }
.qr-name    { font-size:11.5px; font-weight:700; color:#fff; }
.qr-sub     { font-size:9.5px; color:rgba(255,255,255,.6); margin-top:1px; }
.qr-body    { padding:13px 14px; }
.qr-section { margin-bottom:15px; }
.qr-section:last-child { margin-bottom:0; }
.qr-section-title { font-size:9.5px; font-weight:700; color:var(--text3); text-transform:uppercase; letter-spacing:.5px; margin-bottom:7px; }
.qr-vitals  { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.qr-vital   { background:var(--surface2); border:1px solid var(--border); border-radius:7px; padding:6px 8px; text-align:center; }
.qr-vital.hi{ background:var(--red-lt); border-color:#f0c9c9; }
.qr-vital-v { font-size:12px; font-weight:700; color:var(--text2); }
.qr-vital.hi .qr-vital-v { color:var(--red); }
.qr-vital-l { font-size:8.5px; color:var(--text3); text-transform:uppercase; margin-top:1px; }
.qr-hist-item { border-left:2px solid var(--border2); padding:0 0 8px 9px; margin-left:2px; position:relative; }
.qr-hist-item::before { content:''; width:6px; height:6px; border-radius:50%; background:var(--blue-mid); position:absolute; left:-4px; top:3px; }
.qr-hist-item:last-child { padding-bottom:0; }
.qr-hist-date { font-size:8.5px; color:var(--text3); text-transform:uppercase; }
.qr-hist-txt  { font-size:10.5px; color:var(--text); font-weight:600; margin-top:1px; line-height:1.35; }
.qr-med     { display:flex; align-items:flex-start; gap:7px; background:var(--green-lt); border-radius:7px; padding:6px 9px; margin-bottom:5px; }
.qr-med:last-child { margin-bottom:0; }
.qr-med-txt { font-size:10px; color:var(--green); font-weight:700; line-height:1.3; }
.qr-exam    { display:flex; justify-content:space-between; align-items:flex-start; gap:6px; padding:6px 0; border-bottom:1px solid var(--bg); }
.qr-exam:last-child { border-bottom:none; padding-bottom:0; }
.qr-exam-name { font-size:10.5px; font-weight:600; color:var(--text); line-height:1.3; }
.qr-exam-date { font-size:8.5px; color:var(--text3); margin-top:1px; }
.qr-empty   { font-size:10.5px; color:var(--text3); }
.qr-link    { font-size:9.5px; color:var(--blue-mid); font-weight:700; cursor:pointer; }
.qr-footer  { padding:9px 14px; border-top:1px solid var(--border); background:var(--surface2); text-align:center; }

/* ════ PANEL DERECHO ════ */
.hc-right   { display:flex; flex-direction:column; overflow:hidden; flex:1; min-width:0; }
.hc-tabs-row{ display:flex; align-items:stretch; border-bottom:1px solid var(--border); background:var(--surface); flex-shrink:0; }
.hc-tabs    { display:flex; background:var(--surface); overflow-x:auto; flex:1; min-width:0; scrollbar-width:thin; scrollbar-color:var(--border2) transparent; }
.hc-tabs::-webkit-scrollbar { height:6px; }
.hc-tabs::-webkit-scrollbar-track { background:transparent; }
.hc-tabs::-webkit-scrollbar-thumb { background:var(--border2); border-radius:3px; }
.hc-tabs::-webkit-scrollbar-thumb:hover { background:var(--blue-mid); }
.hc-actions { flex-shrink:0; padding:8px 14px; font-size:10px; color:var(--text3); display:flex; align-items:center; gap:8px; background:var(--surface); border-left:1px solid var(--border); box-shadow:-6px 0 8px -6px rgba(20,33,61,.12); }
.hc-tab     { padding:10px 13px; font-size:12px; cursor:pointer; border-bottom:2px solid transparent; color:var(--text3); white-space:nowrap; display:flex; align-items:center; gap:5px; transition:all .15s; }
.hc-tab:hover { color:var(--text); background:var(--bg); }
.hc-tab.act   { color:var(--navy); border-bottom-color:var(--navy-lt); font-weight:600; }
.hc-tab .badge-cnt { font-size:9px; padding:1px 5px; border-radius:10px; background:var(--blue-lt); color:var(--navy); font-weight:700; }
.hc-scroll  { flex:1; overflow-y:auto; padding:14px; display:flex; flex-direction:column; gap:12px; }

/* ════ CARDS SECCIÓN ════ */
.sec-card     { background:var(--surface); border:1px solid var(--border); border-radius:12px; overflow:hidden; flex-shrink:0; }
.sec-card-hdr { padding:9px 14px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; background:var(--surface2); }
.sec-card-ttl { font-size:12px; font-weight:700; color:var(--text); display:flex; align-items:center; gap:6px; }
.sec-card-body{ padding:12px 14px; }

/* ════ SIGNOS VITALES ════ */
.sv-grid  { display:flex; align-items:stretch; overflow-x:auto; border:1px solid var(--border); border-radius:10px; background:var(--bg); }
.sv-card  { background:transparent; border:none; border-right:1px solid var(--border); border-radius:0; padding:10px 16px; display:flex; align-items:center; gap:9px; flex-shrink:0; }
.sv-card:last-child { border-right:none; }
.sv-card.warn { background:var(--amber-lt); }
.sv-card.crit { background:var(--red-lt); }
.sv-ico   { font-size:18px; flex-shrink:0; color:var(--text3); }
.sv-ico.ok{ color:var(--green); }
.sv-ico.hi{ color:var(--amber); }
.sv-ico.cr{ color:var(--red); }
.sv-data  { display:flex; flex-direction:column; min-width:0; }
.sv-val   { font-size:16px; font-weight:700; color:var(--text); line-height:1.15; white-space:nowrap; }
.sv-val .sv-u { font-size:10px; font-weight:400; color:var(--text3); margin-left:2px; }
.sv-val.ok{ color:var(--green); }
.sv-val.hi{ color:var(--amber); }
.sv-val.cr{ color:var(--red); }
.sv-val.na{ color:var(--text3); font-size:14px; }
.sv-lbl   { font-size:9px; color:var(--text3); text-transform:uppercase; letter-spacing:.3px; margin-top:1px; }
.sv-ant   { font-size:8.5px; color:var(--text3); display:flex; align-items:center; gap:2px; margin-top:2px; }
.sv-ant .ti-arrow-up   { color:var(--red); font-size:9px; }
.sv-ant .ti-arrow-down { color:var(--navy-lt); font-size:9px; }

/* ════ EVOLUCIÓN ════ */
.evol-text { font-size:12px; line-height:1.8; color:var(--text); white-space:pre-wrap; }
.evol-fmt { display:flex; flex-direction:column; gap:12px; }
.evol-fmt-sec { }
.evol-fmt-ttl { font-size:10.5px; font-weight:700; color:var(--navy); text-transform:uppercase; letter-spacing:.3px; margin-bottom:4px; }
.evol-fmt-txt { font-size:12px; line-height:1.75; color:var(--text2); white-space:pre-wrap; }

/* ════ DIAGNÓSTICOS ════ */
.dx-pills  { display:flex; flex-wrap:wrap; gap:6px; }
.dx-pill   { display:flex; align-items:center; gap:6px; background:var(--blue-lt); border:1px solid var(--border); border-radius:8px; padding:5px 10px; }
.dx-cod    { font-size:11px; font-weight:700; color:var(--navy); }
.dx-desc   { font-size:11px; color:var(--text2); }
.dx-def    { font-size:9px; padding:1px 6px; border-radius:4px; background:var(--blue-lt); color:var(--navy); font-weight:600; }
.dx-pre    { font-size:9px; padding:1px 6px; border-radius:4px; background:var(--amber-lt); color:var(--amber); font-weight:600; }

/* ════ ANTECEDENTES / ALERGIAS ════ */
.ant-grid  { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:8px; }
.ant-card  { background:var(--bg); border:1px solid var(--border); border-radius:10px; padding:10px 12px; }
.ant-tipo  { font-size:10px; font-weight:700; color:var(--navy); text-transform:uppercase; letter-spacing:.4px; margin-bottom:4px; }
.ant-txt   { font-size:12px; color:var(--text); line-height:1.5; }
.alg-row   { display:flex; align-items:center; gap:10px; padding:8px 14px; border-bottom:1px solid var(--border); }
.alg-row:last-child { border-bottom:none; }
.alg-ico   { width:30px; height:30px; border-radius:50%; background:var(--red-lt); display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.alg-nom   { font-size:12px; font-weight:600; color:var(--text); }
.alg-reg   { font-size:10px; color:var(--text3); margin-top:1px; }

/* ════ RECETAS / LAB ════ */
.rec-card  { border:1px solid var(--border); border-radius:10px; overflow:hidden; margin-bottom:8px; flex-shrink:0; }
.rec-hdr   { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:var(--surface2); border-bottom:1px solid var(--border); }
.rec-body  { padding:8px 12px; display:flex; flex-direction:column; gap:5px; }
.rec-item  { display:flex; gap:9px; align-items:flex-start; padding:5px 0; border-bottom:1px solid var(--bg); }
.rec-item:last-child { border-bottom:none; }
.rec-num   { width:22px; height:22px; border-radius:50%; background:var(--blue-lt); color:var(--navy); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; }
.rec-med   { font-size:12px; font-weight:600; color:var(--text); }
.rec-det   { font-size:11px; color:var(--text3); margin-top:1px; display:flex; gap:10px; }
.lab-card  { border:1px solid var(--border); border-radius:10px; overflow:hidden; margin-bottom:8px; flex-shrink:0; }
.lab-hdr   { display:flex; align-items:center; gap:7px; padding:8px 12px; background:var(--surface2); border-bottom:1px solid var(--border); flex-wrap:wrap; }
.lab-body  { padding:8px 12px; display:flex; flex-wrap:wrap; gap:4px; }
.lab-tag   { font-size:10px; padding:2px 8px; border-radius:4px; background:var(--bg); color:var(--text2); border:1px solid var(--border); }

/* ════ ALERTS ════ */
.alert-badge { font-size:10px; background:var(--red-lt); color:var(--red); padding:2px 9px; border-radius:20px; border:1px solid var(--border); display:inline-flex; align-items:center; gap:4px; }
.warn-badge  { font-size:10px; background:var(--amber-lt); color:var(--amber); padding:2px 9px; border-radius:20px; border:1px solid var(--border); display:inline-flex; align-items:center; gap:4px; }

/* ════ SPINNERS / EMPTY ════ */
.spin-wrap { display:flex; align-items:center; justify-content:center; padding:40px; }
.sp { width:22px; height:22px; border:2px solid var(--border); border-top-color:var(--navy-lt); border-radius:50%; animation:spin .7s linear infinite; }
.empty-hc { text-align:center; padding:36px 20px; color:var(--text3); font-size:13px; }
.empty-icon{ font-size:26px; margin-bottom:8px; opacity:.35; }

/* ════ BUSCAR PACIENTE ════ */
.search-pg   { display:flex; align-items:center; justify-content:center; min-height:60vh; }
.search-card { background:var(--surface); border:1px solid var(--border); border-radius:14px; padding:28px; width:100%; max-width:400px; }
.sug-box { margin-top:4px; border:1px solid var(--border); border-radius:8px; overflow:hidden; background:var(--surface); }
.sug-it  { padding:9px 14px; cursor:pointer; display:flex; align-items:center; gap:10px; font-size:12px; border-bottom:1px solid var(--bg); transition:background .1s; }
.sug-it:last-child { border-bottom:none; }
.sug-it:hover { background:var(--bg); }

/* ════ MODAL ════ */
.ov    { position:fixed; inset:0; background:var(--bg); z-index:1000; display:flex; align-items:flex-start; justify-content:center; overflow-y:auto; }
.modal { background:var(--surface); border-radius:0; width:100%; max-width:1100px; min-height:100vh; overflow-y:visible; border:none; margin:0 auto; }
.mo-hdr       { background:var(--navy); padding:11px 16px; display:flex; align-items:center; justify-content:space-between; border-radius:0; flex-shrink:0; flex-wrap:wrap; gap:8px; position:sticky; top:0; z-index:10; }
.mo-hdr-title { font-size:13px; font-weight:700; color:#fff; }
.mo-tabs      { display:flex; gap:4px; flex-wrap:wrap; }
.mo-tab       { padding:4px 11px; border-radius:6px; border:0.5px solid rgba(255,255,255,.25); background:transparent; color:rgba(255,255,255,.7); font-size:11px; cursor:pointer; transition:all .15s; }
.mo-tab.act   { background:var(--blue-mid); border-color:var(--blue-mid); color:#fff; font-weight:600; }
.mo-tab:hover:not(.act) { background:rgba(255,255,255,.1); color:#fff; }
.mo-body      { padding:16px; display:flex; flex-direction:column; gap:14px; }
.mo-sec       { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.4px; color:var(--text3); margin-bottom:6px; }
.mo-ftr       { padding:10px 16px; border-top:1px solid var(--border); display:flex; justify-content:flex-end; gap:8px; background:var(--surface2); border-radius:0 0 14px 14px; }
.btn-mo-pri   { padding:7px 20px; border-radius:8px; border:none; background:var(--navy); color:#fff; cursor:pointer; font-size:12px; font-weight:600; }
.btn-mo-pri:hover { background:var(--navy-dk); }
.btn-mo-pri:disabled { opacity:.5; cursor:not-allowed; }
.btn-mo-sec   { padding:7px 16px; border-radius:8px; border:1px solid var(--border); background:transparent; cursor:pointer; font-size:12px; color:var(--text); }
.btn-mo-sec:hover { background:var(--bg); }

/* ════ SIGNOS INPUT (modal) ════ */
.sv-input-grid { display:flex; align-items:stretch; overflow-x:auto; border:1px solid var(--border); border-radius:10px; background:var(--bg); }
.sv-input-card { background:transparent; border-radius:0; padding:10px 16px; border:none; border-right:1px solid var(--border); display:flex; align-items:center; gap:9px; flex-shrink:0; transition:background .15s; }
.sv-input-card:last-child { border-right:none; }
.sv-input-card:focus-within { background:var(--surface); }
.sv-input-card.cambiado { border:1px solid var(--red); background:var(--red-lt); border-radius:8px; margin:2px; }
.sv-input-icon { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; background:var(--blue-lt); color:var(--navy); }
.sv-input-body { min-width:64px; }
.sv-input-lbl  { font-size:9px; color:var(--text3); text-transform:uppercase; letter-spacing:.3px; margin-bottom:2px; }
.sv-input-fld  { width:100%; border:none; background:transparent; font-size:14px; font-weight:600; color:var(--text); outline:none; padding:0; }
.sv-input-unit { font-size:10px; color:var(--text3); }
.sv-input-anterior { font-size:9px; color:var(--text3); display:flex; align-items:center; gap:2px; margin-top:1px; }
.sv-input-anterior .ti-arrow-up { color:var(--red); font-size:10px; }
.sv-input-anterior .ti-arrow-down { color:var(--navy-lt); font-size:10px; }

/* ════ CIE10 ════ */
.cie-box { border:1px solid var(--border); border-radius:8px; overflow:hidden; background:var(--surface); max-height:180px; overflow-y:auto; margin-top:4px; }
.cie-it  { padding:8px 12px; cursor:pointer; font-size:12px; border-bottom:1px solid var(--bg); }
.cie-it:hover { background:var(--blue-lt); }

/* ════ RESPONSIVE ════ */
@media (max-width:900px){
  .hc-body { grid-template-columns:1fr; }
  .hc-left { border-right:none; border-bottom:1px solid var(--border); max-height:380px; }
  .hc-right{ min-height:400px; }
  .qr-panel{ display:none; } /* Lectura rápida: se prioriza el contenido de la consulta en pantallas angostas */
}
@media (max-width:640px){
  .hc-hdr     { padding:8px 12px; border-radius:0; }
  .hdr-avatar { width:36px; height:36px; font-size:12px; }
  .hdr-name   { font-size:12px; }
  .hdr-meta   { display:none; }
  .hdr-actions{ display:none; }
  .hdr-databar{ display:flex; }
  .hdr-actbar { display:flex; }
  .hc-body    { border-radius:0; border-left:none; border-right:none; }
  .hc-left    { max-height:360px; }
  .hc-tab     { padding:8px 9px; font-size:11px; }
  .hc-scroll  { padding:10px; gap:10px; padding-bottom:72px; }
  .ant-grid   { grid-template-columns:1fr; }
}
@media (max-width:420px){
  .hdr-avatar { width:32px; height:32px; font-size:11px; }
  .hc-left    { max-height:330px; }
}
@media print { button { display:none !important; } }
.btn-mo-pri { padding:7px 20px; border-radius:8px; border:none; background:var(--navy); color:#fff; cursor:pointer; font-size:12px; font-weight:600; }
.btn-mo-pri:hover { background:var(--navy-dk); }
.btn-mo-pri:disabled { opacity:.5; cursor:not-allowed; }
.btn-mo-sec { padding:7px 16px; border-radius:8px; border:1px solid var(--border); background:transparent; cursor:pointer; font-size:12px; color:var(--text); }
.btn-mo-sec:hover { background:var(--bg); }

/* ════ SELECTOR FORMATO ════ */
.fmt-ov   { position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:1200; display:flex; align-items:center; justify-content:center; padding:20px; }
.fmt-modal{ background:var(--surface); border-radius:14px; width:100%; max-width:560px; border:1px solid var(--border); overflow:hidden; }
.fmt-hdr  { background:var(--navy); padding:14px 18px; }
.fmt-hdr-t{ font-size:13px; font-weight:700; color:#fff; }
.fmt-hdr-s{ font-size:11px; color:rgba(255,255,255,.7); margin-top:3px; }
.fmt-body { padding:18px; display:flex; flex-direction:column; gap:14px; }
.fmt-cards{ display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.fmt-card { border:2px solid var(--border); border-radius:12px; padding:14px; cursor:pointer; transition:all .15s; position:relative; background:var(--surface); }
.fmt-card:hover { border-color:var(--navy-lt); }
.fmt-card.sel   { border-color:var(--navy); background:var(--blue-lt); }
.fmt-card-ico   { font-size:24px; margin-bottom:8px; }
.fmt-card-ttl   { font-size:12px; font-weight:700; color:var(--text); margin-bottom:4px; }
.fmt-card-desc  { font-size:10px; color:var(--text3); line-height:1.6; }
.fmt-card-tags  { display:flex; flex-wrap:wrap; gap:4px; margin-top:8px; }
.fmt-tag        { font-size:8px; padding:2px 7px; border-radius:4px; font-weight:500; }
.fmt-tag-g { background:var(--green-lt); color:var(--green); }
.fmt-tag-b { background:var(--blue-lt); color:var(--navy); }
.fmt-sel-badge  { position:absolute; top:8px; right:8px; background:var(--navy); color:#fff; font-size:9px; padding:2px 8px; border-radius:20px; font-weight:500; }
.fmt-info { background:var(--blue-lt); border:1px solid var(--border); border-radius:8px; padding:9px 12px; font-size:10px; color:var(--navy); display:flex; gap:8px; }
.fmt-ftr  { padding:10px 18px; border-top:1px solid var(--border); display:flex; justify-content:flex-end; gap:8px; background:var(--surface2); }
.sec002         { border:1px solid var(--border); border-radius:9px; overflow:hidden; flex-shrink:0; }
.sec002-hdr     { background:var(--navy); padding:6px 12px; display:flex; align-items:center; justify-content:space-between; }
.sec002-ttl     { font-size:10px; font-weight:700; color:#fff; }
.sec002-ind     { font-size:8px; color:rgba(255,255,255,.5); }
.sec002-scroll  { max-height:160px; overflow-y:auto; padding:10px 12px; scrollbar-width:thin; scrollbar-color:var(--navy-lt) transparent; }
.sec002-scroll::-webkit-scrollbar { width:4px; }
.sec002-scroll::-webkit-scrollbar-thumb { background:var(--navy-lt); border-radius:4px; }
.sec002-scroll-lg { max-height:200px; overflow-y:auto; padding:10px 12px; scrollbar-width:thin; scrollbar-color:var(--navy-lt) transparent; }
.sec002-scroll-lg::-webkit-scrollbar { width:4px; }
.sec002-scroll-lg::-webkit-scrollbar-thumb { background:var(--navy-lt); border-radius:4px; }
.chk002-grid  { display:grid; grid-template-columns:repeat(5,1fr); gap:5px; margin-bottom:8px; }
.chk002-item  { display:flex; align-items:center; gap:5px; padding:5px 7px; border:0.5px solid var(--border); border-radius:6px; cursor:pointer; background:var(--surface2); user-select:none; transition:all .12s; }
.chk002-item:hover { border-color:var(--navy-lt); }
.chk002-item.on    { background:var(--blue-lt); border-color:var(--navy-lt); }
.chk002-box   { width:14px; height:14px; border:1.5px solid var(--navy-lt); border-radius:3px; display:flex; align-items:center; justify-content:center; flex-shrink:0; background:var(--surface); font-size:9px; color:var(--navy); transition:all .12s; }
.chk002-box.on{ background:var(--navy); border-color:var(--navy); color:#fff; }
.chk002-lbl   { font-size:9px; color:var(--text); line-height:1.2; }
.tipo-btn-row { display:flex; gap:6px; margin-bottom:8px; }
.tipo-btn     { padding:4px 14px; border-radius:6px; font-size:10px; cursor:pointer; border:0.5px solid var(--border); color:var(--text3); background:var(--surface2); transition:all .12s; }
.tipo-btn.act { background:var(--navy); color:#fff; border-color:var(--navy); font-weight:600; }
.sub-hdr002   { font-size:9px; font-weight:700; color:var(--text3); text-transform:uppercase; letter-spacing:.3px; margin:7px 0 5px; }
.dx-pill-pre  { font-size:9px; padding:2px 8px; border-radius:4px; background:var(--amber-lt); color:var(--amber); font-weight:600; cursor:pointer; border:1.5px solid transparent; }
.dx-pill-def  { font-size:9px; padding:2px 8px; border-radius:4px; background:var(--blue-lt); color:var(--navy); font-weight:600; cursor:pointer; border:1.5px solid transparent; }
.dx-pill-pre.sel { border-color:var(--amber); }
.dx-pill-def.sel { border-color:var(--navy); }
@media (max-width:640px){
  .fmt-cards   { grid-template-columns:1fr; }
  .fmt-modal   { border-radius:14px 14px 0 0; }
  .fmt-ov      { align-items:flex-end; padding:0; }
  .chk002-grid { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:900px){
  .chk002-grid { grid-template-columns:repeat(3,1fr); }
}
`;

/* Cantidad de una línea de receta: si el médico la escribió a mano (esquemas
   como "1 al mes por 6 meses" o ampollas cada 3 meses, que no se pueden
   expresar con Mañana/Mediodía/Noche × Días) manda lo escrito; si no, se
   calcula con la dosis como siempre. */
const totalPorDosis = x => ((parseFloat(x.MANANA)||0)+(parseFloat(x.MEDIODIA)||0)+(parseFloat(x.NOCHE)||0))*(parseFloat(x.DIAS)||0);
const cantidadEsManual = x => {
  const c = parseFloat(x.CANTIDAD); if (!(c > 0)) return false;
  const t = totalPorDosis(x);
  return t > 0 ? c !== t : c !== 1;
};

/* ── Receta mensual: fechas, vigencia y formato ── */
const VIGENCIA_RECETA_DIAS = 3; // mismo valor que imprime la receta ("Vigencia máxima: 3 días")
const pad2 = n => String(n).padStart(2,'0');
const hoyLocalISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };
const isoADate = iso => { const [y,m,d] = iso.split('-').map(Number); return new Date(y, m-1, d); };
const dateAISO = dt => `${dt.getFullYear()}-${pad2(dt.getMonth()+1)}-${pad2(dt.getDate())}`;
const sumarDiasISO = (iso, dias) => { const dt = isoADate(iso); dt.setDate(dt.getDate()+dias); return dateAISO(dt); };
const fmtFechaCorta = iso => iso ? isoADate(iso).toLocaleDateString('es-EC',{weekday:'short',day:'2-digit',month:'short',year:'numeric'}) : '—';
/* Genera N fechas separadas por "cada" meses, conservando el día del mes
   (si el mes no tiene ese día, usa el último: 31 ene → 28 feb). */
const fechasMensuales = (inicio, cadaMeses, cuantas) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(inicio || ''); if (!m) return [];
  const y0 = +m[1], mo0 = +m[2]-1, d0 = +m[3];
  const cada = Math.max(1, parseInt(cadaMeses) || 1);
  const n = Math.min(24, Math.max(1, parseInt(cuantas) || 1));
  const out = [];
  for (let k = 0; k < n; k++) {
    const total = mo0 + k*cada;
    const y = y0 + Math.floor(total/12), mo = total % 12;
    const dim = new Date(y, mo+1, 0).getDate();
    out.push(`${y}-${pad2(mo+1)}-${pad2(Math.min(d0, dim))}`);
  }
  return out;
};

export default function Historia() {
  // Detecta encabezados conocidos (MOTIVO DE CONSULTA:, ANAMNESIS:, EXAMEN FÍSICO:,
  // DIAGNÓSTICO PRESUNTIVO:, PLAN:, etc.) en el texto plano de la evolución y los
  // separa en bloques con título en negrita — solo afecta cómo se MUESTRA, el
  // texto guardado en la base sigue siendo el mismo bloque plano de siempre.
  const SECCIONES_EVOL = ['MOTIVO DE CONSULTA','ANAMNESIS','EXAMEN FÍSICO','EXAMEN FISICO',
    'DIAGNÓSTICO PRESUNTIVO','DIAGNOSTICO PRESUNTIVO','PLAN','ANÁLISIS','ANALISIS',
    'SUBJETIVO','OBJETIVO','TRATAMIENTO'];
  const formatearEvolucion = texto => {
    if (!texto) return null;
    const cap = s => s.charAt(0)+s.slice(1).toLowerCase();
    // El encabezado solo cuenta si está al inicio de línea (evita falsos positivos
    // cuando una de estas palabras aparece de forma incidental dentro de una oración).
    const patron = new RegExp(`(?:^|\\n)\\s*(${SECCIONES_EVOL.join('|')})\\s*:\\s*`, 'gi');
    const partes = texto.split(patron);
    const bloques = [];
    if (partes.length > 1) {
      // Texto antes del primer encabezado (si existe) se conserva como bloque sin título,
      // en vez de perderse o quedar mal formateado como título.
      const preambulo = partes[0]?.trim();
      if (preambulo) bloques.push({titulo:null, cuerpo:preambulo});
      for (let i=1; i<partes.length; i+=2) {
        const titulo = partes[i]?.trim();
        const cuerpo = partes[i+1]?.trim() || '';
        if (titulo) bloques.push({titulo:cap(titulo), cuerpo});
      }
    } else {
      // Fallback: plantillas numeradas tipo "1. DATOS DE IDENTIFICACIÓN" (ej. plantilla oncológica),
      // que no usan ninguno de los encabezados de SECCIONES_EVOL con ":" al final.
      const patronNum = /(?:^|\n)\s*(\d{1,2})\.\s+([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ0-9 \/\-]{2,60})\s*\n/g;
      const partesNum = texto.split(patronNum);
      if (partesNum.length > 1) {
        const preambulo = partesNum[0]?.trim();
        if (preambulo) bloques.push({titulo:null, cuerpo:preambulo});
        for (let i=1; i<partesNum.length; i+=3) {
          const num = partesNum[i]; const tit = partesNum[i+1]?.trim(); const cuerpo = partesNum[i+2]?.trim() || '';
          if (tit) bloques.push({titulo:`${num}. ${cap(tit)}`, cuerpo});
        }
      }
    }
    // Si no se detectó ningún encabezado conocido, se muestra el texto tal cual
    if (bloques.length === 0) return <div className="evol-text">{texto}</div>;
    return (
      <div className="evol-fmt">
        {bloques.map((b,i)=>(
          <div key={i} className="evol-fmt-sec">
            {b.titulo && <div className="evol-fmt-ttl">{b.titulo}</div>}
            <div className="evol-fmt-txt">{b.cuerpo}</div>
          </div>
        ))}
      </div>
    );
  };

  const { usuario }   = useAuth();
  const [sp]          = useSearchParams();
  const navigate      = useNavigate();
  const [codPac, setCodPac]   = useState(sp.get('paciente')||'');
  const [pacBusq, setPacBusq] = useState('');
  const [pacSugs, setPacSugs] = useState([]);
  const [paciente, setPac]    = useState(null);
  const [convenio, setConv]   = useState('—');
  const [registros, setRegs]  = useState([]);
  const [selIdx, setSelIdx]   = useState(0);
  const [detalle, setDet]     = useState(null);
  const [loadDet, setLoadDet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alergias, setAlg]    = useState([]);
  const [formGineco, setFormGineco] = useState({});
  const [antecs, setAntecs]   = useState([]);
  const [tab, setTab]         = useState('consulta');
  const [showNueva, setNueva] = useState(false);
  const [editCod, setEditCod] = useState(null);
  const [modalTab, setModalTab] = useState('consulta');
  const [form, setForm]       = useState({diagnosticos:[],signosVitales:{}});
  const [svAnterior, setSvAnterior] = useState(null);
  const [tipoEvol, setTipoEvol] = useState('libre');
  const [evoHeight, setEvoHeight] = useState(150);
  const evoResizing = useRef(false);
  const evoStartY = useRef(0);
  const evoStartH = useRef(0);
  const onEvoResizeStart = e => {
    evoResizing.current = true;
    evoStartY.current = e.clientY;
    evoStartH.current = evoHeight;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onEvoResizeMove = e => {
    if (!evoResizing.current) return;
    const delta = e.clientY - evoStartY.current;
    setEvoHeight(Math.max(90, evoStartH.current + delta));
  };
  const onEvoResizeEnd = () => { evoResizing.current = false; };
  const [soapForm, setSoapForm] = useState({S:'',O:'',A:'',P:''});
  const [libreAP, setLibreAP] = useState({A:'',P:''});
  const [recetaItems, setRecItems] = useState([]);
  const [recetaIndicaciones, setRecetaIndicaciones] = useState('');
  const [borradorId, setBorradorId] = useState(null);
  const [borradorInfo, setBorradorInfo] = useState(null);
  const [autoguardadoTexto, setAutoguardadoTexto] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [plantillas, setPlantillas]   = useState([]);
  const [medSugs, setMedSugs]         = useState({});
  const [showPlantillas, setShowPl]   = useState(false);
  const [nombrePlantilla, setNomPl]   = useState('');
  const [savingPl, setSavingPl]       = useState(false);
  const [recetasDet, setRecetasDet]   = useState([]);
  const [rmAbierto, setRmAbierto]       = useState(false);
  const [rm, setRm] = useState({ MEDICAMENTO:'', COD_ITEM:null, CANTIDAD:'1', INDICACION:'', INICIO:hoyLocalISO(), CADA:'1', N:'6' });
  const [rmFechas, setRmFechas]         = useState([]);
  const [rmGenerando, setRmGenerando]   = useState(false);
  useEffect(() => { setRmFechas(fechasMensuales(rm.INICIO, rm.CADA, rm.N)); }, [rm.INICIO, rm.CADA, rm.N]);
  const [certificadosDet, setCertificadosDet] = useState([]);
  const [nuevoCertif, setNuevoCertif] = useState(false);
  const [certifTipo, setCertifTipo]   = useState('REPOSO');
  const [certifDatos, setCertifDatos] = useState({});
  const [certifMsg, setCertifMsg]     = useState('');
  const [certifSaving, setCertifSaving] = useState(false);
  const [consentimientosDet, setConsentimientosDet] = useState([]);
  const [plantillasConsentList, setPlantillasConsentList] = useState([]);
  const [nuevoConsent, setNuevoConsent] = useState(false);
  const [consentPlantillaSel, setConsentPlantillaSel] = useState('');
  const [consentProcEsp, setConsentProcEsp] = useState('');
  const [consentMsg, setConsentMsg] = useState('');
  const [consentSaving, setConsentSaving] = useState(false);
  const [tipoConsent, setTipoConsent] = useState('plantilla'); // 'plantilla' | 'form024'
  const [form024Det, setForm024Det] = useState([]);
  const [form024Datos, setForm024Datos] = useState({TIPO_ATENCION:'AMBULATORIO', TIPO_DECISION:'ACEPTA'});
  const [form024Cie10Q, setForm024Cie10Q] = useState('');
  const [form024Cie10S, setForm024Cie10S] = useState([]);
  const [certifCie10Q, setCertifCie10Q] = useState('');
  const [certifCie10S, setCertifCie10S] = useState([]);
  const [form024Saving, setForm024Saving] = useState(false);
  const [form024Msg, setForm024Msg] = useState('');
  const [form024Imagen, setForm024Imagen] = useState(null);
  const [labDet, setLabDet]           = useState([]);
  const [svUltimos, setSvUltimos]     = useState(null); // signos vitales de la consulta más reciente (para Lectura rápida, independiente de cuál se esté viendo)
  // ── Antecedentes: carga rápida por chips (patológicos/no patológicos/familiares) ──
  const [antecSub, setAntecSub]         = useState('PAT'); // subcategoría activa del formulario
  const [antecToggle, setAntecToggle]   = useState(new Set()); // claves "TIPO|nombre" que difieren de lo ya guardado
  const [famParentesco, setFamParentesco] = useState(''); // parentesco a aplicar a los familiares que se marquen ahora
  const [otrosAntec, setOtrosAntec]     = useState({PAT:'',NPAT:'',FAM:'',OTR:''}); // texto libre por categoría, para lo que no está en el catálogo
  const [qrxItems, setQrxItems]         = useState([{PROCEDIMIENTO:'',FECHA_EVENTO:'',OBSERVACION:''}]);
  const [savingAntec, setSavingAntec]   = useState(false);
  const antecKey = (tipo,nombre) => `${tipo}|${nombre}`;
  const antecExisteKeys = new Set(antecs.filter(a=>['PAT','NPAT','FAM'].includes(a.C_TIPO)).map(a=>antecKey(a.C_TIPO,a.ANTECEDENTE)));
  const antecChecked = (tipo,nombre) => antecExisteKeys.has(antecKey(tipo,nombre)) !== antecToggle.has(antecKey(tipo,nombre));
  const toggleAntecChip = (tipo,nombre) => setAntecToggle(s=>{ const n=new Set(s); const k=antecKey(tipo,nombre); n.has(k)?n.delete(k):n.add(k); return n; });
  const [formAlg, setFormAlg] = useState({});
  const [cie10Q, setCie10Q]   = useState('');
  const [cie10S, setCie10S]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [filtro, setFiltro]   = useState('');
  const timer = useRef(null);
  const [iaActivo,     setIaActivo]  = useState(false);
  const [grabando,     setGrabando]  = useState(false);
  const [mediaRec,     setMediaRec]  = useState(null);
  const [iaProcesando, setIaProc]    = useState(false);
  const [iaBorrador,   setIaBorrador]= useState(null);
  const [dictandoSV,   setDictandoSV]= useState(false);
  const [svDictado,    setSvDictado] = useState(null);
  // Dictado de signos vitales por micrófono — usa el reconocimiento de voz nativo
  // del navegador (Chrome/Edge), no OpenAI ni ningún endpoint del backend.
  const dictarSV = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Tu navegador no soporta dictado por voz — usa Google Chrome o Edge.'); return; }
    const rec = new SR();
    rec.lang = 'es-EC';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    setSvDictado(null);
    setDictandoSV(true);
    rec.onresult = (e) => {
      const texto = e.results[0][0].transcript;
      const detectado = parseSignosDictado(texto);
      if (Object.keys(detectado).length === 0) {
        setSvDictado({ ok:false, texto });
      } else {
        setForm(f => ({ ...f, signosVitales: { ...f.signosVitales, ...detectado } }));
        setSvDictado({ ok:true, texto, detectado });
      }
    };
    rec.onerror = (e) => { setDictandoSV(false); if (e.error !== 'no-speech' && e.error !== 'aborted') alert('Error de dictado: '+e.error); };
    rec.onend = () => setDictandoSV(false);
    try { rec.start(); } catch { setDictandoSV(false); }
  };
  const [timerSeg,     setTimerSeg]  = useState(0);
  const timerRef = useRef(null);
  // ── Formato consulta ──
  const [fmtSelector,  setFmtSelector] = useState(false); // muestra selector de formato
  const [fmtConsulta,  setFmtConsulta] = useState('libre'); // 'libre' | '002'
  // form 002 MSP
  const [form002, setForm002] = useState({
    C_TIPO_CONSULTA:'PRI',
    ANT_PER_FLAGS:{},  ANT_PER_DETALLE:'',
    ANT_FAM_FLAGS:{},  ANT_FAM_DETALLE:'',
    REV_ORGANOS_FLAGS:{}, REV_ORGANOS_DET:'',
    EXAM_REGIONAL_FLAGS:{}, EXAM_SISTEMICO_FLAGS:{}, EXAM_FISICO_DET:'',
    PLAN_TRATAMIENTO:'',
  });
  // ── Impresión Formulario 008 ──
  const [showPrint,    setShowPrint]  = useState(false);
  const [showPrint002, setShowPrint002] = useState(false);
  const [print002Load, setPrint002Load] = useState(false);
  const [printMode,    setPrintMode]  = useState('actual'); // 'actual'|'todas'|'fechas'
  const [printDesde,   setPrintDesde] = useState('');
  const [printHasta,   setPrintHasta] = useState('');
  const [printLoading, setPrintLoad]  = useState(false);
  const [estab,        setEstab]      = useState({});
  // ── Edición 72h ──
  const [editInfo,      setEditInfo]  = useState(null);
  const [showAuthModal, setShowAuth]  = useState(false);
  const [authMotivo,    setAuthMotivo]= useState('');
  const [authLoading,   setAuthLoad]  = useState(false);
  const [authMsg,       setAuthMsg]   = useState('');
  const [editPendiente, setEditPend]  = useState(null);
  const [auditLogs,     setAuditLogs] = useState([]);
  const [loadAudit,     setLoadAudit] = useState(false);


  // Cargar Tabler Icons si no está ya cargado
  useEffect(()=>{
    if(!document.getElementById("tabler-icons-css")){
      const l=document.createElement("link");
      l.id="tabler-icons-css"; l.rel="stylesheet";
      l.href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.x/dist/tabler-icons.min.css";
      document.head.appendChild(l);
    }
  },[]);
  useEffect(()=>{
    api.get('/configuracion/ms-config/publico').then(r=>setEstab(r.data||{})).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(codPac){
      cargarPac(codPac); cargarHC(codPac);
      cargarAlg(codPac); cargarAntecs(codPac); cargarGineco(codPac);
      cargarConv(codPac); cargarRecetasDet(codPac); cargarLabDet(codPac); cargarCertificadosDet(codPac); cargarConsentimientosDet(codPac); cargarForm024Det(codPac);
      api.get('/historia/plantillas-consentimiento').then(r=>setPlantillasConsentList(r.data||[])).catch(()=>{});
    }
  },[codPac]);

  const cargarPac    = async id => { try{ const{data}=await api.get(`/pacientes/${id}`); setPac(data); }catch(_){} };
  const cargarHC     = async id => {
    setLoading(true); setSvUltimos(null);
    try{ const{data}=await api.get(`/historia/${id}?limit=200`); setRegs(data); setSelIdx(0); if(data[0])cargarDet(data[0].COD,true); }
    catch(e){console.error(e);}finally{setLoading(false);}
  };
  const cargarAlg    = async id => { try{ const{data}=await api.get(`/historia/alergias/${id}`); setAlg(data); }catch(_){} };
  const cargarGineco = async id => { try{ const{data}=await api.get(`/historia/antecedentes-ginecologicos/${id}`); setFormGineco(data||{}); }catch(_){ setFormGineco({}); } };
  const cargarLabDet = async id => { try{ const{data}=await api.get(`/laboratorio/solicitudes?cod_paciente=${id}`); setLabDet(data); }catch(_){} };

  // Resultados con rangos
  const [labRangos,   setLabRangos]  = useState({});  // {cod_sol: {detalles,edad,sexo}}
  const [labExpanded, setLabExp]     = useState(null); // cod solicitud expandida

  const verResultados = async codSol => {
    if (labExpanded===codSol){ setLabExp(null); return; }
    setLabExp(codSol);
    if (labRangos[codSol]) return; // ya cargados
    try {
      const {data} = await api.get('/laboratorio/solicitudes/'+codSol+'/rangos');
      setLabRangos(r=>({...r,[codSol]:data}));
    } catch(e){ console.error(e); }
  };

  const evalRangoHist = (v,rango) => {
    if(!rango||!v) return 'na';
    const n=parseFloat(v); if(isNaN(n)) return 'na';
    const li=parseFloat(rango.LIMITE_INFERIOR), ls=parseFloat(rango.LIMITE_SUPERIOR);
    if(ls>0&&n>ls) return 'hi'; if(li>0&&n<li) return 'lo'; return 'ok';
  };
  const cargarRecetasDet = async id => { try{ const{data}=await api.get(`/historia/recetas/${id}`); setRecetasDet(data); }catch(_){} };

  const generarRecetaMensual = async () => {
    if (!codPac) { setMsg('Selecciona un paciente primero'); return; }
    if (!rm.MEDICAMENTO.trim()) { setMsg('Escribe el medicamento de la receta mensual'); return; }
    if (!(parseFloat(rm.CANTIDAD) > 0)) { setMsg('La cantidad de cada receta debe ser mayor a 0'); return; }
    if (rmFechas.length === 0 || rmFechas.some(f => !/^\d{4}-\d{2}-\d{2}$/.test(f || ''))) { setMsg('Revisa las fechas de las recetas'); return; }
    setRmGenerando(true); setMsg('');
    try {
      const { data } = await api.post('/historia/recetas/mensual', {
        COD_PACIENTE: codPac,
        MEDICAMENTO: rm.MEDICAMENTO.trim(),
        COD_ITEM: rm.COD_ITEM || null,
        CANTIDAD: String(rm.CANTIDAD),
        INDICACION: rm.INDICACION?.trim() || null,
        FECHAS: rmFechas,
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/historia/recetas/lote/pdf?cods=${(data.cods||[]).join(',')}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let m = 'Las recetas se guardaron, pero no se pudo generar el PDF';
        try { const j = await res.json(); if (j?.error) m = j.error; } catch (_) {}
        setMsg('✕ ' + m); cargarRecetasDet(codPac); return;
      }
      const blob = await res.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `Recetas_mensuales_${rmFechas[0]}.pdf`; a.click();
      setMsg(`✓ ${rmFechas.length} recetas generadas`);
      cargarRecetasDet(codPac);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || e.message));
    } finally { setRmGenerando(false); }
  };
  const cargarCertificadosDet = async id => { try{ const{data}=await api.get(`/historia/certificados/${id}`); setCertificadosDet(data); }catch(_){} };
  const cargarConsentimientosDet = async id => { try{ const{data}=await api.get(`/historia/consentimientos/${id}`); setConsentimientosDet(data); }catch(_){} };
  const cargarForm024Det = async id => { try{ const{data}=await api.get(`/historia/form024/${id}`); setForm024Det(data); }catch(_){} };
  const onForm024Cie10 = v => {
    setForm024Cie10Q(v); clearTimeout(timer.current);
    if(v.length<2){setForm024Cie10S([]);return;}
    timer.current=setTimeout(async()=>{ try{ const{data}=await api.get(`/historia/buscar/cie10?q=${encodeURIComponent(v)}`); setForm024Cie10S(data); }catch(_){} },300);
  };
  const onCertifCie10 = v => {
    setCertifCie10Q(v); clearTimeout(timer.current);
    if(v.length<2){setCertifCie10S([]);return;}
    timer.current=setTimeout(async()=>{ try{ const{data}=await api.get(`/historia/buscar/cie10?q=${encodeURIComponent(v)}`); setCertifCie10S(data); }catch(_){} },300);
  };
  const cargarAntecs = async id => { try{ const{data}=await api.get(`/historia/antecedentes/${id}`); setAntecs(data); }catch(_){} };

  // Guarda de una sola vez todo lo marcado/desmarcado en los chips (patológicos,
  // no patológicos y familiares), el texto libre "Otro" de cada categoría y las
  // cirugías cargadas — evita que el médico tenga que repetir el formulario por
  // cada antecedente. Compara contra `antecs` (lo ya guardado) para no duplicar:
  // un chip recién marcado se inserta, uno desmarcado que ya existía se elimina.
  const guardarAntecedentes = async () => {
    const nuevoParentescoRequerido = CATALOGO_ANTEC.FAM.some(nombre =>
      antecChecked('FAM',nombre) && !antecExisteKeys.has(antecKey('FAM',nombre))
    ) || otrosAntec.FAM.trim();
    if (nuevoParentescoRequerido && !famParentesco.trim()) {
      setMsg('Indica el parentesco para los antecedentes familiares que estás marcando'); return;
    }
    setSavingAntec(true); setMsg('');
    try {
      const tareas = [];
      ['PAT','NPAT','FAM'].forEach(tipo => {
        CATALOGO_ANTEC[tipo].forEach(nombre => {
          const k = antecKey(tipo,nombre);
          const existia = antecExisteKeys.has(k);
          const marcado = antecChecked(tipo,nombre);
          if (marcado && !existia) {
            tareas.push(api.post('/historia/antecedentes', {
              COD_PACIENTE:codPac, C_TIPO:tipo, ANTECEDENTE:nombre,
              C_PARENTESCO: tipo==='FAM' ? famParentesco.trim() : null,
            }));
          } else if (!marcado && existia) {
            const reg = antecs.find(a=>a.C_TIPO===tipo && a.ANTECEDENTE===nombre);
            if (reg) tareas.push(api.delete(`/historia/antecedentes/${reg.COD}`));
          }
        });
      });
      ['PAT','NPAT','FAM','OTR'].forEach(tipo => {
        const txt = otrosAntec[tipo]?.trim();
        if (txt) tareas.push(api.post('/historia/antecedentes', {
          COD_PACIENTE:codPac, C_TIPO:tipo, ANTECEDENTE:txt,
          C_PARENTESCO: tipo==='FAM' ? famParentesco.trim() : null,
        }));
      });
      qrxItems.filter(it=>it.PROCEDIMIENTO?.trim()).forEach(it => {
        tareas.push(api.post('/historia/antecedentes', {
          COD_PACIENTE:codPac, C_TIPO:'QRX', ANTECEDENTE:it.PROCEDIMIENTO.trim(),
          FECHA_EVENTO: it.FECHA_EVENTO||null, OBSERVACION: it.OBSERVACION||null,
        }));
      });
      if (tareas.length===0) { setMsg('No hay cambios para guardar'); setSavingAntec(false); return; }
      await Promise.all(tareas);
      setAntecToggle(new Set()); setFamParentesco('');
      setOtrosAntec({PAT:'',NPAT:'',FAM:'',OTR:''}); setQrxItems([{PROCEDIMIENTO:'',FECHA_EVENTO:'',OBSERVACION:''}]);
      setMsg('✓ Antecedentes guardados'); cargarAntecs(codPac);
    } catch(e) { setMsg('Error: '+(e.response?.data?.error||e.message)); }
    finally { setSavingAntec(false); }
  };
  const cargarConv   = async id => {
    try{ const{data}=await api.get(`/historia/convenio/${id}`); setConv(data?.CONVENIO||'PARTICULAR'); }
    catch{ setConv('PARTICULAR'); }
  };
  const cargarDet    = async (cod,esUltimo=false) => {
    setLoadDet(true); setDet(null);
    try{ const{data}=await api.get(`/historia/registro/${cod}`); setDet(data); if(esUltimo)setSvUltimos(data.signosVitales?.[0]||{}); }
    catch(e){ const reg=registros.find(r=>r.COD===cod); if(reg)setDet({...reg,diagnosticos:reg.diagnosticos||[],signosVitales:[],recetas:[]}); }
    finally{ setLoadDet(false); }
  };
  const cargarPlantillas = async () => { try{ const{data}=await api.get('/historia/plantillas-receta'); setPlantillas(data); }catch(_){} };

  const selRow = (idx,cod) => { setSelIdx(idx); cargarDet(cod); setTab('consulta'); };

  // Signos vitales de la consulta anterior a la que se está VIENDO (no la que
  // se está creando — eso ya lo cubre svAnterior). Sirve para mostrar la
  // comparativa "antes: X" también al revisar una consulta ya guardada.
  const [svAnteriorVista, setSvAnteriorVista] = useState(null);
  useEffect(() => {
    const anterior = registros[selIdx+1];
    if (!anterior?.COD) { setSvAnteriorVista(null); return; }
    let cancelado = false;
    api.get(`/historia/registro/${anterior.COD}`)
      .then(r => { if(!cancelado) setSvAnteriorVista(r.data?.signosVitales?.[0] || null); })
      .catch(() => { if(!cancelado) setSvAnteriorVista(null); });
    return () => { cancelado = true; };
  }, [registros, selIdx]);

  const abrirNueva = () => {
    setFmtSelector(true); // primero mostrar selector de formato
  };

  const confirmarFormato = () => {
    setFmtSelector(false);
    setNueva(true); setEditCod(null); setModalTab('consulta'); setMsg('');
    api.get('/historia/ia-config').then(r=>setIaActivo(r.data?.activo||false)).catch(()=>{});
    setForm({diagnosticos:[],signosVitales:{}}); setTipoEvol('libre');
    setSoapForm({S:'',O:'',A:'',P:''});
    setLibreAP({A:'',P:''});
    setForm002({C_TIPO_CONSULTA:'PRI',ANT_PER_FLAGS:{},ANT_PER_DETALLE:'',ANT_FAM_FLAGS:{},ANT_FAM_DETALLE:'',REV_ORGANOS_FLAGS:{},REV_ORGANOS_DET:'',EXAM_REGIONAL_FLAGS:{},EXAM_SISTEMICO_FLAGS:{},EXAM_FISICO_DET:'',PLAN_TRATAMIENTO:''});
    setRecItems([]); setRecetaIndicaciones(''); setNomPl(''); setShowPl(false);
    cargarPlantillas();
    // Signos vitales de la consulta anterior, para la comparativa "anterior: X"
    setSvAnterior(null);
    if (registros[0]?.COD) {
      api.get(`/historia/registro/${registros[0].COD}`)
        .then(r => setSvAnterior(r.data?.signosVitales?.[0] || null))
        .catch(() => setSvAnterior(null));
    }
  };

  /* ════ GENERADOR HTML FORMULARIO 008 MSP ════ */
  const generarHTML008 = (registros008, pac, medico={}, sv008={}) => {
    const e = estab;
    const nombrePac = [pac?.APELLIDO_PATERNO,pac?.APELLIDO_MATERNO,pac?.PRIMER_NOMBRE].filter(Boolean).join(' ');
    const edad = pac?.FECHA_NACIMIENTO ? Math.floor((Date.now()-new Date(pac.FECHA_NACIMIENTO))/3.156e10) : '—';
    const sexo = pac?.C_SEXO==='M'?'Masculino':pac?.C_SEXO==='F'?'Femenino':'—';

    const tarjetaConsulta = (r,i) => {
      const sv = r.signosVitales?.[0] || {};
      const diags = (r.diagnosticos||[]).map(d=>`${d.COD_CIE10} ${d.CIE10_DESC||d.desc||''}`).join(', ');
      return `
        <div class="consulta-card">
          <div class="consulta-hdr">Consulta — ${fmtD008(r.FECHA)}<span style="float:right">${r.MEDICO||r.USUARIO_REGISTRO||'—'}</span></div>
          <div class="consulta-body">
            <div class="vitals-row">
              <span>PA: ${sv.PRESION_ARTERIAL||'—'}</span>
              <span>Pulso: ${sv.PULSO||'—'}</span>
              <span>Temp: ${sv.TEMP_AXILAR||'—'}</span>
              <span>Peso: ${sv.PESO||'—'}</span>
              <span>Talla: ${sv.TALLA||'—'}</span>
              <span>Sat O₂: ${sv.SATURACION_OXIGENO||'—'}</span>
            </div>
            <div class="evo-lbl">Evolución / Motivo de consulta</div>
            <div class="evo-txt">${r.EVOLUCION||'—'}</div>
            <div class="diag-lbl">Diagnóstico CIE-10</div>
            <div class="diag-txt">${diags||'—'}</div>
          </div>
        </div>`;
    };

    const firmaHtml = medico?.FIRMA_URL
      ? `<img src="${fotoUrl(medico.FIRMA_URL)}" alt="Firma" style="max-height:60px;max-width:200px;display:block;margin:0 auto 4px" onerror="this.style.display='none'">`
      : `<div class="firma-line"></div>`;

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Formulario 008 - ${nombrePac}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,sans-serif; font-size:11px; color:#000; padding:10px 15px; }
    .hdr { display:grid; grid-template-columns:64px 1fr auto; gap:14px; align-items:center; border:1px solid #000; border-radius:8px; padding:10px 14px; margin-bottom:8px; }
    .hdr-logo-box { width:56px; height:56px; border:1px solid #ccc; border-radius:6px; display:flex; align-items:center; justify-content:center; overflow:hidden; background:#f5f5f5; }
    .hdr-logo-box img { max-width:100%; max-height:100%; object-fit:contain; }
    .hdr-logo-ph { font-size:8px; color:#999; font-weight:700; text-align:center; }
    .hdr-title { font-size:14px; font-weight:700; }
    .hdr-sub { font-size:10px; color:#333; margin-top:2px; }
    .hdr-msp { font-size:8.5px; color:#777; margin-top:4px; text-transform:uppercase; letter-spacing:.3px; }
    .hdr-meta { text-align:right; font-size:9px; color:#555; line-height:1.5; white-space:nowrap; }
    .hdr-meta b { font-size:11px; color:#000; }
    .sec { border:1px solid #000; margin-bottom:6px; }
    .sec-title { background:#1a3a5c; color:#fff; padding:4px 8px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
    .sec-body { padding:6px 8px; }
    .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:4px 16px; }
    .field { display:flex; gap:4px; align-items:baseline; padding:2px 0; border-bottom:1px dotted #ccc; }
    .field-lbl { font-size:9px; color:#555; text-transform:uppercase; white-space:nowrap; flex-shrink:0; }
    .field-val { font-size:11px; font-weight:500; }
    .consulta-card { border:1px solid #000; margin-bottom:6px; break-inside:avoid; }
    .consulta-hdr { background:#1a3a5c; color:#fff; padding:4px 8px; font-size:10px; font-weight:700; }
    .consulta-body { padding:8px; }
    .vitals-row { display:flex; gap:14px; flex-wrap:wrap; font-size:10px; color:#333; margin-bottom:6px; padding-bottom:6px; border-bottom:1px dotted #ccc; }
    .evo-lbl, .diag-lbl { font-size:9px; color:#555; text-transform:uppercase; margin-bottom:2px; }
    .evo-txt { font-size:10.5px; white-space:pre-wrap; line-height:1.4; margin-bottom:6px; }
    .diag-txt { font-size:10px; line-height:1.4; }
    .firma { display:flex; justify-content:flex-end; margin-top:20px; }
    .firma-box { text-align:center; width:200px; }
    .firma-line { border-top:1px solid #000; margin-bottom:4px; margin-top:30px; }
    .msp-logo { font-size:9px; color:#555; text-align:center; }
    @media print {
      body { padding:5px 10px; }
      button { display:none !important; }
    }
  </style>
</head>
<body>

<!-- ENCABEZADO MSP -->
<div class="hdr">
  <div class="hdr-logo-box">
    ${e.LOGO_PATH
      ? `<img src="${fotoUrl(e.LOGO_PATH)}" alt="Logo" onerror="this.parentElement.innerHTML='<span class=&quot;hdr-logo-ph&quot;>MSP</span>'">`
      : `<span class="hdr-logo-ph">MSP</span>`}
  </div>
  <div>
    <div class="hdr-title">Historia clínica única</div>
    <div class="hdr-sub">${e.NOMBRE_ESTABLECIMIENTO||'—'} — ${e.DIRECCION||''}${e.CANTON?' · '+e.CANTON:''}${e.PROVINCIA?' · '+e.PROVINCIA:''} · Tel: ${e.TELEFONO_EMPRESA||'—'}</div>
    <div class="hdr-msp">República del Ecuador · Ministerio de Salud Pública · ${e.TIPO_ESTABLECIMIENTO||'Consultorio Médico'} · ${e.NIVEL_ATENCION||'Primer Nivel'}</div>
  </div>
  <div class="hdr-meta">
    Formulario Nº<br><b>008</b><br>Código INEC<br><b>${e.CODIGO_INEC||'—'}</b>
  </div>
</div>

<!-- DATOS DEL PACIENTE -->
<div class="sec">
  <div class="sec-title">1. Datos del paciente</div>
  <div class="sec-body">
    <div class="grid2">
      <div class="field"><span class="field-lbl">Apellidos y nombres:</span><span class="field-val">${nombrePac}</span></div>
      <div class="field"><span class="field-lbl">N° Historia:</span><span class="field-val">${pac?.COD||'—'}</span></div>
      <div class="field"><span class="field-lbl">Cédula / Pasaporte:</span><span class="field-val">${pac?.IDENTIFICACION||'—'}</span></div>
      <div class="field"><span class="field-lbl">Fecha nacimiento:</span><span class="field-val">${fmtD008(pac?.FECHA_NACIMIENTO)}</span></div>
      <div class="field"><span class="field-lbl">Edad:</span><span class="field-val">${edad} años</span></div>
      <div class="field"><span class="field-lbl">Sexo:</span><span class="field-val">${sexo}</span></div>
      <div class="field"><span class="field-lbl">Tipo de sangre:</span><span class="field-val">${pac?.TIPO_SANGRE||'—'}</span></div>
      <div class="field"><span class="field-lbl">Teléfono:</span><span class="field-val">${pac?.TELEFONO||'—'}</span></div>
      <div class="field"><span class="field-lbl">Dirección:</span><span class="field-val">${pac?.DIRECCION_DOMICILIO||'—'}</span></div>
      <div class="field"><span class="field-lbl">Seguro / Convenio:</span><span class="field-val">${convenio||'PARTICULAR'}</span></div>
    </div>
  </div>
</div>

<!-- CONSULTAS -->
<div class="sec-title" style="border:1px solid #000;border-bottom:none">2. Registro de consultas (${registros008.length} consulta${registros008.length!==1?'s':''})</div>
<div style="margin-bottom:6px">
  ${registros008.map((r,i)=>tarjetaConsulta(r,i)).join('')}
</div>

<!-- FIRMA -->
<div class="firma">
  <div class="firma-box">
    ${firmaHtml}
    <div style="font-size:11px;font-weight:700">${medico?.NOMBRES || e.DIRECTOR_MEDICO || '—'}</div>
    <div style="font-size:9px;color:#555">${medico?.ESPECIALIDAD || e.CARGO_DIRECTOR || 'Médico Tratante'}</div>
    <div style="font-size:9px;color:#555">Reg. MSP: ${medico?.REGISTRO_MSP || e.REGISTRO_MSP_DIR || '—'}</div>
  </div>
</div>

<div class="msp-logo" style="margin-top:10px;border-top:1px solid #ccc;padding-top:6px">
  Formulario 008 — Historia Clínica Única · MSP Ecuador · ${e.NOMBRE_ESTABLECIMIENTO||''} · RUC: ${e.RUC||'—'}
</div>

</body></html>`;
  };

  const fmtD008 = s => { if(!s)return'—'; const p=String(s).slice(0,10); const[y,m,d]=p.split('-'); return `${d}/${m}/${y}`; };

  const imprimir008 = async () => {
    setPrintLoad(true);
    try {
      let regsImpr = [];
      if (printMode==='actual') {
        // Solo la consulta seleccionada
        regsImpr = [detalle];
      } else if (printMode==='todas') {
        // Todas las consultas del paciente con detalle
        const promesas = registros.map(r => api.get(`/historia/registro/${r.COD}`).then(res=>res.data).catch(()=>r));
        regsImpr = await Promise.all(promesas);
      } else {
        // Por rango de fechas
        const filtradas = registros.filter(r => {
          const f = r.FECHA?.slice(0,10);
          return (!printDesde||f>=printDesde) && (!printHasta||f<=printHasta);
        });
        const promesas = filtradas.map(r => api.get(`/historia/registro/${r.COD}`).then(res=>res.data).catch(()=>r));
        regsImpr = await Promise.all(promesas);
      }

      const html = generarHTML008(regsImpr, paciente, {});
      const w = window.open('','_blank','width=1000,height=800');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(()=>{ w.print(); }, 800);
      setShowPrint(false);
    } catch(e) { alert('Error al generar: '+e.message); }
    setPrintLoad(false);
  };

  /* ════ GENERADOR FORMULARIO 002 MSP ════ */
  const generarHTML002 = (reg, pac) => {
    const e = estab;
    const fmtD002 = s => { if(!s)return'—'; const p=String(s).slice(0,10); const[y,m,d]=p.split('-'); return `${d}/${m}/${y}`; };
    const nombrePac = [pac?.APELLIDO_PATERNO,pac?.APELLIDO_MATERNO,pac?.PRIMER_NOMBRE].filter(Boolean).join(' ');
    const edad = pac?.FECHA_NACIMIENTO ? Math.floor((Date.now()-new Date(pac.FECHA_NACIMIENTO))/3.156e10) : '—';
    const sv = reg.signosVitales?.[0] || {};
    const diags = reg.diagnosticos || [];

    // Parsear flags JSON
    const pf = v => { try{ return typeof v==='string'?JSON.parse(v):v||{}; }catch{ return {}; } };
    const antPer  = pf(reg.ANT_PER_FLAGS);
    const antFam  = pf(reg.ANT_FAM_FLAGS);
    const revOrg  = pf(reg.REV_ORGANOS_FLAGS);
    const exReg   = pf(reg.EXAM_REGIONAL_FLAGS);
    const exSist  = pf(reg.EXAM_SISTEMICO_FLAGS);

    const chk = (val) => val ? '☑' : '☐';
    const IMC = sv.PESO && sv.TALLA ? (parseFloat(sv.PESO) / Math.pow(parseFloat(sv.TALLA)/100, 2)).toFixed(1) : '—';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Formulario 002 - ${nombrePac}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:10px;color:#000;padding:8px 12px}
    table{width:100%;border-collapse:collapse}
    td,th{border:1px solid #555;padding:3px 5px;vertical-align:top;font-size:9px}
    .sec-hdr{background:#1F3864;color:#fff;padding:3px 8px;font-size:9px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
    .sub-hdr{background:#BDD7EE;padding:2px 6px;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.3px}
    .lbl{font-size:8px;color:#555;display:block;margin-bottom:1px}
    .val{font-size:10px;font-weight:500}
    .chk-row{display:flex;flex-wrap:wrap;gap:6px;padding:4px 0}
    .chk-item{font-size:9px;white-space:nowrap}
    .lines{min-height:32px;background:repeating-linear-gradient(transparent,transparent 13px,#ccc 13px,#ccc 14px);padding:2px 4px;font-size:9px}
    .firma-line{border-top:1px solid #000;margin-top:30px;margin-bottom:4px}
    @media print{body{padding:4px 8px}button{display:none!important}}
  </style>
</head>
<body>

<!-- ENCABEZADO -->
<table style="margin-bottom:4px">
  <tr>
    <td style="width:22%;border:1px solid #555;padding:5px 7px">
      <div class="lbl">República del Ecuador</div>
      <div style="font-size:10px;font-weight:700">Ministerio de Salud Pública</div>
      <div class="lbl" style="margin-top:4px">Tipo establecimiento</div>
      <div style="font-size:9px">${e.TIPO_ESTABLECIMIENTO||'Consultorio Médico'}</div>
      <div class="lbl" style="margin-top:2px">Nivel de atención</div>
      <div style="font-size:9px">${e.NIVEL_ATENCION||'Primer Nivel'}</div>
    </td>
    <td style="width:56%;border:1px solid #555;padding:5px 7px;text-align:center">
      <div style="font-size:13px;font-weight:700;letter-spacing:.5px">HISTORIA CLÍNICA ÚNICA</div>
      <div style="font-size:11px;font-weight:700;margin-top:3px">${e.NOMBRE_ESTABLECIMIENTO||'—'}</div>
      <div style="font-size:9px;margin-top:2px;color:#444">${e.DIRECCION||''} ${e.CANTON?'· '+e.CANTON:''} ${e.PROVINCIA?'· '+e.PROVINCIA:''}</div>
      <div style="font-size:9px;color:#444">Tel: ${e.TELEFONO_EMPRESA||'—'} · ${e.EMAIL_EMPRESA||''}</div>
    </td>
    <td style="width:22%;border:1px solid #555;padding:5px 7px;text-align:center">
      <div class="lbl">Código INEC</div>
      <div style="font-size:14px;font-weight:700;border:1px solid #555;padding:1px 6px;display:inline-block">${e.CODIGO_INEC||'—'}</div>
      <div class="lbl" style="margin-top:4px">Formulario N°</div>
      <div style="font-size:18px;font-weight:700;color:#1F3864">002</div>
      <div class="lbl" style="margin-top:2px">N° Historia</div>
      <div style="font-size:11px;font-weight:700">${pac?.COD||'—'}</div>
    </td>
  </tr>
</table>

<!-- A. DATOS DEL PACIENTE -->
<div class="sec-hdr">A. Datos de identificación del usuario / paciente</div>
<table style="margin-bottom:0">
  <tr>
    <td style="width:30%"><span class="lbl">Apellidos y nombres</span><span class="val">${nombrePac}</span></td>
    <td style="width:16%"><span class="lbl">Cédula / Pasaporte</span><span class="val">${pac?.IDENTIFICACION||'—'}</span></td>
    <td style="width:12%"><span class="lbl">Fecha nacimiento</span><span class="val">${fmtD002(pac?.FECHA_NACIMIENTO)}</span></td>
    <td style="width:8%"><span class="lbl">Edad</span><span class="val">${edad} a</span></td>
    <td style="width:10%"><span class="lbl">Sexo</span><span class="val">${pac?.C_SEXO==='M'?'☑ M  ☐ F':'☐ M  ☑ F'}</span></td>
    <td style="width:12%"><span class="lbl">Tipo sangre</span><span class="val">${pac?.TIPO_SANGRE||'—'}</span></td>
    <td style="width:12%"><span class="lbl">Fecha consulta</span><span class="val">${fmtD002(reg.FECHA)}</span></td>
  </tr>
  <tr>
    <td colspan="3"><span class="lbl">Dirección domicilio</span><span class="val">${pac?.DIRECCION_DOMICILIO||'—'}</span></td>
    <td colspan="2"><span class="lbl">Teléfono</span><span class="val">${pac?.TELEFONO||'—'}</span></td>
    <td colspan="2"><span class="lbl">Seguro / Convenio</span><span class="val">${convenio||'PARTICULAR'}</span></td>
  </tr>
</table>

<!-- B. MOTIVO -->
<div class="sec-hdr">B. Motivo de consulta — ${reg.C_TIPO_CONSULTA==='SUB'?'☑ Subsecuente  ☐ Primera':'☑ Primera  ☐ Subsecuente'}</div>
<table><tr><td><div class="lines">${(reg.EVOLUCION||'').split('\n')[0]||''}</div></td></tr></table>

<!-- C. ANTECEDENTES PERSONALES -->
<div class="sec-hdr">C. Antecedentes patológicos personales</div>
<table style="margin-bottom:0">
  <tr style="background:#BDD7EE">
    <td>${chk(antPer.CARDIOPATIA)} Cardiopatía</td>
    <td>${chk(antPer.HIPERTENSION)} Hipertensión</td>
    <td>${chk(antPer.ENF_VALVULAR)} Enf. Valvular</td>
    <td>${chk(antPer.ENDOCRINOLOGICO)} Endocrinológico</td>
    <td>${chk(antPer.CANCER)} Cáncer</td>
    <td>${chk(antPer.TUBERCULOSIS)} Tuberculosis</td>
    <td>${chk(antPer.ENF_INFECCIOSA)} Enf. Infecciosa</td>
    <td>${chk(antPer.ENF_HEREDITARIA)} Enf. Hereditaria</td>
    <td>${chk(antPer.QUIRURGICO)} Quirúrgico</td>
    <td>${chk(antPer.GINECO_OBS)} Gineco-Obst.</td>
    <td>${chk(antPer.OTROS)} Otros</td>
  </tr>
  <tr><td colspan="11"><div class="lines">${reg.ANT_PER_DETALLE||''}</div></td></tr>
</table>

<!-- D. ANTECEDENTES FAMILIARES -->
<div class="sec-hdr">D. Antecedentes patológicos familiares</div>
<table style="margin-bottom:0">
  <tr style="background:#BDD7EE">
    <td>${chk(antFam.CARDIOPATIA)} Cardiopatía</td>
    <td>${chk(antFam.HIPERTENSION)} Hipertensión</td>
    <td>${chk(antFam.ENF_VALVULAR)} Enf. Valvular</td>
    <td>${chk(antFam.ENDOCRINOLOGICO)} Endocrinológico</td>
    <td>${chk(antFam.CANCER)} Cáncer</td>
    <td>${chk(antFam.TUBERCULOSIS)} Tuberculosis</td>
    <td>${chk(antFam.ENF_INFECCIOSA)} Enf. Infecciosa</td>
    <td>${chk(antFam.ENF_HEREDITARIA)} Enf. Hereditaria</td>
    <td>${chk(antFam.OTROS)} Otros</td>
  </tr>
  <tr><td colspan="9"><div class="lines">${reg.ANT_FAM_DETALLE||''}</div></td></tr>
</table>

<!-- E. ENFERMEDAD ACTUAL -->
<div class="sec-hdr">E. Enfermedad o problema actual</div>
<table><tr><td><div class="lines" style="min-height:44px">${reg.EVOLUCION||''}</div></td></tr></table>

<!-- F. CONSTANTES VITALES -->
<div class="sec-hdr">F. Constantes vitales y antropometría</div>
<table>
  <tr style="background:#BDD7EE">
    <th>Fecha</th><th>Hora</th><th>Temp. °C</th><th>P.A. mmHg</th>
    <th>Pulso/min</th><th>Frec. resp.</th><th>Peso kg</th><th>Talla cm</th>
    <th>IMC</th><th>Perím. abd.</th><th>Glucosa</th><th>Sat O₂ %</th>
  </tr>
  <tr>
    <td>${fmtD002(reg.FECHA)}</td>
    <td>${reg.FECHA?new Date(reg.FECHA).toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
    <td>${sv.TEMP_AXILAR||'—'}</td>
    <td>${sv.PRESION_ARTERIAL||'—'}</td>
    <td>${sv.PULSO||'—'}</td>
    <td>${sv.RESPIRATORIA||'—'}</td>
    <td>${sv.PESO||'—'}</td>
    <td>${sv.TALLA||'—'}</td>
    <td>${IMC}</td>
    <td>${sv.CINTURA_ABDOMINAL||'—'}</td>
    <td>—</td>
    <td>${sv.SATURACION_OXIGENO||sv.SATURACION_O2||'—'}</td>
  </tr>
</table>

<!-- G. REVISIÓN ÓRGANOS -->
<div class="sec-hdr">G. Revisión actual de órganos y sistemas</div>
<table style="margin-bottom:0">
  <tr style="background:#BDD7EE">
    <td>${chk(revOrg.PIEL_ANEXOS)} Piel-Anexos</td>
    <td>${chk(revOrg.RESPIRATORIO)} Respiratorio</td>
    <td>${chk(revOrg.DIGESTIVO)} Digestivo</td>
    <td>${chk(revOrg.MUSCULO_ESQ)} Músculo-Esq.</td>
    <td>${chk(revOrg.HEMO_LINFATICO)} Hemo-Linfático</td>
    <td>${chk(revOrg.ORGANOS_SENTIDOS)} Órg. Sentidos</td>
    <td>${chk(revOrg.CARDIO_VASCULAR)} Cardio-Vasc.</td>
    <td>${chk(revOrg.GENITO_URINARIO)} Génito-Urin.</td>
    <td>${chk(revOrg.ENDOCRINO)} Endocrino</td>
    <td>${chk(revOrg.NERVIOSO)} Nervioso</td>
  </tr>
  <tr><td colspan="10"><div class="lines">${reg.REV_ORGANOS_DET||''}</div></td></tr>
</table>

<!-- H. EXAMEN FÍSICO -->
<div class="sec-hdr">H. Examen físico</div>
<table style="margin-bottom:0">
  <tr><td colspan="15" class="sub-hdr">Regional</td></tr>
  <tr style="background:#BDD7EE">
    <td>${chk(exReg.PIEL_FANERAS)} Piel-Fan.</td>
    <td>${chk(exReg.CABEZA)} Cabeza</td>
    <td>${chk(exReg.OJOS)} Ojos</td>
    <td>${chk(exReg.OIDOS)} Oídos</td>
    <td>${chk(exReg.NARIZ)} Nariz</td>
    <td>${chk(exReg.BOCA)} Boca</td>
    <td>${chk(exReg.OROFARINGE)} Orofaringe</td>
    <td>${chk(exReg.CUELLO)} Cuello</td>
    <td>${chk(exReg.AXILAS_MAMAS)} Axilas-Mamas</td>
    <td>${chk(exReg.TORAX)} Tórax</td>
    <td>${chk(exReg.ABDOMEN)} Abdomen</td>
    <td>${chk(exReg.INGLE_PERINE)} Ingle-Periné</td>
    <td>${chk(exReg.MIEMBROS_SUP)} Miemb. Sup.</td>
    <td>${chk(exReg.MIEMBROS_INF)} Miemb. Inf.</td>
    <td>${chk(exReg.COLUMNA)} Columna</td>
  </tr>
  <tr><td colspan="15" class="sub-hdr">Sistémico</td></tr>
  <tr style="background:#BDD7EE">
    <td colspan="2">${chk(exSist.URINARIO)} Urinario</td>
    <td colspan="2">${chk(exSist.RESPIRATORIO)} Respiratorio</td>
    <td colspan="2">${chk(exSist.MUSCULO_ESQ)} Músculo-Esq.</td>
    <td colspan="2">${chk(exSist.HEMO_LINFATICO)} Hemo-Linfático</td>
    <td colspan="2">${chk(exSist.VASCULAR)} Vascular</td>
    <td colspan="2">${chk(exSist.DIGESTIVO)} Digestivo</td>
    <td colspan="2">${chk(exSist.ENDOCRINO)} Endocrino</td>
    <td>${chk(exSist.NEUROLOGICO)} Neurológico</td>
  </tr>
  <tr><td colspan="15"><div class="lines">${reg.EXAM_FISICO_DET||''}</div></td></tr>
</table>

<!-- I. DIAGNÓSTICOS -->
<div class="sec-hdr">I. Diagnóstico</div>
<table>
  <tr style="background:#BDD7EE">
    <th style="width:5%">N°</th>
    <th style="width:50%">Diagnóstico / Problema</th>
    <th style="width:10%">CIE-10</th>
    <th style="width:8%">PRE</th>
    <th style="width:8%">DEF</th>
    <th style="width:5%">N°</th>
    <th style="width:14%">CIE-10</th>
  </tr>
  ${diags.slice(0,3).map((d,i)=>`
  <tr>
    <td style="text-align:center">${i+1}</td>
    <td style="font-weight:500">${d.CIE10_DESC||d.desc||'—'}</td>
    <td style="text-align:center;font-weight:700">${d.COD_CIE10}</td>
    <td style="text-align:center">${d.C_TIPO_DIAGNOSTICO==='PRE'?'☑':'☐'}</td>
    <td style="text-align:center">${d.C_TIPO_DIAGNOSTICO!=='PRE'?'☑':'☐'}</td>
    <td style="text-align:center">${i+4}</td>
    <td></td>
  </tr>`).join('')}
  ${diags.length===0?`<tr><td style="text-align:center">1</td><td></td><td></td><td style="text-align:center">☐</td><td style="text-align:center">☐</td><td style="text-align:center">4</td><td></td></tr>`:''}
</table>

<!-- J. PLAN DE TRATAMIENTO -->
<div class="sec-hdr">J. Plan de tratamiento</div>
<table><tr><td><div class="lines" style="min-height:44px">${reg.PLAN_TRATAMIENTO||''}</div></td></tr></table>

<!-- K. DATOS DEL PROFESIONAL -->
<div class="sec-hdr">K. Datos del profesional responsable</div>
<table>
  <tr style="background:#BDD7EE">
    <th style="width:12%">Fecha</th>
    <th style="width:10%">Hora</th>
    <th style="width:20%">Primer nombre</th>
    <th style="width:20%">Primer apellido</th>
    <th style="width:20%">Segundo apellido</th>
    <th style="width:18%">N° identificación</th>
  </tr>
  <tr>
    <td>${fmtD002(reg.FECHA)}</td>
    <td>${reg.FECHA?new Date(reg.FECHA).toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
    <td colspan="3" style="font-weight:500">${reg.MEDICO||reg.USUARIO_REGISTRO||'—'}</td>
    <td>—</td>
  </tr>
  <tr style="background:#BDD7EE">
    <td colspan="2" style="font-size:8px">Firma</td>
    <td colspan="2" style="font-size:8px">Sello</td>
    <td colspan="2" style="font-size:8px">Registro MSP: ${e.REGISTRO_MSP_DIR||'—'}</td>
  </tr>
  <tr>
    <td colspan="2" style="height:35px"></td>
    <td colspan="2" style="height:35px"></td>
    <td colspan="2" style="font-size:9px;vertical-align:middle">${e.DIRECTOR_MEDICO||'—'}<br/>${e.CARGO_DIRECTOR||''}</td>
  </tr>
</table>

<div style="margin-top:6px;border-top:1px solid #ccc;padding-top:4px;font-size:8px;color:#555;text-align:center">
  SNS-MSP / HCU-form.002 / 2021 &nbsp;·&nbsp; CONSULTA EXTERNA &nbsp;·&nbsp; ${e.NOMBRE_ESTABLECIMIENTO||''} &nbsp;·&nbsp; RUC: ${e.RUC||'—'}
</div>

</body></html>`;
  };

  const imprimir002 = async () => {
    if (!detalle) { alert('Selecciona una consulta primero'); return; }
    setPrint002Load(true);
    try {
      // Cargar detalle completo si no tiene los campos 002
      let reg = detalle;
      if (!reg.ANT_PER_FLAGS && reg.COD) {
        const { data } = await api.get(`/historia/registro/${reg.COD}`);
        reg = data;
      }
      const html = generarHTML002(reg, paciente);
      const w = window.open('','_blank','width=1000,height=900');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(()=>{ w.print(); }, 800);
    } catch(e) { alert('Error al generar: '+e.message); }
    setPrint002Load(false);
  };

  const _doAbrirEditar = det => {
    setNueva(true); setEditCod(det.COD); setModalTab('consulta'); setMsg('');
    api.get('/historia/ia-config').then(r=>setIaActivo(r.data?.activo||false)).catch(()=>{});
    const esSoap=det.EVOLUCION?.startsWith('S (Subjetivo):');
    if(esSoap){
      setTipoEvol('soap');
      const p={S:'',O:'',A:'',P:''};
      ['S','O','A','P'].forEach(k=>{ const m=det.EVOLUCION.match(new RegExp(`${k} \\([^)]+\\):\n([\\s\\S]*?)(?=\n\n[SOAP] \\(|$)`)); if(m)p[k]=m[1].trim(); });
      setSoapForm(p);
    } else {
      setTipoEvol('libre');
      const ev = det.EVOLUCION || '';
      const mA = ev.match(/\n\nAnálisis:\n([\s\S]*?)(?=\n\nPlan:\n|$)/);
      const mP = ev.match(/\n\nPlan:\n([\s\S]*)$/);
      if(mA || mP){
        const corte = ev.indexOf('\n\nAnálisis:\n');
        setForm(f=>({...f,EVOLUCION: corte!==-1 ? ev.slice(0,corte) : ev}));
        setLibreAP({A:mA?mA[1].trim():'', P:mP?mP[1].trim():''});
      } else {
        setForm(f=>({...f,EVOLUCION: ev}));
        setLibreAP({A:'',P:''});
      }
    }
    // Carga la receta ya guardada de esta consulta (el backend la empareja
    // por mismo paciente + mismo día — det.recetas viene de GET /registro/:id).
    // Antes esto se reseteaba a [] siempre, por eso "no cargaba la receta"
    // al editar una consulta vieja.
    if (det.recetas?.length > 0) {
      setRecItems(det.recetas
        .filter(r => r.MEDICAMENTO)
        .map(r => ({ MEDICAMENTO: r.MEDICAMENTO, COD_ITEM: r.COD_ITEM || null, CANTIDAD: r.CANTIDAD || '',
          MANANA: r.MANANA ?? '', MEDIODIA: r.MEDIODIA ?? '', NOCHE: r.NOCHE ?? '', DIAS: r.DIAS ?? '',
          INDICACION: r.INDICACION || '' })).map(x => ({ ...x, CANTIDAD_MANUAL: cantidadEsManual(x) })));
      setRecetaIndicaciones(det.recetas[0]?.INDICACIONES_GENERALES || '');
    } else {
      setRecItems([]); setRecetaIndicaciones('');
    }
    cargarPlantillas();

    // Cargar campos que antes solo se llenaban al crear — evita que al
    // editar se sobreescriban con vacío (el payload manda '{}' stringified,
    // que no es null, así que el IFNULL del backend no los protege).
    const pj = v => { try { return typeof v==='string' ? JSON.parse(v) : (v||{}); } catch(_) { return {}; } };
    setForm002({
      C_TIPO_CONSULTA:      det.C_TIPO_CONSULTA || 'PRI',
      ANT_PER_FLAGS:        pj(det.ANT_PER_FLAGS),
      ANT_PER_DETALLE:      det.ANT_PER_DETALLE || '',
      ANT_FAM_FLAGS:        pj(det.ANT_FAM_FLAGS),
      ANT_FAM_DETALLE:      det.ANT_FAM_DETALLE || '',
      REV_ORGANOS_FLAGS:    pj(det.REV_ORGANOS_FLAGS),
      REV_ORGANOS_DET:      det.REV_ORGANOS_DET || '',
      EXAM_REGIONAL_FLAGS:  pj(det.EXAM_REGIONAL_FLAGS),
      EXAM_SISTEMICO_FLAGS: pj(det.EXAM_SISTEMICO_FLAGS),
      EXAM_FISICO_DET:      det.EXAM_FISICO_DET || '',
      PLAN_TRATAMIENTO:     det.PLAN_TRATAMIENTO || '',
    });
    setForm(f=>({...f,
      MOTIVO_CONSULTA: det.MOTIVO_CONSULTA || '',
      SIGNOS_ALARMA:   det.SIGNOS_ALARMA || '',
      OBSERVACIONES:   det.OBSERVACIONES || '',
    }));
  };

  const abrirEditar = async det => {
    try {
      const { data } = await api.get(`/historia/${det.COD}/editar-info`);
      setEditInfo(data);
      if (data.requiereAutorizacion) {
        setEditPend(det); setAuthMotivo(''); setAuthMsg('');
        setShowAuth(true); return;
      }
    } catch(_) {}
    _doAbrirEditar(det);
  };

  const confirmarAutorizacion = async () => {
    if (!authMotivo.trim()) { setAuthMsg('El motivo de la edición es obligatorio'); return; }
    setAuthLoad(true); setAuthMsg('');
    try {
      await api.post(`/historia/${editPendiente.COD}/autorizar-edicion`, { MOTIVO: authMotivo });
      setShowAuth(false);
      _doAbrirEditar(editPendiente);
    } catch(e) {
      setAuthMsg(e.response?.data?.error || e.message);
    } finally {
      setAuthLoad(false);
    }
  };


  const onBusqPac = v => {
    setPacBusq(v); clearTimeout(timer.current);
    if(v.length<2){setPacSugs([]);return;}
    timer.current=setTimeout(async()=>{ try{ const{data}=await api.get(`/pacientes?q=${encodeURIComponent(v)}&limit=8`); setPacSugs(data.data||data); }catch(_){} },300);
  };

  const onCie10 = v => {
    setCie10Q(v); clearTimeout(timer.current);
    if(v.length<2){setCie10S([]);return;}
    timer.current=setTimeout(async()=>{ try{ const{data}=await api.get(`/historia/buscar/cie10?q=${encodeURIComponent(v)}`); setCie10S(data); }catch(_){} },300);
  };

  const addDiag = d => {
    if(form.diagnosticos.find(x=>x.COD_CIE10===d.COD))return;
    setForm(f=>({...f,diagnosticos:[...f.diagnosticos,{COD_CIE10:d.COD,desc:d.DIAGNOSTICO,C_TIPO_DIAGNOSTICO:'DEF'}]}));
    setCie10Q(''); setCie10S([]);
  };

  const buscarMed = async (idx,q) => {
    if(q.length<2){setMedSugs(s=>({...s,[idx]:[]}));return;}
    try{ const{data}=await api.get(`/historia/medicamentos?q=${encodeURIComponent(q)}`); setMedSugs(s=>({...s,[idx]:data})); }catch(_){}
  };

  const guardarEnVademecum = async n => { if(!n?.trim())return; try{ await api.post('/historia/medicamentos',{MEDICAMENTO:n}); }catch(_){} };

  const aplicarPlantilla = pl => {
    setRecItems(pl.items.map(it=>({MEDICAMENTO:it.MEDICAMENTO,CANTIDAD:it.CANTIDAD||'',
      MANANA:it.MANANA??'',MEDIODIA:it.MEDIODIA??'',NOCHE:it.NOCHE??'',DIAS:it.DIAS??'',
      INDICACION:it.INDICACION||''})).map(x=>({...x,CANTIDAD_MANUAL:cantidadEsManual(x)}))); setShowPl(false); setModalTab('receta');
  };

  const guardarComoPlantilla = async () => {
    if(!nombrePlantilla.trim()){setMsg('Escribe un nombre para la plantilla');return;}
    const items=recetaItems.filter(r=>r.MEDICAMENTO?.trim()); if(!items.length){setMsg('Agrega al menos un medicamento');return;}
    setSavingPl(true);
    try{ await api.post('/historia/plantillas-receta',{NOMBRE:nombrePlantilla,items}); setMsg('✓ Plantilla guardada'); setNomPl(''); cargarPlantillas(); }
    catch(e){setMsg('Error: '+(e.response?.data?.error||e.message));}
    finally{setSavingPl(false);}
  };

  /* ═══════════════════════════════════════════════════════
     AUTOGUARDADO — consulta y receta del modal Nueva/Editar
     ═══════════════════════════════════════════════════════
     Mismo patrón que ya funciona en la instancia de Neuromed. Solo se
     incluyen en el snapshot los campos que existen de verdad en ESTE
     archivo (acá no hay antecedentes/RAS/examen físico itemizados como
     en la versión de Neuromed — este formulario es más simple). */
  const snapshotBorrador = () => ({
    fmtConsulta, tipoEvol, modalTab, form, form002, soapForm, libreAP,
    recetaItems, recetaIndicaciones,
  });

  const restaurarBorrador = c => {
    if (!c) return;
    if (c.fmtConsulta) setFmtConsulta(c.fmtConsulta);
    if (c.tipoEvol) setTipoEvol(c.tipoEvol);
    if (c.modalTab) setModalTab(c.modalTab);
    if (c.form) setForm(c.form);
    if (c.form002) setForm002(c.form002);
    if (c.soapForm) setSoapForm(c.soapForm);
    if (c.libreAP) setLibreAP(c.libreAP);
    if (c.recetaItems) setRecItems(c.recetaItems);
    setRecetaIndicaciones(c.recetaIndicaciones || '');
  };

  const guardarBorradorAuto = async () => {
    if (!codPac) return;
    try {
      const { data } = await api.post('/historia/borrador', {
        COD: borradorId || undefined,
        COD_PACIENTE: codPac,
        TIPO: 'CONSULTA',
        COD_HISTORIAL_CLINICO: editCod || null,
        CONTENIDO: snapshotBorrador(),
      });
      if (data?.COD) setBorradorId(data.COD);
      setAutoguardadoTexto(`Borrador guardado ${new Date().toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'})}`);
    } catch (_) { /* el autoguardado nunca debe interrumpir al médico con un error */ }
  };
  // Ref con la versión más reciente de guardarBorradorAuto: el setInterval de
  // abajo se crea una sola vez por sesión de modal, así que sin esto seguiría
  // llamando para siempre a la función "congelada" del primer render (con
  // form/form002 vacíos) en vez de leer lo que el médico va escribiendo.
  const guardarBorradorAutoRef = useRef(guardarBorradorAuto);
  useEffect(() => { guardarBorradorAutoRef.current = guardarBorradorAuto; });

  useEffect(() => {
    if (!showNueva || !codPac) return;
    setBorradorInfo(null);
    api.get('/historia/borrador', { params: { cod_paciente: codPac, tipo: 'CONSULTA', cod_historial_clinico: editCod || undefined } })
      .then(({ data }) => { if (data?.COD) setBorradorInfo(data); })
      .catch(() => {});
    const t = setInterval(() => guardarBorradorAutoRef.current?.(), 30000);
    return () => clearInterval(t);
  }, [showNueva, editCod, codPac]);

  const restaurarBorradorPendiente = () => {
    if (!borradorInfo) return;
    let contenido = borradorInfo.CONTENIDO;
    try { if (typeof contenido === 'string') contenido = JSON.parse(contenido); } catch (_) { contenido = null; }
    restaurarBorrador(contenido);
    setBorradorId(borradorInfo.COD);
    setBorradorInfo(null);
  };
  const descartarBorradorPendiente = () => {
    if (borradorInfo?.COD) api.delete(`/historia/borrador/${borradorInfo.COD}`).catch(() => {});
    setBorradorInfo(null);
  };

  const guardar = async () => {
    if(tipoEvol==='libre'&&!form.EVOLUCION?.trim()){setMsg('La evolución es obligatoria');return;}
    if(tipoEvol==='soap'&&!soapForm.S?.trim()){setMsg('El campo Subjetivo (S) es obligatorio');return;}
    if(editCod&&editInfo&&!editInfo.dentroPlazo&&!form._MOTIVO?.trim()){setMsg('Debes ingresar el motivo de la edición fuera de plazo');return;}
    setSaving(true); setMsg('');
    try{
      const evFinal=tipoEvol==='soap'
        ?`S (Subjetivo):\n${soapForm.S}\n\nO (Objetivo):\n${soapForm.O}\n\nA (Análisis):\n${soapForm.A}\n\nP (Plan):\n${soapForm.P}`
        :`${form.EVOLUCION}${(libreAP.A?.trim()||libreAP.P?.trim())?`\n\nAnálisis:\n${libreAP.A||''}\n\nPlan:\n${libreAP.P||''}`:''}`;
      const payload={COD_PACIENTE:codPac,EVOLUCION:evFinal,C_COMPLEJIDAD:form.C_COMPLEJIDAD||'NRM',
        diagnosticos:form.diagnosticos.map(d=>({COD_CIE10:d.COD_CIE10,C_TIPO_DIAGNOSTICO:d.C_TIPO_DIAGNOSTICO})),
        signosVitales:form.signosVitales,recetaItems:recetaItems.filter(r=>r.MEDICAMENTO?.trim()).map(r=>{
          const total=((parseFloat(r.MANANA)||0)+(parseFloat(r.MEDIODIA)||0)+(parseFloat(r.NOCHE)||0))*(parseFloat(r.DIAS)||0);
          return {...r, CANTIDAD: (r.CANTIDAD_MANUAL && parseFloat(r.CANTIDAD)>0) ? String(r.CANTIDAD) : (total>0 ? String(total) : (r.CANTIDAD||'1'))};
        }),
        recetaIndicaciones:recetaIndicaciones||null,
        MOTIVO_EDICION:form._MOTIVO||null, forzar:editInfo?.esAdmin||false,
        // RAS y Examen físico — siempre se envían, sin importar el formato elegido
        REV_ORGANOS_FLAGS: JSON.stringify(form002.REV_ORGANOS_FLAGS),
        REV_ORGANOS_DET:   form002.REV_ORGANOS_DET,
        EXAM_FISICO_DET:   form002.EXAM_FISICO_DET,
        // Motivo de consulta se guarda siempre (Formato libre y Formulario 002 MSP);
        // signos de alarma y observaciones solo existen en Formato libre
        MOTIVO_CONSULTA: form.MOTIVO_CONSULTA||null,
        ...(fmtConsulta==='libre' ? {
          SIGNOS_ALARMA:   form.SIGNOS_ALARMA||null,
          OBSERVACIONES:   form.OBSERVACIONES||null,
        } : {}),
        // Campos exclusivos del Formulario 002 MSP
        ...(fmtConsulta==='002' ? {
          C_TIPO_CONSULTA:      form002.C_TIPO_CONSULTA,
          ANT_PER_FLAGS:        JSON.stringify(form002.ANT_PER_FLAGS),
          ANT_PER_DETALLE:      form002.ANT_PER_DETALLE,
          ANT_FAM_FLAGS:        JSON.stringify(form002.ANT_FAM_FLAGS),
          ANT_FAM_DETALLE:      form002.ANT_FAM_DETALLE,
          EXAM_REGIONAL_FLAGS:  JSON.stringify(form002.EXAM_REGIONAL_FLAGS),
          EXAM_SISTEMICO_FLAGS: JSON.stringify(form002.EXAM_SISTEMICO_FLAGS),
          PLAN_TRATAMIENTO:     form002.PLAN_TRATAMIENTO,
        } : {}),
      };
      let resp;
      if(editCod){ resp = await api.put(`/historia/${editCod}`,payload); }
      else{ resp = await api.post('/historia',payload); }
      // La consulta (y su receta) ya quedaron guardadas de verdad -- el
      // borrador de autoguardado ya no sirve para nada, se borra.
      if (borradorId) { api.delete(`/historia/borrador/${borradorId}`).catch(() => {}); setBorradorId(null); }
      setAutoguardadoTexto('');
      setNueva(false); setForm({diagnosticos:[],signosVitales:{}});
      setMsg(resp.data?.recetaError
        ? `⚠️ Consulta registrada, pero la receta no se guardó: ${resp.data.recetaError}`
        : '✓ Consulta registrada');
      await cargarHC(codPac); cargarRecetasDet(codPac); cargarCertificadosDet(codPac); cargarConsentimientosDet(codPac); cargarForm024Det(codPac);
    }catch(e){setMsg('Error: '+(e.response?.data?.error||e.message));}
    finally{setSaving(false);}
  };

  const imprimirReceta = () => {
    if(!recetaItems.filter(r=>r.MEDICAMENTO?.trim()).length){setMsg('Agrega medicamentos primero');return;}
    const pac=paciente;
    const html=`<!DOCTYPE html><html><head><style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:0;padding:20px}
      .hdr{border-bottom:2px solid #1a3a5c;padding-bottom:10px;margin-bottom:12px;display:flex;justify-content:space-between}
      .hdr h2{margin:0;font-size:16px;color:#1a3a5c}.hdr p{margin:2px 0;color:#666}
      .pac-info{background:#f5f5f5;padding:10px;border-radius:6px;margin-bottom:14px;display:grid;grid-template-columns:1fr 1fr;gap:4px}
      .pac-info span{font-size:11px;color:#555}.pac-info strong{color:#1a3a5c}
      .rx{font-size:36px;color:#1a3a5c;font-weight:bold;margin:10px 0 6px}
      .med-item{border-left:3px solid #2e6da4;padding:8px 12px;margin-bottom:10px;background:#fafafa}
      .med-nom{font-size:14px;font-weight:bold;color:#1a3a5c}.med-det{font-size:11px;color:#666;margin-top:3px}
      .firma{margin-top:40px;border-top:1px solid #ccc;padding-top:8px;text-align:center;font-size:11px;color:#666}
    </style></head><body>
    <div class="hdr"><div><h2>RECETA MÉDICA</h2><p>Fecha: ${new Date().toLocaleDateString('es-EC')}</p></div></div>
    <div class="pac-info">
      <div><span>Paciente: </span><strong>${[pac?.APELLIDO_PATERNO,pac?.APELLIDO_MATERNO,pac?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</strong></div>
      <div><span>Cédula: </span><strong>${pac?.IDENTIFICACION||''}</strong></div>
      <div><span>Edad: </span><strong>${pac?.FECHA_NACIMIENTO?Math.floor((Date.now()-new Date(pac.FECHA_NACIMIENTO))/3.156e10)+' años':''}</strong></div>
      <div><span>Fecha: </span><strong>${new Date().toLocaleDateString('es-EC')}</strong></div>
    </div>
    <div class="rx">℞</div>
    ${recetaItems.filter(r=>r.MEDICAMENTO?.trim()).map((it,i)=>`
      <div class="med-item"><div class="med-nom">${i+1}. ${it.MEDICAMENTO}</div>
      ${it.CANTIDAD?`<div class="med-det">Cantidad: ${it.CANTIDAD}</div>`:''}
      ${it.INDICACION?`<div class="med-det">📋 ${it.INDICACION}</div>`:''}</div>`).join('')}
    <div class="firma"><div style="margin-bottom:40px"></div>_________________________________<br>Firma del médico tratante</div>
    </body></html>`;
    const w=window.open('','_blank','width=700,height=900'); w.document.write(html); w.document.close(); w.focus(); setTimeout(()=>w.print(),500);
  };

  const imprimirRecetaExistente = receta => {
    const pac=paciente;
    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:0;padding:24px}
      .hdr{border-bottom:2px solid #1a3a5c;padding-bottom:10px;margin-bottom:14px;display:flex;justify-content:space-between}
      .hdr h2{margin:0;font-size:17px;color:#1a3a5c;font-weight:700}.hdr p{margin:2px 0;color:#666;font-size:11px}
      .pac-info{background:#f8f9fa;padding:10px 14px;border-radius:6px;margin-bottom:16px;display:grid;grid-template-columns:1fr 1fr;gap:5px}
      .pac-info .lbl{font-size:10px;color:#888;text-transform:uppercase}.pac-info .val{font-size:12px;font-weight:500;color:#1a3a5c}
      .rx-title{font-size:28px;color:#2e6da4;font-weight:900;margin:10px 0 14px;font-style:italic}
      .med-item{border-left:3px solid #2e6da4;padding:8px 14px;margin-bottom:10px;background:#fafbff;border-radius:0 6px 6px 0}
      .med-nom{font-size:14px;font-weight:700;color:#1a3a5c}.med-sub{font-size:11px;color:#555;margin-top:2px;display:flex;gap:16px}
      .firma{margin-top:50px;display:flex;justify-content:flex-end}
      .firma-box{text-align:center;width:220px}.firma-line{border-top:1px solid #1a3a5c;margin-bottom:6px}
      .firma-nom{font-size:12px;font-weight:600;color:#1a3a5c}.firma-sub{font-size:10px;color:#666}
    </style></head><body>
    <div class="hdr"><div><h2>RECETA MÉDICA</h2><p>${new Date(receta.FECHA).toLocaleDateString('es-EC',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div></div>
    <div class="pac-info">
      <div><div class="lbl">Paciente</div><div class="val">${[pac?.APELLIDO_PATERNO,pac?.APELLIDO_MATERNO,pac?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div></div>
      <div><div class="lbl">Cédula</div><div class="val">${pac?.IDENTIFICACION||'—'}</div></div>
      <div><div class="lbl">Edad</div><div class="val">${pac?.FECHA_NACIMIENTO?Math.floor((Date.now()-new Date(pac.FECHA_NACIMIENTO))/3.156e10)+' años':'—'}</div></div>
      <div><div class="lbl">Fecha</div><div class="val">${new Date(receta.FECHA).toLocaleDateString('es-EC')}</div></div>
    </div>
    <div class="rx-title">&#8478;</div>
    ${(receta.items||[]).map((it,i)=>`
      <div class="med-item"><div class="med-nom">${i+1}. ${it.MEDICAMENTO}</div>
      <div class="med-sub">${it.CANTIDAD?`<span>📦 ${it.CANTIDAD}</span>`:''}${it.INDICACION?`<span>📋 ${it.INDICACION}</span>`:''}</div></div>`).join('')}
    ${receta.INDICACIONES_GENERALES?`<div style="margin-top:14px;padding:10px 14px;background:#fffbea;border-radius:6px;font-size:12px"><strong>Indicaciones:</strong> ${receta.INDICACIONES_GENERALES}</div>`:''}
    <div class="firma"><div class="firma-box"><div class="firma-line"></div><div class="firma-nom">Dr. ${detalle?.MEDICO||''}</div><div class="firma-sub">Médico tratante</div></div></div>
    </body></html>`;
    const w=window.open('','_blank','width=750,height=950'); w.document.write(html); w.document.close(); w.focus(); setTimeout(()=>w.print(),600);
  };

  const regs=filtro
    ?registros.filter(r=>(r.EVOLUCION||'').toLowerCase().includes(filtro.toLowerCase())||(r.MEDICO||'').toLowerCase().includes(filtro.toLowerCase()))
    :registros;

  const sv=detalle?.signosVitales?.[0]||{};
  const svMap={
    PRESION_ARTERIAL:  sv.PRESION_ARTERIAL,
    PULSO:             sv.PULSO,
    RESPIRATORIA:      sv.RESPIRATORIA?sv.RESPIRATORIA+' rpm':null,
    SATURACION_OXIGENO:sv.SATURACION_OXIGENO||sv.SATURACION_O2,
    PESO:  sv.PESO  ?sv.PESO+' kg' :null,
    TALLA: sv.TALLA ?sv.TALLA+' cm':null,
    CINTURA_ABDOMINAL: sv.CINTURA_ABDOMINAL?sv.CINTURA_ABDOMINAL+' cm':null,
    TEMP_AXILAR: sv.TEMP_AXILAR?sv.TEMP_AXILAR+'°C':null,
  };
  const alertasSV=Object.entries(svMap).filter(([k,v])=>v&&SV_RANGOS[k]&&['alto','critico','bajo'].includes(SV_RANGOS[k].eval(v)));
  const imcActual = calcIMC(sv.PESO, sv.TALLA);
  const pesoIdealActual = calcPesoIdeal(sv.TALLA);
  const deficitActual = (imcActual!=null && pesoIdealActual!=null) ? (parseFloat(sv.PESO) - pesoIdealActual) : null;
  const imcAnterior = svAnteriorVista ? calcIMC(svAnteriorVista.PESO, svAnteriorVista.TALLA) : null;

  // ── Lectura rápida (sidebar): signos vitales de la consulta más reciente, medicación de la última receta,
  // historia reciente (diagnósticos) y exámenes recientes — independiente de cuál consulta se esté viendo ──
  const svqr = svUltimos || {};
  const svMapUltimos = {
    PRESION_ARTERIAL:   svqr.PRESION_ARTERIAL,
    PULSO:              svqr.PULSO,
    RESPIRATORIA:       svqr.RESPIRATORIA?svqr.RESPIRATORIA+' rpm':null,
    TEMP_AXILAR:        svqr.TEMP_AXILAR?svqr.TEMP_AXILAR+'°C':null,
    SATURACION_OXIGENO: svqr.SATURACION_OXIGENO||svqr.SATURACION_O2,
  };
  const medicacionActiva = recetasDet[0]?.items||[];
  const historiaReciente = registros.slice(0,4);
  const examenesRecientes = labDet.slice(0,4);
  const svCl = e => e==='ok'?'ok':e==='critico'?'cr':e==='alto'||e==='bajo'?'hi':'';

  /* ════════════════
     SIN PACIENTE
  ════════════════ */
  if(!codPac) return (
    <>
      <style>{css}</style>
      <div className="search-pg fade-up">
        <div className="search-card">
          <div style={{fontSize:16,fontWeight:700,marginBottom:5,color:'var(--text)'}}>Historia Clínica</div>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>Busca un paciente para acceder a su historial</div>
          <input className="form-control" placeholder="Nombre, apellido o cédula..." value={pacBusq} autoFocus onChange={e=>onBusqPac(e.target.value)}/>
          {pacSugs.length>0&&(
            <div className="sug-box">
              {pacSugs.map(p=>(
                <div key={p.COD} className="sug-it" onClick={()=>{setCodPac(p.COD);setPacBusq('');setPacSugs([]);}}>
                  <div style={{width:34,height:34,borderRadius:'50%',background:'var(--blue-lt)',color:'var(--navy)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0,overflow:'hidden'}}>
                    {p.FOTO_URL?<img src={fotoUrl(p.FOTO_URL)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:((p.PRIMER_NOMBRE?.[0]||'')+(p.APELLIDO_PATERNO?.[0]||''))}
                  </div>
                  <div>
                    <div style={{fontWeight:600}}>{[p.APELLIDO_PATERNO,p.APELLIDO_MATERNO,p.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
                    <div style={{fontSize:11,color:'var(--text3)'}}>{p.IDENTIFICACION}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  /* ════════════════
     CON PACIENTE
  ════════════════ */
  return (
    <>
      <style>{css}</style>
      {msg&&<div style={{marginBottom:8,padding:'8px 14px',borderRadius:8,fontSize:12,
        background:msg.startsWith('✓')?'var(--green-lt)':'var(--red-lt)',
        color:msg.startsWith('✓')?'var(--green)':'var(--red)',
        border:`1px solid ${msg.startsWith('✓')?'var(--green)':'var(--red)'}`}}>{msg}</div>}

      <div className="hc-page fade-up">

        {/* ══ HEADER ══ */}
        <div className="hc-hdr">
          <div className="hdr-avatar">
            {paciente?.FOTO_URL
              ?<img src={fotoUrl(paciente.FOTO_URL)} alt="" onError={e=>{e.target.style.display='none';}}/>
              :(paciente?.PRIMER_NOMBRE?.[0]||'')+(paciente?.APELLIDO_PATERNO?.[0]||'')}
          </div>
          <div className="hdr-info">
            <div className="hdr-name">{[paciente?.APELLIDO_PATERNO,paciente?.APELLIDO_MATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
            <div className="hdr-sub">CI: {paciente?.IDENTIFICACION||'—'} · {calcEdad(paciente?.FECHA_NACIMIENTO)} · {paciente?.C_SEXO==='M'?'M':paciente?.C_SEXO==='F'?'F':'—'} · Tipo sangre: {paciente?.TIPO_SANGRE||'—'}</div>
            <div className="hdr-meta">
              <span className="hdr-chip">📞 {paciente?.TELEFONO||'—'}</span>
              <span className="hdr-chip">🛡 {convenio}</span>
              {alergias.length>0
                ?alergias.slice(0,3).map(a=><span key={a.COD} className="hdr-chip alg">⚠️ {a.ALERGIA}</span>)
                :<span className="hdr-chip">Sin alergias</span>}
            </div>
          </div>
          <div className="hdr-actions">
            <button className="btn-nueva-hc" onClick={abrirNueva}>+ Nueva consulta</button>
            <div className="hdr-btns-row">
              <button className="hdr-btn-sec" onClick={()=>navigate('/facturacion',{state:{codPac,nombre:[paciente?.APELLIDO_PATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}})}>Facturar</button>
              <button className="hdr-btn-sec" onClick={()=>{setCodPac('');setPac(null);setRegs([]);setDet(null);setSvUltimos(null);}}>Cambiar paciente</button>
            </div>
          </div>
        </div>

        {/* databar mobile */}
        <div className="hdr-databar">
          {[['CI',paciente?.IDENTIFICACION||'—'],['Edad',calcEdad(paciente?.FECHA_NACIMIENTO)],['Sangre',paciente?.TIPO_SANGRE||'—'],['Tel',paciente?.TELEFONO||'—'],['Seguro',convenio]].map(([l,v])=>(
            <div key={l} className="hdr-di"><div className="hdr-di-l">{l}</div><div className="hdr-di-v">{v}</div></div>
          ))}
          {alergias.slice(0,2).map(a=>(
            <div key={a.COD} className="hdr-di"><div className="hdr-di-l" style={{color:'#fca5a5'}}>Alergia</div><span className="alg-chip-mb">{a.ALERGIA}</span></div>
          ))}
        </div>

        {/* actbar mobile */}
        <div className="hdr-actbar">
          <button className="hdr-act-btn pri" onClick={abrirNueva}>+ Consulta</button>
          <button className="hdr-act-btn" onClick={()=>navigate('/facturacion',{state:{codPac,nombre:[paciente?.APELLIDO_PATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}})}>Facturar</button>
          <button className="hdr-act-btn" onClick={()=>navigate(`/laboratorio?paciente=${codPac}`)}>Lab</button>
          <button className="hdr-act-btn" onClick={()=>{setCodPac('');setPac(null);setRegs([]);setDet(null);setSvUltimos(null);}}>Cambiar</button>
        </div>

        {/* ══ CUERPO ══ */}
        <div className="hc-body">

          {/* BARRA VER EVOLUCIONES */}
          <div className="evo-bar">
            <button className="evo-bar-btn" onClick={()=>setShowTimeline(true)}>
              <i className="ti ti-history" aria-hidden="true"></i> Ver evoluciones {regs.length>0?`(${regs.length})`:''}
            </button>
            {detalle&&<span className="evo-bar-cur">Viendo: {fmtF(detalle.FECHA)} · {detalle.MEDICO||'Sin médico'}</span>}
          </div>

          {showTimeline&&(
            <div className="tl-ov" onClick={e=>e.target.classList.contains('tl-ov')&&setShowTimeline(false)}>
              <div className="tl-panel">
                <div className="tl-panel-hdr">
                  <span>Evoluciones</span>
                  <button className="tl-panel-close" onClick={()=>setShowTimeline(false)}>
                    <i className="ti ti-x" aria-hidden="true"></i>
                  </button>
                </div>
                <div className="hc-left">
                  <div className="tl-srch">
                    <input placeholder="Buscar en historial..." value={filtro} onChange={e=>setFiltro(e.target.value)}/>
                  </div>
                  <div className="tl-cnt">{regs.length} registros</div>
                  <div className="tl-list">
                    {loading?<div className="spin-wrap"><div className="sp"/></div>
                      :regs.length===0?<div className="empty-hc">Sin registros</div>
                      :agrupar(regs).map(([yr,items])=>(
                        <div key={yr}>
                          <div className="tl-yr">{yr}</div>
                          {items.map(r=>{
                            const idx=registros.indexOf(r);
                            return(
                              <div key={r.COD} className={`tl-row ${idx===selIdx?'sel':''}`} onClick={()=>{selRow(idx,r.COD);setShowTimeline(false);}}>
                                <div className="tl-date">{fmtF(r.FECHA)}</div>
                                <div className="tl-doc">{r.MEDICO||'Sin médico'}</div>
                                <div className="tl-prev">{r.EVOLUCION?.slice(0,55)}{r.EVOLUCION?.length>55?'…':''}</div>
                                {r.diagnosticos?.length>0&&<div className="tl-tags">{r.diagnosticos.slice(0,3).map((d,j)=><span key={j} className="tl-tag">{d.COD_CIE10}</span>)}</div>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PANEL + SIDEBAR LECTURA RÁPIDA */}
          <div className="hc-main-row">
          <div className="hc-right">
            {(detalle||loadDet)?(
              <>
                <div className="hc-tabs-row">
                  <div className="hc-tabs">
                    {[['consulta','ti-notes','Consulta',null],['recetas','ti-pill','Recetas',recetasDet.length||null],
                      ['certificados','ti-file-certificate','Certificados',certificadosDet.length||null],
                      ['consentimientos','ti-signature','Consentimientos',consentimientosDet.length||null],
                      ['lab','ti-test-pipe','Lab',labDet.length||null],['antecedentes','ti-clipboard-list','Antecedentes',null],
                      ['alergias','ti-alert-triangle','Alergias',alergias.length||null],
                      ['auditoria','ti-history','Auditoría',null]].map(([k,ic,lbl,cnt])=>(
                      <div key={k} className={`hc-tab ${tab===k?'act':''}`} onClick={()=>setTab(k)}>
                        <i className={`ti ${ic}`} aria-hidden="true"></i>{lbl}
                        {cnt?<span className="badge-cnt">{cnt}</span>:null}
                      </div>
                    ))}
                  </div>
                  {/* Fuera de .hc-tabs a propósito: si las pestañas no caben y hacen
                      scroll, estas acciones (sobre todo Editar) deben seguir visibles
                      sin tener que desplazarse — antes vivían dentro de la franja que
                      hace scroll y quedaban fuera de vista. */}
                  {detalle&&<div className="hc-actions">
                    <button className="btn btn-sm" style={{fontSize:10}} onClick={()=>setShowPrint(true)}>
                      <i className="ti ti-printer" style={{fontSize:11}} aria-hidden="true"></i> Imprimir evolución
                    </button>
                    {detalle?.ANT_PER_FLAGS&&(
                      <button className="btn btn-sm" style={{fontSize:10,background:'var(--navy)',color:'#fff',border:'none'}}
                        disabled={print002Load} onClick={imprimir002}>
                        {print002Load?'...':'🏥 002 MSP'}
                      </button>
                    )}
                    <span>{fmtF(detalle.FECHA)}</span>
                    {editInfo&&detalle.COD===editInfo.COD&&(
                      <span style={{fontSize:10,padding:'2px 8px',borderRadius:20,fontWeight:600,
                        background:editInfo.dentroPlazo?'var(--green-lt)':editInfo.esAdmin?'var(--blue-lt)':'var(--red-lt)',
                        color:editInfo.dentroPlazo?'var(--green)':editInfo.esAdmin?'var(--navy)':'var(--red)'}}>
                        {editInfo.dentroPlazo?`${Math.floor(editInfo.horasRestantes)}h restantes`:editInfo.esAdmin?'Admin':'Requiere auth'}
                      </span>
                    )}
                    <button className="btn btn-sm" style={{fontSize:10}}
                      onClick={()=>{ setEditInfo(null); abrirEditar(detalle); }}>
                      <i className="ti ti-edit" style={{fontSize:11}} aria-hidden="true"></i> Editar
                    </button>
                  </div>}
                </div>

                {loadDet?<div className="spin-wrap"><div className="sp"/></div>:(
                  <div className="hc-scroll">

                    {tab==='consulta'&&(<>
                      {/* Signos vitales */}
                      <div className="sec-card">
                        <div className="sec-card-hdr">
                          <span className="sec-card-ttl"><i className="ti ti-heart-rate-monitor" aria-hidden="true"></i> Signos vitales</span>
                          {alertasSV.length>0&&<span className={alertasSV.some(([k,v])=>SV_RANGOS[k].eval(v)==='critico')?'alert-badge':'warn-badge'}>
                            <i className="ti ti-alert-triangle" aria-hidden="true"></i> {alertasSV.length} valor(es) alterado(s)
                          </span>}
                        </div>
                        <div className="sec-card-body">
                          <div className="sv-grid">
                            {Object.entries(svMap).map(([key,val])=>{
                              const r=SV_RANGOS[key]; const e=r?r.eval(val):'na'; const cl=svCl(e);
                              const valorAnterior = key!=='PRESION_ARTERIAL' ? svAnteriorVista?.[key] : null;
                              let flecha = null;
                              if (valorAnterior && val) {
                                const actualNum = parseFloat(val), anteriorNum = parseFloat(valorAnterior);
                                if (!isNaN(actualNum) && !isNaN(anteriorNum) && actualNum!==anteriorNum) {
                                  flecha = actualNum > anteriorNum ? 'ti-arrow-up' : 'ti-arrow-down';
                                }
                              }
                              return(
                                <div key={key} className={`sv-card ${e==='critico'?'crit':e==='alto'||e==='bajo'?'warn':''}`}>
                                  <i className={`ti ${r?.icon||'ti-circle'} sv-ico ${cl}`} aria-hidden="true"></i>
                                  <div className="sv-data">
                                    <span className={`sv-val ${cl}`}>{val||'—'}{val&&r?.unit&&<span className="sv-u">{r.unit}</span>}</span>
                                    <span className="sv-lbl">{r?.label||key}{e==='alto'?' ▲':e==='critico'?' !!':e==='bajo'?' ▼':''}</span>
                                    {valorAnterior && (
                                      <span className="sv-ant">
                                        {flecha && <i className={`ti ${flecha}`} aria-hidden="true"></i>}
                                        antes: {valorAnterior}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {imcActual!=null && (()=>{ const cl=svCl(imcEstado(imcActual)); return(
                              <div className={`sv-card ${imcEstado(imcActual)==='critico'?'crit':imcEstado(imcActual)==='alto'?'warn':''}`}>
                                <i className={`ti ti-report-analytics sv-ico ${cl}`} aria-hidden="true"></i>
                                <div className="sv-data">
                                  <span className={`sv-val ${cl}`}>{imcActual.toFixed(1)}</span>
                                  <span className="sv-lbl">IMC · {imcEtiqueta(imcActual)}</span>
                                  {imcAnterior!=null && Math.abs(imcAnterior-imcActual)>=0.1 && (
                                    <span className="sv-ant">
                                      <i className={`ti ${imcActual>imcAnterior?'ti-arrow-up':'ti-arrow-down'}`} aria-hidden="true"></i>
                                      antes: {imcAnterior.toFixed(1)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );})()}
                            {pesoIdealActual!=null && (
                              <div className="sv-card">
                                <i className="ti ti-target-arrow sv-ico" aria-hidden="true"></i>
                                <div className="sv-data">
                                  <span className="sv-val">{pesoIdealActual.toFixed(1)}<span className="sv-u">kg</span></span>
                                  <span className="sv-lbl">Peso ideal (calc.)</span>
                                </div>
                              </div>
                            )}
                            {deficitActual!=null && (
                              <div className="sv-card">
                                <i className={`ti ${deficitActual>=0?'ti-trending-up':'ti-trending-down'} sv-ico`} aria-hidden="true"></i>
                                <div className="sv-data">
                                  <span className="sv-val">{deficitActual>=0?'+':''}{deficitActual.toFixed(1)}<span className="sv-u">kg</span></span>
                                  <span className="sv-lbl">{deficitActual>=0?'Excedente':'Déficit'} de peso</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Evolución */}
                      <div className="sec-card">
                        <div className="sec-card-hdr">
                          <span className="sec-card-ttl"><i className="ti ti-file-text" aria-hidden="true"></i> Evolución</span>
                        </div>
                        <div className="sec-card-body">
                          {detalle?.EVOLUCION
                            ? formatearEvolucion(detalle.EVOLUCION)
                            : <div className="evol-text">Sin evolución registrada</div>}
                        </div>
                      </div>

                      {/* Diagnósticos */}
                      {detalle?.diagnosticos?.length>0&&(
                        <div className="sec-card">
                          <div className="sec-card-hdr">
                            <span className="sec-card-ttl"><i className="ti ti-stethoscope" aria-hidden="true"></i> Diagnósticos CIE-10</span>
                          </div>
                          <div className="sec-card-body">
                            <div className="dx-pills">
                              {detalle.diagnosticos.map((d,i)=>(
                                <div key={i} className="dx-pill">
                                  <span className="dx-cod">{d.COD_CIE10}</span>
                                  <span className="dx-desc">{d.CIE10_DESC||''}</span>
                                  <span className={d.C_TIPO_DIAGNOSTICO==='PRE'?'dx-pre':'dx-def'}>{d.C_TIPO_DIAGNOSTICO==='PRE'?'Presuntivo':'Definitivo'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>)}

                    {tab==='recetas'&&(
                      recetasDet.length===0
                        ?<div className="empty-hc"><div className="empty-icon">℞</div>Sin recetas registradas</div>
                        :recetasDet.map(rec=>(
                          <div key={rec.COD} className="rec-card">
                            <div className="rec-hdr">
                              <span style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>📋 {fmtD(rec.FECHA)}</span>
                              <button className="btn btn-sm" style={{fontSize:10}}
                                onClick={async()=>{
                                  try{
                                    const url=`${API_BASE.replace('/api','')}/api/historia/recetas/${rec.COD}/pdf`;
                                    const token=localStorage.getItem('token');
                                    const res=await fetch(url,{headers:{Authorization:`Bearer ${token}`}});
                                    if(!res.ok){
                                      // Antes esto se descargaba igual con extensión .pdf aunque el
                                      // servidor devolviera un error — el navegador no podía abrir
                                      // ese archivo. Ahora se muestra el error real en vez de "descargarlo".
                                      let msg='No se pudo generar el PDF de la receta';
                                      try{ const j=await res.json(); if(j?.error) msg=j.error; }catch(_){}
                                      alert(msg); return;
                                    }
                                    const blob=await res.blob();
                                    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`Receta_${rec.FECHA?.slice(0,10)||'doc'}.pdf`;a.click();
                                  }catch(e){alert('Error: '+e.message);}
                                }}>📄 PDF</button>
                            </div>
                            <div className="rec-body">
                              {(rec.items||[]).map((it,i)=>(
                                <div key={i} className="rec-item">
                                  <div className="rec-num">{i+1}</div>
                                  <div style={{flex:1}}>
                                    <div className="rec-med">{it.MEDICAMENTO}</div>
                                    <div className="rec-det">
                                      {it.CANTIDAD&&<span>📦 {it.CANTIDAD}</span>}
                                      {it.INDICACION&&<span>{it.INDICACION}</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {rec.INDICACIONES_GENERALES&&<div style={{marginTop:6,fontSize:11,color:'var(--text2)',padding:'6px 10px',background:'var(--amber-lt)',borderRadius:6}}><strong>Indicaciones:</strong> {rec.INDICACIONES_GENERALES}</div>}
                            </div>
                          </div>
                        ))
                    )}

                    {tab==='certificados'&&(<>
                      {['MEDICO','ENFERMERIA','ADMINISTRADOR'].includes(usuario?.tipo_usuario)&&(
                        <div style={{marginBottom:12}}>
                          {!nuevoCertif
                            ? <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none'}}
                                onClick={()=>{setNuevoCertif(true);setCertifTipo('REPOSO');setCertifDatos({});setCertifMsg('');}}>
                                <i className="ti ti-plus" aria-hidden="true"></i> Nuevo certificado
                              </button>
                            : <div className="rec-card">
                                <div style={{marginBottom:10}}>
                                  <label className="form-label" style={{fontSize:10}}>Tipo de certificado</label>
                                  <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6}}>
                                    {[['REPOSO','ti-bed','Reposo médico'],['ASISTENCIA','ti-stethoscope','Asistencia'],
                                      ['SALUD','ti-heart','Salud/Aptitud'],['GENERAL','ti-file-text','General'],
                                      ['DISCAPACIDAD','ti-wheelchair','Discapacidad']].map(([k,ic,lbl])=>(
                                      <div key={k} onClick={()=>{setCertifTipo(k);setCertifDatos({});}}
                                        style={{border:`1.5px solid ${certifTipo===k?'var(--navy)':'var(--border)'}`,
                                          background:certifTipo===k?'var(--blue-lt)':'transparent',borderRadius:8,
                                          padding:8,textAlign:'center',cursor:'pointer'}}>
                                        <i className={`ti ${ic}`} style={{fontSize:16,color:certifTipo===k?'var(--navy)':'var(--text3)'}} aria-hidden="true"></i>
                                        <div style={{fontSize:10,marginTop:3,color:certifTipo===k?'var(--navy)':'var(--text3)',fontWeight:certifTipo===k?600:400}}>{lbl}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {certifTipo==='REPOSO'&&(<>
                                  {form.diagnosticos?.length>0&&(
                                    <button className="btn btn-sm" style={{fontSize:10,marginBottom:8}}
                                      onClick={()=>setCertifDatos(d=>({...d,diagnostico_texto:form.diagnosticos[0].desc,cod_cie10:form.diagnosticos[0].COD_CIE10}))}>
                                      Usar diagnóstico de esta consulta
                                    </button>
                                  )}
                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Tipo de contingencia</label>
                                      <select className="form-control" style={{fontSize:12}}
                                        value={certifDatos.tipo_contingencia||'Enfermedad general'}
                                        onChange={e=>setCertifDatos(d=>({...d,tipo_contingencia:e.target.value}))}>
                                        <option>Enfermedad general</option>
                                        <option>Enfermedad catastrófica</option>
                                        <option>Maternidad</option>
                                      </select>
                                    </div>
                                    <div style={{position:'relative'}}>
                                      <label className="form-label" style={{fontSize:10}}>Código CIE-10</label>
                                      <input className="form-control" style={{fontSize:12}} placeholder="Ej: Z00 o busca por nombre..."
                                        value={certifDatos.cod_cie10||''}
                                        onChange={e=>{onCertifCie10(e.target.value); setCertifDatos(d=>({...d,cod_cie10:e.target.value}));}}/>
                                      {certifCie10S.length>0&&(
                                        <div className="cie-box">
                                          {certifCie10S.map(d=>(
                                            <div key={d.COD} className="cie-it" onMouseDown={()=>{
                                              setCertifDatos(dd=>({...dd,cod_cie10:d.COD_CIE10||d.COD,diagnostico_texto:d.DIAGNOSTICO}));
                                              setCertifCie10S([]);
                                            }}>
                                              <strong style={{color:'var(--navy)'}}>{d.COD_CIE10||d.COD}</strong> — {d.DIAGNOSTICO}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Diagnóstico</label>
                                    <input className="form-control" style={{fontSize:12}} value={certifDatos.diagnostico_texto||''}
                                      onChange={e=>setCertifDatos(d=>({...d,diagnostico_texto:e.target.value}))}/>
                                  </div>
                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Días de reposo</label>
                                      <input type="number" min="1" max="30" className="form-control" style={{fontSize:12}}
                                        value={certifDatos.dias_reposo||''} onChange={e=>setCertifDatos(d=>({...d,dias_reposo:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Desde</label>
                                      <input type="date" className="form-control" style={{fontSize:12}} value={certifDatos.fecha_desde||''}
                                        onChange={e=>setCertifDatos(d=>({...d,fecha_desde:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Hasta</label>
                                      <input type="date" className="form-control" style={{fontSize:12}} value={certifDatos.fecha_hasta||''}
                                        onChange={e=>setCertifDatos(d=>({...d,fecha_hasta:e.target.value}))}/>
                                    </div>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Puesto de trabajo del paciente</label>
                                    <input className="form-control" style={{fontSize:12}} value={certifDatos.puesto_trabajo||''}
                                      onChange={e=>setCertifDatos(d=>({...d,puesto_trabajo:e.target.value}))}/>
                                  </div>
                                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,marginBottom:10}}>
                                    <input type="checkbox" checked={!!certifDatos.presenta_sintomas}
                                      onChange={e=>setCertifDatos(d=>({...d,presenta_sintomas:e.target.checked}))}/>
                                    Presenta síntomas
                                  </label>
                                </>)}

                                {certifTipo==='ASISTENCIA'&&(<>
                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Hora de inicio</label>
                                      <input type="time" className="form-control" style={{fontSize:12}} value={certifDatos.hora_inicio||''}
                                        onChange={e=>setCertifDatos(d=>({...d,hora_inicio:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Hora de fin</label>
                                      <input type="time" className="form-control" style={{fontSize:12}} value={certifDatos.hora_fin||''}
                                        onChange={e=>setCertifDatos(d=>({...d,hora_fin:e.target.value}))}/>
                                    </div>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Motivo de la atención</label>
                                    <textarea className="form-control" rows={2} style={{fontSize:12}} value={certifDatos.motivo||''}
                                      onChange={e=>setCertifDatos(d=>({...d,motivo:e.target.value}))}/>
                                  </div>
                                </>)}

                                {certifTipo==='SALUD'&&(<>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Propósito (ej: aptitud laboral, deporte, conducir)</label>
                                    <input className="form-control" style={{fontSize:12}} value={certifDatos.proposito||''}
                                      onChange={e=>setCertifDatos(d=>({...d,proposito:e.target.value}))}/>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Hallazgos / Observaciones</label>
                                    <textarea className="form-control" rows={2} style={{fontSize:12}} value={certifDatos.observaciones||''}
                                      onChange={e=>setCertifDatos(d=>({...d,observaciones:e.target.value}))}/>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Resultado</label>
                                    <select className="form-control" style={{fontSize:12}} value={certifDatos.apto||'Apto sin restricciones'}
                                      onChange={e=>setCertifDatos(d=>({...d,apto:e.target.value}))}>
                                      <option>Apto sin restricciones</option>
                                      <option>Apto con restricciones</option>
                                      <option>No apto</option>
                                    </select>
                                  </div>
                                </>)}

                                {certifTipo==='GENERAL'&&(<>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Título</label>
                                    <input className="form-control" style={{fontSize:12}} placeholder="Ej: A QUIEN CORRESPONDA"
                                      value={certifDatos.titulo||''} onChange={e=>setCertifDatos(d=>({...d,titulo:e.target.value}))}/>
                                  </div>
                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Texto del certificado</label>
                                    <textarea className="form-control" rows={4} style={{fontSize:12}} value={certifDatos.cuerpo||''}
                                      onChange={e=>setCertifDatos(d=>({...d,cuerpo:e.target.value}))}/>
                                  </div>
                                </>)}

                                {certifTipo==='DISCAPACIDAD'&&(<>
                                  <div style={{marginBottom:10}}>
                                    <label className="form-label" style={{fontSize:10}}>Diagnósticos (Patología / CIE-10)</label>
                                    {(certifDatos.diagnosticos||[{patologia:'',cie10:''}]).map((dgn,i)=>(
                                      <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 100px 28px',gap:6,marginBottom:6}}>
                                        <input className="form-control" style={{fontSize:12}} placeholder="Patología" value={dgn.patologia||''}
                                          onChange={e=>setCertifDatos(d=>{const arr=[...(d.diagnosticos||[{patologia:'',cie10:''}])];arr[i]={...arr[i],patologia:e.target.value};return{...d,diagnosticos:arr};})}/>
                                        <input className="form-control" style={{fontSize:12}} placeholder="CIE-10" value={dgn.cie10||''}
                                          onChange={e=>setCertifDatos(d=>{const arr=[...(d.diagnosticos||[{patologia:'',cie10:''}])];arr[i]={...arr[i],cie10:e.target.value};return{...d,diagnosticos:arr};})}/>
                                        <button className="btn btn-sm" style={{padding:'4px 6px'}}
                                          onClick={()=>setCertifDatos(d=>({...d,diagnosticos:(d.diagnosticos||[{patologia:'',cie10:''}]).filter((_,j)=>j!==i)}))}>✕</button>
                                      </div>
                                    ))}
                                    <button className="btn btn-sm" style={{fontSize:10}}
                                      onClick={()=>setCertifDatos(d=>({...d,diagnosticos:[...(d.diagnosticos||[{patologia:'',cie10:''}]),{patologia:'',cie10:''}]}))}>
                                      + Agregar diagnóstico
                                    </button>
                                  </div>

                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Cuadro clínico (antecedentes, examen físico, si amerita o no tratamiento quirúrgico)</label>
                                    <textarea className="form-control" rows={4} style={{fontSize:12}} value={certifDatos.cuadro_clinico||''}
                                      onChange={e=>setCertifDatos(d=>({...d,cuadro_clinico:e.target.value}))}/>
                                  </div>

                                  <div style={{marginBottom:10}}>
                                    <label className="form-label" style={{fontSize:10}}>Tratamientos recibidos</label>
                                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:6}}>
                                      <div>
                                        <label className="form-label" style={{fontSize:9}}>Fecha de inicio</label>
                                        <input type="date" className="form-control" style={{fontSize:12}} value={certifDatos.tratamiento?.fecha_inicio||''}
                                          onChange={e=>setCertifDatos(d=>({...d,tratamiento:{...(d.tratamiento||{}),fecha_inicio:e.target.value}}))}/>
                                      </div>
                                      <div>
                                        <label className="form-label" style={{fontSize:9}}>Quirúrgico (tipo y fecha)</label>
                                        <input className="form-control" style={{fontSize:12}} value={certifDatos.tratamiento?.quirurgico||''}
                                          onChange={e=>setCertifDatos(d=>({...d,tratamiento:{...(d.tratamiento||{}),quirurgico:e.target.value}}))}/>
                                      </div>
                                    </div>
                                    <div style={{marginBottom:6}}>
                                      <label className="form-label" style={{fontSize:9}}>Farmacológico / No farmacológico (fármacos y dosis)</label>
                                      <input className="form-control" style={{fontSize:12}} value={certifDatos.tratamiento?.farmacologico||''}
                                        onChange={e=>setCertifDatos(d=>({...d,tratamiento:{...(d.tratamiento||{}),farmacologico:e.target.value}}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:9}}>Rehabilitación (terapia física, lenguaje, educación especial)</label>
                                      <input className="form-control" style={{fontSize:12}} value={certifDatos.tratamiento?.rehabilitacion||''}
                                        onChange={e=>setCertifDatos(d=>({...d,tratamiento:{...(d.tratamiento||{}),rehabilitacion:e.target.value}}))}/>
                                    </div>
                                  </div>

                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Secuelas permanentes e irreversibles</label>
                                    {(certifDatos.secuelas||[{patologia:'',cie10:'',descripcion:''}]).map((sec,i)=>(
                                      <div key={i} style={{border:'1px solid var(--border)',borderRadius:6,padding:8,marginBottom:6}}>
                                        <div style={{display:'grid',gridTemplateColumns:'1fr 100px 28px',gap:6,marginBottom:6}}>
                                          <input className="form-control" style={{fontSize:12}} placeholder="Patología" value={sec.patologia||''}
                                            onChange={e=>setCertifDatos(d=>{const arr=[...(d.secuelas||[{patologia:'',cie10:'',descripcion:''}])];arr[i]={...arr[i],patologia:e.target.value};return{...d,secuelas:arr};})}/>
                                          <input className="form-control" style={{fontSize:12}} placeholder="CIE-10" value={sec.cie10||''}
                                            onChange={e=>setCertifDatos(d=>{const arr=[...(d.secuelas||[{patologia:'',cie10:'',descripcion:''}])];arr[i]={...arr[i],cie10:e.target.value};return{...d,secuelas:arr};})}/>
                                          <button className="btn btn-sm" style={{padding:'4px 6px'}}
                                            onClick={()=>setCertifDatos(d=>({...d,secuelas:(d.secuelas||[{patologia:'',cie10:'',descripcion:''}]).filter((_,j)=>j!==i)}))}>✕</button>
                                        </div>
                                        <textarea className="form-control" rows={2} style={{fontSize:12}}
                                          placeholder="Descripción de las secuelas, exámenes complementarios de respaldo"
                                          value={sec.descripcion||''}
                                          onChange={e=>setCertifDatos(d=>{const arr=[...(d.secuelas||[{patologia:'',cie10:'',descripcion:''}])];arr[i]={...arr[i],descripcion:e.target.value};return{...d,secuelas:arr};})}/>
                                      </div>
                                    ))}
                                    <button className="btn btn-sm" style={{fontSize:10}}
                                      onClick={()=>setCertifDatos(d=>({...d,secuelas:[...(d.secuelas||[{patologia:'',cie10:'',descripcion:''}]),{patologia:'',cie10:'',descripcion:''}]}))}>
                                      + Agregar secuela
                                    </button>
                                  </div>
                                </>)}

                                {certifMsg&&<div style={{fontSize:11,color:'var(--red)',marginBottom:8}}>{certifMsg}</div>}
                                <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                  <button className="btn btn-sm" onClick={()=>setNuevoCertif(false)}>Cancelar</button>
                                  <button className="btn btn-primary btn-sm" disabled={certifSaving}
                                    onClick={async()=>{
                                      setCertifSaving(true); setCertifMsg('');
                                      try{
                                        await api.post('/historia/certificados',{
                                          COD_PACIENTE:codPac, COD_HISTORIAL_CLINICO:null,
                                          TIPO:certifTipo, datos:certifDatos
                                        });
                                        setNuevoCertif(false);
                                        cargarCertificadosDet(codPac);
                                      }catch(e){ setCertifMsg(e.response?.data?.error||e.message); }
                                      finally{ setCertifSaving(false); }
                                    }}>
                                    {certifSaving?'Generando...':'Generar certificado'}
                                  </button>
                                </div>
                              </div>
                          }
                        </div>
                      )}

                      {certificadosDet.length===0&&!nuevoCertif
                        ?<div className="empty-hc"><div className="empty-icon">📄</div>Sin certificados emitidos</div>
                        :certificadosDet.map(c=>(
                          <div key={c.COD} className="rec-card">
                            <div className="rec-hdr">
                              <span style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>
                                {{REPOSO:'🛏️ Reposo médico',ASISTENCIA:'🩺 Asistencia médica',SALUD:'❤️ Salud/Aptitud',GENERAL:'📄 Certificado general',DISCAPACIDAD:'♿ Certificado de discapacidad'}[c.TIPO]||c.TIPO}
                                {' · '}{fmtD(c.FECHA)}
                              </span>
                              <button className="btn btn-sm" style={{fontSize:10}}
                                onClick={async()=>{
                                  try{
                                    const url=`${API_BASE.replace('/api','')}/api/historia/certificados/${c.COD}/pdf`;
                                    const token=localStorage.getItem('token');
                                    const res=await fetch(url,{headers:{Authorization:`Bearer ${token}`}});
                                    if(!res.ok){
                                      let msg='No se pudo generar el PDF del certificado';
                                      try{ const j=await res.json(); if(j?.error) msg=j.error; }catch(_){}
                                      alert(msg); return;
                                    }
                                    const blob=await res.blob();
                                    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`Certificado_${c.TIPO}_${c.FECHA?.slice(0,10)||'doc'}.pdf`;a.click();
                                  }catch(e){alert('Error: '+e.message);}
                                }}>📄 PDF</button>
                            </div>
                            <div className="rec-body" style={{fontSize:11,color:'var(--text2)'}}>
                              {c.TIPO==='REPOSO'&&`${c.datos?.dias_reposo||''} día(s) · ${c.datos?.diagnostico_texto||''}`}
                              {c.TIPO==='ASISTENCIA'&&(c.datos?.motivo||'Atención registrada')}
                              {c.TIPO==='SALUD'&&`${c.datos?.proposito||''} — ${c.datos?.apto||''}`}
                              {c.TIPO==='GENERAL'&&(c.datos?.titulo||'Certificado')}
                              {c.TIPO==='DISCAPACIDAD'&&(c.datos?.diagnosticos?.[0]?.patologia||'Certificado de discapacidad')}
                              <div style={{marginTop:3,color:'var(--text3)'}}>Emitido por: {c.MEDICO_NOM||c.USUARIO_REGISTRO}</div>
                            </div>
                          </div>
                        ))
                      }
                    </>)}

                    {tab==='consentimientos'&&(<>
                      {['MEDICO','ENFERMERIA','ADMINISTRADOR'].includes(usuario?.tipo_usuario)&&(
                        <div style={{marginBottom:12}}>
                          {!nuevoConsent
                            ? <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none'}}
                                onClick={()=>{setNuevoConsent(true);setTipoConsent('plantilla');setConsentPlantillaSel('');setConsentProcEsp('');setConsentMsg('');
                                  setForm024Datos({TIPO_ATENCION:'AMBULATORIO',TIPO_DECISION:'ACEPTA'});setForm024Cie10Q('');setForm024Cie10S([]);setForm024Msg('');setForm024Imagen(null);}}>
                                <i className="ti ti-plus" aria-hidden="true"></i> Nuevo consentimiento
                              </button>
                            : <div className="rec-card">
                                <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',marginBottom:12}}>
                                  <div onClick={()=>setTipoConsent('plantilla')}
                                    style={{padding:'6px 12px',fontSize:12,cursor:'pointer',
                                      borderBottom:tipoConsent==='plantilla'?'2px solid var(--navy)':'none',
                                      fontWeight:tipoConsent==='plantilla'?600:400,color:tipoConsent==='plantilla'?'var(--navy)':'var(--text3)'}}>
                                    Plantilla personalizada
                                  </div>
                                  <div onClick={()=>setTipoConsent('form024')}
                                    style={{padding:'6px 12px',fontSize:12,cursor:'pointer',
                                      borderBottom:tipoConsent==='form024'?'2px solid var(--navy)':'none',
                                      fontWeight:tipoConsent==='form024'?600:400,color:tipoConsent==='form024'?'var(--navy)':'var(--text3)'}}>
                                    Formulario MSP 024
                                  </div>
                                </div>

                                {tipoConsent==='plantilla'&&(
                                  plantillasConsentList.length===0?(
                                    <div style={{fontSize:12,color:'var(--text3)'}}>
                                      No hay plantillas creadas todavía. Pídele al administrador que cree una en Configuración → Consentimientos.
                                    </div>
                                  ):(<>
                                    <div style={{marginBottom:10}}>
                                      <label className="form-label" style={{fontSize:10}}>Tipo de consentimiento</label>
                                      <select className="form-control" style={{fontSize:12}} value={consentPlantillaSel}
                                        onChange={e=>setConsentPlantillaSel(e.target.value)}>
                                        <option value="">Selecciona una plantilla...</option>
                                        {plantillasConsentList.map(p=><option key={p.COD} value={p.COD}>{p.TITULO}</option>)}
                                      </select>
                                    </div>
                                    <div style={{marginBottom:10}}>
                                      <label className="form-label" style={{fontSize:10}}>Procedimiento específico (opcional)</label>
                                      <input className="form-control" style={{fontSize:12}} placeholder="Ej: Extracción de lipoma en brazo izquierdo"
                                        value={consentProcEsp} onChange={e=>setConsentProcEsp(e.target.value)}/>
                                    </div>
                                    {consentMsg&&<div style={{fontSize:11,color:'var(--red)',marginBottom:8}}>{consentMsg}</div>}
                                    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                      <button className="btn btn-sm" onClick={()=>setNuevoConsent(false)}>Cancelar</button>
                                      <button className="btn btn-primary btn-sm" disabled={consentSaving||!consentPlantillaSel}
                                        onClick={async()=>{
                                          setConsentSaving(true); setConsentMsg('');
                                          try{
                                            await api.post('/historia/consentimientos',{
                                              COD_PACIENTE:codPac, COD_PLANTILLA:consentPlantillaSel,
                                              PROCEDIMIENTO_ESPECIFICO:consentProcEsp||null
                                            });
                                            setNuevoConsent(false);
                                            cargarConsentimientosDet(codPac);
                                          }catch(e){ setConsentMsg(e.response?.data?.error||e.message); }
                                          finally{ setConsentSaving(false); }
                                        }}>
                                        {consentSaving?'Generando...':'Generar e imprimir'}
                                      </button>
                                    </div>
                                  </>)
                                )}

                                {tipoConsent==='form024'&&(<>
                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Servicio</label>
                                      <input className="form-control" style={{fontSize:12}} value={form024Datos.SERVICIO||''}
                                        onChange={e=>setForm024Datos(f=>({...f,SERVICIO:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Tipo de atención</label>
                                      <select className="form-control" style={{fontSize:12}} value={form024Datos.TIPO_ATENCION}
                                        onChange={e=>setForm024Datos(f=>({...f,TIPO_ATENCION:e.target.value}))}>
                                        <option value="AMBULATORIO">Ambulatorio</option>
                                        <option value="HOSPITALIZACION">Hospitalización</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div style={{marginBottom:8,position:'relative'}}>
                                    <label className="form-label" style={{fontSize:10}}>Diagnóstico (CIE-10)</label>
                                    <input className="form-control" style={{fontSize:12}} placeholder="Buscar por nombre o código..."
                                      value={form024Cie10Q || form024Datos.DIAGNOSTICO || ''}
                                      onChange={e=>{onForm024Cie10(e.target.value); setForm024Datos(f=>({...f,DIAGNOSTICO:e.target.value,COD_CIE10:''}));}}/>
                                    {form024Cie10S.length>0&&(
                                      <div className="cie-box">
                                        {form024Cie10S.map(d=>(
                                          <div key={d.COD} className="cie-it" onMouseDown={()=>{
                                            setForm024Datos(f=>({...f,DIAGNOSTICO:d.DIAGNOSTICO,COD_CIE10:d.COD_CIE10||d.COD}));
                                            setForm024Cie10Q(d.DIAGNOSTICO); setForm024Cie10S([]);
                                          }}>
                                            <strong style={{color:'var(--navy)'}}>{d.COD_CIE10||d.COD}</strong> — {d.DIAGNOSTICO}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  <div style={{marginBottom:8}}>
                                    <label className="form-label" style={{fontSize:10}}>Nombre del procedimiento recomendado</label>
                                    <input className="form-control" style={{fontSize:12}} value={form024Datos.NOMBRE_PROCEDIMIENTO||''}
                                      onChange={e=>setForm024Datos(f=>({...f,NOMBRE_PROCEDIMIENTO:e.target.value}))}/>
                                  </div>

                                  <div style={{height:1,background:'var(--border)',margin:'10px 0 12px'}}/>
                                  <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:8}}>DATOS DEL PROCEDIMIENTO</div>

                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>En qué consiste</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.EN_QUE_CONSISTE||''} onChange={e=>setForm024Datos(f=>({...f,EN_QUE_CONSISTE:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Cómo se realiza</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.COMO_SE_REALIZA||''} onChange={e=>setForm024Datos(f=>({...f,COMO_SE_REALIZA:e.target.value}))}/>
                                    </div>
                                  </div>

                                  <div style={{marginBottom:10}}>
                                    <label className="form-label" style={{fontSize:10}}>Gráfico de la intervención (opcional, para facilitar la comprensión al paciente)</label>
                                    <input type="file" accept="image/*" className="form-control" style={{fontSize:12}}
                                      onChange={e=>setForm024Imagen(e.target.files[0]||null)}/>
                                    {form024Imagen&&<div style={{fontSize:10,color:'var(--green)',marginTop:3}}>📎 {form024Imagen.name}</div>}
                                  </div>

                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Beneficios del procedimiento</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.BENEFICIOS||''} onChange={e=>setForm024Datos(f=>({...f,BENEFICIOS:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Duración estimada</label>
                                      <input className="form-control" style={{fontSize:12}} placeholder="Ej: 30 a 45 minutos"
                                        value={form024Datos.DURACION_ESTIMADA||''} onChange={e=>setForm024Datos(f=>({...f,DURACION_ESTIMADA:e.target.value}))}/>
                                    </div>
                                  </div>

                                  <div style={{height:1,background:'var(--border)',margin:'10px 0 12px'}}/>
                                  <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:8}}>RIESGOS</div>

                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Riesgos frecuentes (poco graves)</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.RIESGOS_FRECUENTES||''} onChange={e=>setForm024Datos(f=>({...f,RIESGOS_FRECUENTES:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Riesgos poco frecuentes (graves)</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.RIESGOS_POCO_FRECUENTES||''} onChange={e=>setForm024Datos(f=>({...f,RIESGOS_POCO_FRECUENTES:e.target.value}))}/>
                                    </div>
                                  </div>

                                  <div style={{marginBottom:10}}>
                                    <label className="form-label" style={{fontSize:10}}>Riesgos específicos del paciente</label>
                                    <input className="form-control" style={{fontSize:12}} placeholder="Edad, estado de salud, creencias, valores, etc."
                                      value={form024Datos.RIESGOS_ESPECIFICOS||''} onChange={e=>setForm024Datos(f=>({...f,RIESGOS_ESPECIFICOS:e.target.value}))}/>
                                  </div>

                                  <div style={{height:1,background:'var(--border)',margin:'10px 0 12px'}}/>
                                  <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:8}}>DESPUÉS DEL PROCEDIMIENTO</div>

                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Alternativas al procedimiento</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.ALTERNATIVAS||''} onChange={e=>setForm024Datos(f=>({...f,ALTERNATIVAS:e.target.value}))}/>
                                    </div>
                                    <div>
                                      <label className="form-label" style={{fontSize:10}}>Manejo posterior</label>
                                      <textarea className="form-control" rows={2} style={{fontSize:12}}
                                        value={form024Datos.MANEJO_POSTERIOR||''} onChange={e=>setForm024Datos(f=>({...f,MANEJO_POSTERIOR:e.target.value}))}/>
                                    </div>
                                  </div>

                                  <div style={{marginBottom:10}}>
                                    <label className="form-label" style={{fontSize:10}}>Consecuencias si no se realiza</label>
                                    <textarea className="form-control" rows={2} style={{fontSize:12}}
                                      value={form024Datos.CONSECUENCIAS_NO_REALIZAR||''} onChange={e=>setForm024Datos(f=>({...f,CONSECUENCIAS_NO_REALIZAR:e.target.value}))}/>
                                  </div>

                                  {form024Msg&&<div style={{fontSize:11,color:'var(--red)',marginBottom:8}}>{form024Msg}</div>}
                                  <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                    <button className="btn btn-sm" onClick={()=>setNuevoConsent(false)}>Cancelar</button>
                                    <button className="btn btn-primary btn-sm" disabled={form024Saving||!form024Datos.NOMBRE_PROCEDIMIENTO?.trim()}
                                      onClick={async()=>{
                                        setForm024Saving(true); setForm024Msg('');
                                        try{
                                          const {data} = await api.post('/historia/form024', {COD_PACIENTE:codPac, ...form024Datos});
                                          if(form024Imagen){
                                            const fd=new FormData(); fd.append('grafico',form024Imagen);
                                            await api.post(`/historia/form024/${data.COD}/grafico`,fd,{headers:{'Content-Type':'multipart/form-data'}});
                                          }
                                          setNuevoConsent(false); setForm024Imagen(null);
                                          cargarForm024Det(codPac);
                                        }catch(e){ setForm024Msg(e.response?.data?.error||e.message); }
                                        finally{ setForm024Saving(false); }
                                      }}>
                                      {form024Saving?'Generando...':'Generar e imprimir'}
                                    </button>
                                  </div>
                                </>)}
                              </div>
                          }
                        </div>
                      )}

                      {consentimientosDet.length===0&&form024Det.length===0&&!nuevoConsent
                        ?<div className="empty-hc"><div className="empty-icon">✍️</div>Sin consentimientos registrados</div>
                        :(<>
                          {consentimientosDet.map(c=>(
                            <div key={'pl'+c.COD} className="rec-card">
                              <div className="rec-hdr">
                                <span style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>✍️ {c.TITULO} · {fmtD(c.FECHA)}</span>
                                <button className="btn btn-sm" style={{fontSize:10}}
                                  onClick={async()=>{
                                    try{
                                      const url=`${API_BASE.replace('/api','')}/api/historia/consentimientos/${c.COD}/pdf`;
                                      const token=localStorage.getItem('token');
                                      const res=await fetch(url,{headers:{Authorization:`Bearer ${token}`}});
                                      if(!res.ok){
                                        let msg='No se pudo generar el PDF del consentimiento';
                                        try{ const j=await res.json(); if(j?.error) msg=j.error; }catch(_){}
                                        alert(msg); return;
                                      }
                                      const blob=await res.blob();
                                      const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`Consentimiento_${c.FECHA?.slice(0,10)||'doc'}.pdf`;a.click();
                                    }catch(e){alert('Error: '+e.message);}
                                  }}>📄 PDF</button>
                              </div>
                              <div className="rec-body" style={{fontSize:11,color:'var(--text2)'}}>
                                {c.PROCEDIMIENTO_ESPECIFICO||'Sin procedimiento específico'}
                                <div style={{marginTop:3,color:'var(--text3)'}}>Generado por: {c.MEDICO_NOM||c.USUARIO_REGISTRO}</div>
                              </div>
                            </div>
                          ))}
                          {form024Det.map(c=>(
                            <div key={'f024'+c.COD} className="rec-card">
                              <div className="rec-hdr">
                                <span style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>📋 Form. 024 — {c.NOMBRE_PROCEDIMIENTO} · {fmtD(c.FECHA)}</span>
                                <button className="btn btn-sm" style={{fontSize:10}}
                                  onClick={async()=>{
                                    try{
                                      const url=`${API_BASE.replace('/api','')}/api/historia/form024/${c.COD}/pdf`;
                                      const token=localStorage.getItem('token');
                                      const res=await fetch(url,{headers:{Authorization:`Bearer ${token}`}});
                                      if(!res.ok){
                                        let msg='No se pudo generar el PDF del formulario 024';
                                        try{ const j=await res.json(); if(j?.error) msg=j.error; }catch(_){}
                                        alert(msg); return;
                                      }
                                      const blob=await res.blob();
                                      const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`Form024_${c.FECHA?.slice(0,10)||'doc'}.pdf`;a.click();
                                    }catch(e){alert('Error: '+e.message);}
                                  }}>📄 PDF</button>
                              </div>
                              <div className="rec-body" style={{fontSize:11,color:'var(--text2)'}}>
                                {{ACEPTA:'✅ Aceptado',NIEGA:'❌ Negado',REVOCA:'↩️ Revocado'}[c.TIPO_DECISION]||c.TIPO_DECISION}
                                <div style={{marginTop:3,color:'var(--text3)'}}>Generado por: {c.MEDICO_NOM||c.USUARIO_REGISTRO}</div>
                              </div>
                            </div>
                          ))}
                        </>)
                      }
                    </>)}

                    {tab==='lab'&&(<>
                      {labDet.length===0
                        ?<div className="empty-hc"><div className="empty-icon">🧪</div>Sin solicitudes de laboratorio</div>
                        :labDet.map(sol=>(
                          <div key={sol.COD} className="lab-card">
                            <div className="lab-hdr">
                              <span style={{fontSize:12,fontWeight:600,color:'var(--text)',flex:1}}>{sol.TIPO_DETERMINACION}</span>
                              <span className={`badge ${sol.C_ESTADO==='CNF'?'badge-green':sol.C_ESTADO==='ANL'?'badge-red':'badge-blue'}`}>
                                {sol.C_ESTADO==='CNF'?'Confirmado':sol.C_ESTADO==='ANL'?'Anulado':'Solicitado'}
                              </span>
                              <span style={{fontSize:10,color:'var(--text3)'}}>{fmtD(sol.FECHA_SOLICITUD)}</span>
                                {sol.C_ESTADO==='CNF'&&(
                                  <button className="btn btn-sm" style={{fontSize:10,background:'var(--blue-lt)',color:'var(--navy)',border:'1px solid var(--navy-lt)'}}
                                    onClick={async()=>{
                                      try{
                                        const token=localStorage.getItem('token');
                                        const res=await fetch(`${process.env.REACT_APP_API_URL||'http://localhost:3001/api'}/laboratorio/solicitudes/${sol.COD}/informe-pdf`,{method:'POST',headers:{Authorization:`Bearer ${token}`}});
                                        if(!res.ok){alert('Error al generar informe');return;}
                                        const blob=await res.blob(); const a=document.createElement('a'); a.href=URL.createObjectURL(blob); a.download=`Informe_Lab_${sol.COD}.pdf`; a.click();
                                      }catch(e){alert('Error: '+e.message);}
                                    }}>📋 Informe</button>
                                )}
                                <button className="btn btn-sm" style={{fontSize:10}}
                                  onClick={async()=>{
                                    try{
                                      const token=localStorage.getItem('token');
                                      const res=await fetch(`${process.env.REACT_APP_API_URL||'http://localhost:3001/api'}/laboratorio/solicitudes/${sol.COD}/pdf`,{method:'POST',headers:{Authorization:`Bearer ${token}`}});
                                      if(!res.ok){
                                        let msg='No se pudo generar el PDF del pedido';
                                        try{ const j=await res.json(); if(j?.error) msg=j.error; }catch(_){}
                                        alert(msg); return;
                                      }
                                      const blob=await res.blob(); const a=document.createElement('a'); a.href=URL.createObjectURL(blob); a.download=`Pedido_Lab_${sol.COD}.pdf`; a.click();
                                    }catch(e){alert('Error: '+e.message);}
                                  }}>🖨️ PDF</button>
                            </div>
                            {/* Tags de determinaciones */}
                            <div className="lab-body">
                              {(sol.detalles||[]).map(d=>(
                                <span key={d.COD} className="lab-tag">
                                  {d.DETERMINACION}{d.RESULTADO&&<span style={{color:'var(--green)',marginLeft:3}}>✓</span>}
                                  {d.ARCHIVO_RESULTADO&&(
                                    <a href={`${API_BASE}/uploads/laboratorio/${d.ARCHIVO_RESULTADO}`} target="_blank" rel="noreferrer"
                                      style={{marginLeft:5,color:'var(--navy-lt)',fontWeight:500}}>📄 Ver archivo</a>
                                  )}
                                </span>
                              ))}
                            </div>
                            {/* Botón ver resultados si está confirmado */}
                            {sol.C_ESTADO==='CNF'&&(
                              <div style={{padding:'0 12px 8px'}}>
                                <button className="btn btn-sm" style={{fontSize:10,width:'100%',background:'var(--blue-lt)',color:'var(--navy)',border:'1px solid var(--navy-lt)'}}
                                  onClick={()=>verResultados(sol.COD)}>
                                  {labExpanded===sol.COD?'▲ Ocultar resultados':'🔬 Ver resultados con rangos'}
                                </button>
                              </div>
                            )}
                            {/* Tabla de resultados expandida */}
                            {labExpanded===sol.COD&&(
                              <div style={{borderTop:'1px solid var(--border)'}}>
                                {!labRangos[sol.COD]&&<div style={{padding:12,textAlign:'center',fontSize:11,color:'var(--text3)'}}>Cargando resultados...</div>}
                                {labRangos[sol.COD]&&(
                                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                                    <thead>
                                      <tr style={{background:'var(--navy)'}}>
                                        <th style={{padding:'5px 10px',color:'#fff',textAlign:'left',fontSize:9,fontWeight:500}}>Determinación</th>
                                        <th style={{padding:'5px 10px',color:'#fff',textAlign:'left',fontSize:9,fontWeight:500}}>Resultado</th>
                                        <th style={{padding:'5px 10px',color:'#fff',textAlign:'left',fontSize:9,fontWeight:500}}>Unidad</th>
                                        <th style={{padding:'5px 10px',color:'#fff',textAlign:'left',fontSize:9,fontWeight:500}}>Rango</th>
                                        <th style={{padding:'5px 10px',color:'#fff',textAlign:'left',fontSize:9,fontWeight:500}}>Estado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(labRangos[sol.COD].detalles||[]).map(det=>{
                                        const est = evalRangoHist(det.RESULTADO, det.rango);
                                        const hasSubs = det.subdeterminaciones?.length>0;
                                        return (
                                          <React.Fragment key={det.COD}>
                                            {!hasSubs&&(
                                              <tr style={{borderBottom:'1px solid var(--bg)',background:est!=='ok'&&est!=='na'?'var(--red-lt)':''}}>
                                                <td style={{padding:'5px 10px',fontWeight:500}}>{det.DETERMINACION}</td>
                                                <td style={{padding:'5px 10px',fontWeight:600,color:est==='hi'?'var(--red)':est==='lo'?'var(--amber)':'var(--green)'}}>
                                                  {det.ARCHIVO_RESULTADO?(
                                                    <a href={`${API_BASE}/uploads/laboratorio/${det.ARCHIVO_RESULTADO}`} target="_blank" rel="noreferrer"
                                                      style={{color:'var(--navy-lt)',fontWeight:500}}>📄 Ver archivo ↗</a>
                                                  ):(<>{est==='hi'&&'↑ '}{est==='lo'&&'↓ '}{det.RESULTADO||'—'}</>)}
                                                </td>
                                                <td style={{padding:'5px 10px',fontSize:10,color:'var(--text3)'}}>{det.rango?.UNIDAD||'—'}</td>
                                                <td style={{padding:'5px 10px',fontSize:10,color:'var(--text3)'}}>
                                                  {det.rango?`${det.rango.LIMITE_INFERIOR>0?det.rango.LIMITE_INFERIOR:''} ${det.rango.LIMITE_INFERIOR>0&&det.rango.LIMITE_SUPERIOR>0?'–':''} ${det.rango.LIMITE_SUPERIOR>0?det.rango.LIMITE_SUPERIOR:''}`.trim()||'—':'—'}
                                                </td>
                                                <td style={{padding:'5px 10px'}}>
                                                  {det.RESULTADO
                                                    ? <span style={{fontSize:9,padding:'1px 7px',borderRadius:4,fontWeight:600,
                                                        background:est==='ok'?'var(--green-lt)':est==='hi'?'var(--red-lt)':'var(--amber-lt)',
                                                        color:est==='ok'?'var(--green)':est==='hi'?'var(--red)':'var(--amber)'}}>
                                                        {est==='ok'?'✓ Normal':est==='hi'?'↑ Alto':'↓ Bajo'}
                                                      </span>
                                                    : <span style={{fontSize:9,color:'var(--text3)'}}>—</span>}
                                                </td>
                                              </tr>
                                            )}
                                            {hasSubs&&(
                                              <>
                                                <tr style={{background:'var(--blue-lt)'}}>
                                                  <td colSpan={5} style={{padding:'3px 10px',fontSize:9,fontWeight:700,color:'var(--navy)',textTransform:'uppercase',letterSpacing:'.3px'}}>{det.DETERMINACION}</td>
                                                </tr>
                                                {det.subdeterminaciones.map(sub=>{
                                                  const est2=evalRangoHist(sub.RESULTADO,sub.rango);
                                                  return(
                                                    <tr key={sub.COD} style={{borderBottom:'1px solid var(--bg)',background:est2!=='ok'&&est2!=='na'?'var(--red-lt)':''}}>
                                                      <td style={{padding:'5px 10px 5px 20px'}}>{sub.SUBDETERMINACION}</td>
                                                      <td style={{padding:'5px 10px',fontWeight:600,color:est2==='hi'?'var(--red)':est2==='lo'?'var(--amber)':'var(--green)'}}>
                                                        {est2==='hi'&&'↑ '}{est2==='lo'&&'↓ '}{sub.RESULTADO||'—'}
                                                      </td>
                                                      <td style={{padding:'5px 10px',fontSize:10,color:'var(--text3)'}}>{sub.rango?.UNIDAD||'—'}</td>
                                                      <td style={{padding:'5px 10px',fontSize:10,color:'var(--text3)'}}>
                                                        {sub.rango?`${sub.rango.LIMITE_INFERIOR>0?sub.rango.LIMITE_INFERIOR:''} ${sub.rango.LIMITE_INFERIOR>0&&sub.rango.LIMITE_SUPERIOR>0?'–':''} ${sub.rango.LIMITE_SUPERIOR>0?sub.rango.LIMITE_SUPERIOR:''}`.trim()||'—':'—'}
                                                      </td>
                                                      <td style={{padding:'5px 10px'}}>
                                                        {sub.RESULTADO
                                                          ? <span style={{fontSize:9,padding:'1px 7px',borderRadius:4,fontWeight:600,
                                                              background:est2==='ok'?'var(--green-lt)':est2==='hi'?'var(--red-lt)':'var(--amber-lt)',
                                                              color:est2==='ok'?'var(--green)':est2==='hi'?'var(--red)':'var(--amber)'}}>
                                                              {est2==='ok'?'✓ Normal':est2==='hi'?'↑ Alto':'↓ Bajo'}
                                                            </span>
                                                          : <span style={{fontSize:9,color:'var(--text3)'}}>—</span>}
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                                {/* Observaciones */}
                                {labRangos[sol.COD]&&sol.OBS_RESULTADOS&&(
                                  <div style={{padding:'8px 12px',background:'var(--blue-lt)',fontSize:11,color:'var(--navy)',borderTop:'1px solid var(--border)'}}>
                                    <strong>Obs:</strong> {sol.OBS_RESULTADOS}
                                  </div>
                                )}
                              </div>
                            )}
                            {sol.DATOS_CLINICOS&&!sol.DATOS_CLINICOS.includes('ARCHIVO:')&&<div style={{padding:'4px 12px 8px',fontSize:11,color:'var(--text3)'}}>{sol.DATOS_CLINICOS}</div>}
                          </div>
                        ))}
                      <button className="btn btn-primary btn-sm" style={{width:'100%'}} onClick={()=>navigate(`/laboratorio?paciente=${codPac}`)}>+ Nueva solicitud de laboratorio</button>
                    </>)}

                    {tab==='antecedentes'&&(
                      <div className="sec-card">
                        <div className="sec-card-hdr"><span className="sec-card-ttl"><i className="ti ti-clipboard-list" aria-hidden="true"></i> Antecedentes</span></div>
                        <div className="sec-card-body">
                          {antecs.length===0?<div className="empty-hc">Sin antecedentes registrados</div>
                            :<div style={{display:'flex',flexDirection:'column',gap:10}}>
                              {['PAT','NPAT','FAM','QRX','GIN','OTR','PER'].map(tipo=>{
                                const items = antecs.filter(a=>a.C_TIPO===tipo);
                                if(!items.length) return null;
                                return (
                                  <div key={tipo} style={{border:'1px solid var(--border)',borderRadius:9,overflow:'hidden'}}>
                                    <div style={{background:'var(--surface2)',padding:'7px 12px',fontSize:10.5,fontWeight:700,color:'var(--navy)',textTransform:'uppercase',letterSpacing:.3}}>
                                      {TIPO_ICON[tipo]} {TIPO_MAP[tipo]||tipo}
                                    </div>
                                    <div style={{padding:'10px 12px',display:'flex',flexWrap:'wrap',gap:6}}>
                                      {items.map((a,i)=>(
                                        <div key={i} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:20,padding:'5px 11px',fontSize:11}}>
                                          <span>{a.ANTECEDENTE||'—'}</span>
                                          {a.C_PARENTESCO&&<span style={{color:'var(--text3)',fontSize:9.5}}>({a.C_PARENTESCO})</span>}
                                          {a.FECHA_EVENTO&&<span style={{color:'var(--text3)',fontSize:9.5}}>({fmtD(a.FECHA_EVENTO)})</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>}
                        </div>
                      </div>
                    )}

                    {tab==='alergias'&&(
                      <div className="sec-card">
                        <div className="sec-card-hdr"><span className="sec-card-ttl"><i className="ti ti-alert-triangle" aria-hidden="true"></i> Alergias registradas</span></div>
                        <div className="sec-card-body" style={{padding:0}}>
                          {alergias.length===0?<div className="empty-hc">Sin alergias registradas</div>
                            :alergias.map((a,i)=>(
                              <div key={i} className="alg-row">
                                <div className="alg-ico">⚠️</div>
                                <div><div className="alg-nom">{a.ALERGIA}</div><div className="alg-reg">Registrada: {a.FECHA_REGISTRO?new Date(a.FECHA_REGISTRO).toLocaleDateString('es-EC'):'—'}</div></div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {tab==='auditoria'&&(
                      <div className="sec-card">
                        <div className="sec-card-hdr">
                          <span className="sec-card-ttl"><i className="ti ti-history" aria-hidden="true"></i> Auditoría del registro</span>
                          {!auditLogs.length&&<button className="btn btn-sm" style={{fontSize:10}}
                            onClick={()=>cargarAuditoria(detalle.COD)}>Cargar historial</button>}
                        </div>
                        <div className="sec-card-body" style={{padding:0}}>
                          {loadAudit?<div className="spin-wrap"><div className="sp"/></div>
                          :auditLogs.length===0?<div className="empty-hc"><div className="empty-icon">📋</div>Clic en "Cargar historial" para ver los cambios</div>
                          :auditLogs.map((l,i)=>{
                            const acCls={CREACION:'badge-green',EDICION_DENTRO_PLAZO:'badge-blue',
                              EDICION_AUTORIZADA:'badge-amber',AUTORIZACION_CONCEDIDA:'badge-amber',
                              IMPRESION:'badge-gray'}[l.ACCION]||'badge-gray';
                            const acLbl={CREACION:'✚ Creación',EDICION_DENTRO_PLAZO:'✏️ Edición',
                              EDICION_AUTORIZADA:'🔓 Ed. autorizada',AUTORIZACION_CONCEDIDA:'✅ Autorizado',
                              AUTORIZACION_NEGADA:'❌ Negado',IMPRESION:'🖨️ Impresión'}[l.ACCION]||l.ACCION;
                            return(
                              <div key={i} style={{display:'flex',gap:10,padding:'10px 14px',borderBottom:'1px solid var(--border)',fontSize:11}}>
                                <div style={{flexShrink:0}}>
                                  <span className={`badge ${acCls}`}>{acLbl}</span>
                                </div>
                                <div style={{flex:1}}>
                                  <div style={{fontWeight:600,color:'var(--text)'}}>{l.USUARIO_NOM}</div>
                                  <div style={{color:'var(--text3)',marginTop:1}}>{l.ROL_USUARIO} · {l.FECHA}{l.CAMPOS_MODIFICADOS?` · ${l.CAMPOS_MODIFICADOS}`:''}</div>
                                  {l.MOTIVO&&<div style={{color:'var(--amber)',marginTop:2,fontStyle:'italic'}}>Motivo: {l.MOTIVO}</div>}
                                  {l.AUTORIZADO_POR&&<div style={{color:'var(--green)',marginTop:2}}>Autorizado por: {l.AUTORIZADO_NOM||l.AUTORIZADO_POR}</div>}
                                </div>
                                {l.IP&&<div style={{fontSize:9,color:'var(--text3)',flexShrink:0}}>{l.IP.replace('::ffff:','')}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </>
            ):(
              <div className="empty-hc"><div className="empty-icon">📋</div>Selecciona una consulta del historial</div>
            )}
          </div>

          {/* ══ SIDEBAR LECTURA RÁPIDA ══ */}
          <div className="qr-panel">
            <div className="qr-hd">
              <div className="qr-avatar">
                {paciente?.FOTO_URL
                  ?<img src={fotoUrl(paciente.FOTO_URL)} alt=""/>
                  :(paciente?.PRIMER_NOMBRE?.[0]||'')+(paciente?.APELLIDO_PATERNO?.[0]||'')}
              </div>
              <div>
                <div className="qr-tag">Lectura rápida</div>
                <div className="qr-name">{[paciente?.APELLIDO_PATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
                <div className="qr-sub">HC {codPac} · {calcEdad(paciente?.FECHA_NACIMIENTO)} · {paciente?.C_SEXO==='M'?'Masculino':paciente?.C_SEXO==='F'?'Femenino':'—'}</div>
              </div>
            </div>
            <div className="qr-body">

              <div className="qr-section">
                <div className="qr-section-title">Alergias</div>
                {alergias.length>0
                  ?<div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {alergias.slice(0,4).map(a=><span key={a.COD} className="badge badge-red">⚠️ {a.ALERGIA}</span>)}
                    </div>
                  :<span className="badge badge-green">Sin alergias registradas</span>}
              </div>

              <div className="qr-section">
                <div className="qr-section-title">Últimos signos vitales</div>
                <div className="qr-vitals">
                  {Object.entries(svMapUltimos).map(([key,val])=>{
                    const r=SV_RANGOS[key]; const e=val&&r?r.eval(val):'na';
                    return (
                      <div key={key} className={`qr-vital ${['alto','critico','bajo'].includes(e)?'hi':''}`}>
                        <div className="qr-vital-v">{val||'—'}</div>
                        <div className="qr-vital-l">{r?.label||key}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="qr-section">
                <div className="qr-section-title">Historia reciente</div>
                {historiaReciente.length===0
                  ?<div className="qr-empty">Sin registros previos</div>
                  :historiaReciente.map(r=>(
                    <div key={r.COD} className="qr-hist-item">
                      <div className="qr-hist-date">{fmtD(r.FECHA)}</div>
                      <div className="qr-hist-txt">
                        {r.diagnosticos?.length>0
                          ?r.diagnosticos.map(d=>d.CIE10_DESC||d.COD_CIE10).filter(Boolean).join(' · ')
                          :(r.EVOLUCION?.slice(0,60)+(r.EVOLUCION?.length>60?'…':'')||'Sin diagnóstico registrado')}
                      </div>
                    </div>
                  ))}
              </div>

              <div className="qr-section">
                <div className="qr-section-title">Historial de exámenes</div>
                {examenesRecientes.length===0
                  ?<div className="qr-empty">Sin solicitudes de laboratorio</div>
                  :<>
                    {examenesRecientes.map(sol=>(
                      <div key={sol.COD} className="qr-exam">
                        <div>
                          <div className="qr-exam-name">{sol.TIPO_DETERMINACION}</div>
                          <div className="qr-exam-date">{fmtD(sol.FECHA_SOLICITUD)}</div>
                        </div>
                        <span className={`badge ${sol.C_ESTADO==='CNF'?'badge-green':sol.C_ESTADO==='ANL'?'badge-red':'badge-blue'}`}>
                          {sol.C_ESTADO==='CNF'?'Confirmado':sol.C_ESTADO==='ANL'?'Anulado':'Solicitado'}
                        </span>
                      </div>
                    ))}
                    {labDet.length>examenesRecientes.length&&(
                      <div style={{textAlign:'center',marginTop:8}}>
                        <span className="qr-link" onClick={()=>setTab('lab')}>Ver historial completo ({labDet.length}) →</span>
                      </div>
                    )}
                  </>}
              </div>

              <div className="qr-section">
                <div className="qr-section-title">Medicación (última receta)</div>
                {medicacionActiva.length===0
                  ?<div className="qr-empty">Sin recetas registradas</div>
                  :medicacionActiva.map((it,i)=>(
                    <div key={i} className="qr-med">
                      <span>💊</span>
                      <span className="qr-med-txt">{it.MEDICAMENTO}{it.INDICACION?` — ${it.INDICACION}`:''}</span>
                    </div>
                  ))}
              </div>

              <div className="qr-section">
                <div className="qr-section-title">Antecedentes</div>
                {antecs.length===0
                  ?<div className="qr-empty">Sin antecedentes registrados</div>
                  :<>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {antecs.slice(0,6).map(a=>(
                        <span key={a.COD} className={`badge ${a.C_TIPO==='PAT'?'badge-red':a.C_TIPO==='NPAT'?'badge-green':'badge-blue'}`}>
                          {TIPO_ICON[a.C_TIPO]} {a.ANTECEDENTE}
                        </span>
                      ))}
                    </div>
                    {antecs.length>6&&(
                      <div style={{textAlign:'center',marginTop:8}}>
                        <span className="qr-link" onClick={()=>setTab('antecedentes')}>Ver todos ({antecs.length}) →</span>
                      </div>
                    )}
                  </>}
              </div>

            </div>
            <div className="qr-footer">
              <span className="qr-link" onClick={()=>setTab('antecedentes')}>Ver antecedentes completos →</span>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          MODAL NUEVA / EDITAR CONSULTA
      ════════════════════════════════════════ */}
      {/* ══ SELECTOR DE FORMATO DE CONSULTA ══ */}
      {fmtSelector&&(
        <div className="fmt-ov" onClick={e=>e.target.classList.contains('fmt-ov')&&setFmtSelector(false)}>
          <div className="fmt-modal">
            <div className="fmt-hdr">
              <div className="fmt-hdr-t">¿Con qué formato deseas registrar la consulta?</div>
              <div className="fmt-hdr-s">Selecciona el formato para — {[paciente?.APELLIDO_PATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
            </div>
            <div className="fmt-body">
              <div className="fmt-cards">
                {/* Formato libre */}
                <div className={`fmt-card${fmtConsulta==='libre'?' sel':''}`} onClick={()=>setFmtConsulta('libre')}>
                  {fmtConsulta==='libre'&&<span className="fmt-sel-badge">✓ Seleccionado</span>}
                  <div className="fmt-card-ico">📝</div>
                  <div className="fmt-card-ttl">Formato libre</div>
                  <div className="fmt-card-desc">Signos vitales, evolución libre o S.O.A.P., diagnósticos CIE-10 y receta. Ideal para seguimientos rápidos.</div>
                  <div className="fmt-card-tags">
                    <span className="fmt-tag fmt-tag-g">Rápido</span>
                    <span className="fmt-tag fmt-tag-g">Seguimiento</span>
                    <span className="fmt-tag fmt-tag-g">S.O.A.P.</span>
                  </div>
                </div>
                {/* Formulario 002 MSP */}
                <div className={`fmt-card${fmtConsulta==='002'?' sel':''}`} onClick={()=>setFmtConsulta('002')}>
                  {fmtConsulta==='002'&&<span className="fmt-sel-badge">✓ Seleccionado</span>}
                  <div className="fmt-card-ico">🏥</div>
                  <div className="fmt-card-ttl">Formulario 002 MSP</div>
                  <div className="fmt-card-desc">Incluye antecedentes, revisión de órganos, examen físico regional y sistémico, plan de tratamiento. Genera el formulario oficial imprimible.</div>
                  <div className="fmt-card-tags">
                    <span className="fmt-tag fmt-tag-b">Oficial MSP</span>
                    <span className="fmt-tag fmt-tag-b">Imprimible</span>
                    <span className="fmt-tag fmt-tag-b">Auditable</span>
                  </div>
                </div>
              </div>
              <div className="fmt-info">
                <span style={{fontSize:16,flexShrink:0}}>ℹ️</span>
                <div style={{fontSize:10,color:'var(--navy)'}}>
                  El formato aplica solo a esta consulta. Si seleccionas <strong>Formulario 002 MSP</strong>, al guardar podrás imprimir el formulario oficial del Ministerio de Salud Pública.
                </div>
              </div>
            </div>
            <div className="fmt-ftr">
              <button className="btn-mo-sec" onClick={()=>setFmtSelector(false)}>Cancelar</button>
              <button className="btn-mo-pri" onClick={confirmarFormato}>Continuar →</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL IMPRESIÓN FORMULARIO 008 ══ */}
      {showPrint&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
          onClick={e=>e.target===e.currentTarget&&setShowPrint(false)}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:440,border:'1px solid var(--border)',overflow:'hidden'}}>
            {/* Header */}
            <div style={{background:'var(--navy)',padding:'13px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:'#fff'}}>🖨️ Imprimir Formulario 008</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,.7)',marginTop:2}}>Historia Clínica Única — MSP Ecuador</div>
              </div>
              <button style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:18}} onClick={()=>setShowPrint(false)}>✕</button>
            </div>
            {/* Body */}
            <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text)',marginBottom:4}}>¿Qué deseas imprimir?</div>

              {/* Opción 1 — Consulta actual */}
              <label style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:9,border:`2px solid ${printMode==='actual'?'var(--navy-lt)':'var(--border)'}`,background:printMode==='actual'?'var(--blue-lt)':'var(--surface)',cursor:'pointer'}}>
                <input type="radio" name="printMode" value="actual" checked={printMode==='actual'} onChange={()=>setPrintMode('actual')} style={{marginTop:2}}/>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>Consulta seleccionada</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{fmtF(detalle?.FECHA)} · {detalle?.MEDICO||''}</div>
                </div>
              </label>

              {/* Opción 2 — Todas */}
              <label style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:9,border:`2px solid ${printMode==='todas'?'var(--navy-lt)':'var(--border)'}`,background:printMode==='todas'?'var(--blue-lt)':'var(--surface)',cursor:'pointer'}}>
                <input type="radio" name="printMode" value="todas" checked={printMode==='todas'} onChange={()=>setPrintMode('todas')} style={{marginTop:2}}/>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>Toda la historia clínica</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{registros.length} consulta{registros.length!==1?'s':''} registradas</div>
                </div>
              </label>

              {/* Opción 3 — Por fechas */}
              <label style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:9,border:`2px solid ${printMode==='fechas'?'var(--navy-lt)':'var(--border)'}`,background:printMode==='fechas'?'var(--blue-lt)':'var(--surface)',cursor:'pointer'}}>
                <input type="radio" name="printMode" value="fechas" checked={printMode==='fechas'} onChange={()=>setPrintMode('fechas')} style={{marginTop:2}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>Por rango de fechas</div>
                  {printMode==='fechas'&&(
                    <div style={{display:'flex',gap:8,marginTop:8,alignItems:'center'}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Desde</div>
                        <input type="date" className="form-control" style={{fontSize:11}} value={printDesde} onChange={e=>setPrintDesde(e.target.value)}/>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Hasta</div>
                        <input type="date" className="form-control" style={{fontSize:11}} value={printHasta} onChange={e=>setPrintHasta(e.target.value)}/>
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <div style={{background:'var(--blue-lt)',borderRadius:8,padding:'9px 12px',fontSize:11,color:'var(--navy)'}}>
                ℹ️ Se abrirá una ventana para previsualizar e imprimir el formulario oficial MSP 008
              </div>
            </div>
            {/* Footer */}
            <div style={{padding:'10px 18px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:8,background:'var(--surface2)'}}>
              <button className="btn-mo-sec" onClick={()=>setShowPrint(false)}>Cancelar</button>
              <button className="btn-mo-pri" disabled={printLoading||( printMode==='fechas'&&!printDesde&&!printHasta)} onClick={imprimir008}>
                {printLoading?'Generando...':'🖨️ Generar e imprimir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL AUTORIZACIÓN EDICIÓN FUERA DE PLAZO ══ */}
      {showAuthModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
          onClick={e=>e.target===e.currentTarget&&setShowAuth(false)}>
          <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:420,border:'1px solid var(--border)',overflow:'hidden'}}>
            {/* Header */}
            <div style={{background:'var(--amber)',padding:'14px 18px',display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:22}}>🔒</span>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:'#fff'}}>Edición fuera de plazo</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,.85)',marginTop:2}}>
                  Esta consulta superó las {editInfo?.horasConfig||72}h de edición libre
                </div>
              </div>
            </div>
            {/* Body */}
            <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{background:'var(--amber-lt)',border:'1px solid var(--amber)',borderRadius:8,padding:'10px 12px',fontSize:11,color:'var(--amber)'}}>
                <strong>Consulta:</strong> {fmtF(editPendiente?.FECHA)}<br/>
                <strong>Tiempo transcurrido:</strong> {Math.round(editInfo?.horasDesde||0)}h ({Math.floor((editInfo?.horasDesde||0)/24)}d {Math.round((editInfo?.horasDesde||0)%24)}h)<br/>
                <strong>Solo puede autorizar:</strong> Administrador o Director Médico
              </div>
              {authMsg&&<div style={{padding:'7px 12px',borderRadius:8,fontSize:12,background:'var(--red-lt)',color:'var(--red)',border:'1px solid var(--red)'}}>{authMsg}</div>}
              <div className="form-group">
                <label className="form-label">Motivo de la edición *</label>
                <textarea className="form-control" rows={3}
                  placeholder="Ej: Corrección de diagnóstico, información omitida..."
                  value={authMotivo} onChange={e=>setAuthMotivo(e.target.value)}/>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:4}}>Queda registrado en la auditoría de historia clínica</div>
              </div>
              <div style={{background:'var(--blue-lt)',borderRadius:8,padding:'9px 12px',fontSize:11,color:'var(--navy)'}}>
                ℹ️ Debes tener rol <strong>Administrador</strong> o <strong>Director Médico</strong>. La acción queda registrada con tu usuario.
              </div>
            </div>
            {/* Footer */}
            <div style={{padding:'10px 18px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:8,background:'var(--surface2)'}}>
              <button className="btn-mo-sec" onClick={()=>setShowAuth(false)}>Cancelar</button>
              <button className="btn-mo-pri" style={{background:'var(--amber)'}} disabled={authLoading} onClick={confirmarAutorizacion}>
                {authLoading?'Autorizando...':'✓ Autorizar y editar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNueva&&(
        <div className="ov">
          <div className="modal">
            <div className="mo-hdr">
              <span className="mo-hdr-title">{editCod?'✏️ Editar':'Nueva consulta'} — {[paciente?.APELLIDO_PATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</span>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <div className="mo-tabs">
                  {[['consulta','📋 Consulta'],['receta','💊 Receta'],['lab','🧪 Lab'],['antecedente','🗂 Antecedente'],['alergia','⚠️ Alergia'],['gineco','🌸 Gineco-obstétrico']].map(([t,l])=>(
                    <button key={t} className={`mo-tab ${modalTab===t?'act':''}`} onClick={()=>setModalTab(t)}>{l}</button>
                  ))}
                </div>
                <button className="btn-mo-sec" style={{padding:'4px 10px'}} onClick={()=>{setNueva(false);setMsg('');}}>✕</button>
              </div>
            </div>

            <div className="mo-body">
              {msg&&<div style={{padding:'7px 12px',borderRadius:8,fontSize:12,
                background:msg.startsWith('✓')?'var(--green-lt)':'var(--red-lt)',
                color:msg.startsWith('✓')?'var(--green)':'var(--red)',
                border:`1px solid ${msg.startsWith('✓')?'var(--green)':'var(--red)'}`}}>{msg}</div>}

              {borradorInfo&&(
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,
                  background:'var(--blue-lt)',border:'1px solid var(--navy-lt)',fontSize:12,color:'var(--navy)'}}>
                  <span style={{flex:1}}>💾 Se encontró un borrador autoguardado de esta {editCod?'consulta':'nueva consulta'}
                    ({new Date(borradorInfo.FECHA_ACTUALIZACION).toLocaleString('es-EC',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}). ¿Deseas continuar con él?</span>
                  <button type="button" className="btn-mo-pri" style={{fontSize:11,padding:'4px 10px'}} onClick={restaurarBorradorPendiente}>Restaurar</button>
                  <button type="button" className="btn-mo-sec" style={{fontSize:11,padding:'4px 10px'}} onClick={descartarBorradorPendiente}>Descartar</button>
                </div>
              )}

              {editCod&&editInfo&&!editInfo.dentroPlazo&&(
                <div className="form-group" style={{background:'var(--amber-lt)',border:'1px solid var(--amber)',borderRadius:8,padding:'10px 12px'}}>
                  <label className="form-label" style={{color:'var(--amber)'}}>⚠️ Motivo de la edición fuera de plazo *</label>
                  <textarea className="form-control" rows={2}
                    placeholder="Ej: Corrección de diagnóstico, información omitida..."
                    value={form._MOTIVO||''} onChange={e=>setForm(f=>({...f,_MOTIVO:e.target.value}))}/>
                  <div style={{fontSize:10,color:'var(--text3)',marginTop:4}}>
                    Esta consulta superó las {editInfo.horasConfig||72}h de edición libre. Queda registrado en la auditoría.
                  </div>
                </div>
              )}

              {modalTab==='consulta'&&(<>
                {/* Signos vitales input */}
                <div>
                  <div className="mo-sec" style={{display:'flex',alignItems:'center',gap:8}}>
                    Signos vitales
                    <button type="button" onClick={dictarSV} disabled={dictandoSV}
                      style={{border:'1px solid var(--border)',background:dictandoSV?'var(--red-lt)':'var(--surface)',
                        color:dictandoSV?'var(--red)':'var(--navy)',borderRadius:6,padding:'2px 8px',fontSize:10,
                        fontWeight:700,cursor:dictandoSV?'default':'pointer',display:'inline-flex',alignItems:'center',gap:4,
                        textTransform:'none',letterSpacing:0}}>
                      <i className={`ti ${dictandoSV?'ti-microphone-off':'ti-microphone'}`} aria-hidden="true"></i>
                      {dictandoSV?'Escuchando…':'Dictar por voz'}
                    </button>
                  </div>
                  {svDictado && (
                    <div style={{fontSize:10.5,marginBottom:6,padding:'5px 9px',borderRadius:6,
                      background: svDictado.ok?'var(--green-lt)':'var(--bg)',
                      color: svDictado.ok?'var(--green)':'var(--text3)'}}>
                      {svDictado.ok
                        ? `🎙 Detectado: ${Object.entries(svDictado.detectado).map(([k,v])=>`${SV_RANGOS[k]?.label||k} ${v}`).join(', ')}`
                        : `🎙 No reconocí ningún signo vital en: "${svDictado.texto}"`}
                    </div>
                  )}
                  <div className="sv-input-grid">
                    {[['PRESION_ARTERIAL','P. Arterial','120/80','mmHg','ti-activity'],
                      ['PULSO','Pulso','72','bpm','ti-heart'],
                      ['RESPIRATORIA','Fr. Resp.','16','rpm','ti-wind'],
                      ['SATURACION_OXIGENO','Sat O₂','98','%','ti-lungs'],
                      ['PESO','Peso','68','kg','ti-scale'],
                      ['TALLA','Talla','162','cm','ti-ruler'],
                      ['CINTURA_ABDOMINAL','Cint. abdom.','88','cm','ti-tape-measure'],
                      ['TEMP_AXILAR','Temp.','36.8','°C','ti-thermometer']].map(([k,l,ph,unit,ic])=>{
                      const valorAnterior = (!editCod && svAnterior) ? svAnterior[k] : null;
                      let flecha = null;
                      if (valorAnterior && form.signosVitales[k] && k!=='PRESION_ARTERIAL') {
                        const actualNum = parseFloat(form.signosVitales[k]);
                        const anteriorNum = parseFloat(valorAnterior);
                        if (!isNaN(actualNum) && !isNaN(anteriorNum) && actualNum!==anteriorNum) {
                          flecha = actualNum > anteriorNum ? 'ti-arrow-up' : 'ti-arrow-down';
                        }
                      }
                      return (
                      <div key={k} className={`sv-input-card ${flecha?'cambiado':''}`}>
                        <div className="sv-input-icon"><i className={`ti ${ic}`} aria-hidden="true"></i></div>
                        <div className="sv-input-body">
                          <div className="sv-input-lbl">{l}</div>
                          <input className="sv-input-fld" placeholder={ph} value={form.signosVitales[k]||''}
                            onChange={e=>setForm(f=>({...f,signosVitales:{...f.signosVitales,[k]:e.target.value}}))}/>
                          <div className="sv-input-unit">{unit}</div>
                          {valorAnterior && (
                            <div className="sv-input-anterior">
                              {flecha && <i className={`ti ${flecha}`} aria-hidden="true"></i>}
                              anterior: {valorAnterior}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    {(()=>{
                      const imcLive = calcIMC(form.signosVitales.PESO, form.signosVitales.TALLA);
                      const pesoIdealLive = calcPesoIdeal(form.signosVitales.TALLA);
                      const deficitLive = (imcLive!=null && pesoIdealLive!=null) ? (parseFloat(form.signosVitales.PESO) - pesoIdealLive) : null;
                      if (imcLive==null && pesoIdealLive==null) return null;
                      return (<>
                        {imcLive!=null && (
                          <div className="sv-input-card">
                            <div className="sv-input-icon"><i className="ti ti-report-analytics" aria-hidden="true"></i></div>
                            <div className="sv-input-body">
                              <div className="sv-input-lbl">IMC (calc.)</div>
                              <div className="sv-input-fld" style={{cursor:'default'}}>{imcLive.toFixed(1)}</div>
                              <div className="sv-input-unit">{imcEtiqueta(imcLive)}</div>
                            </div>
                          </div>
                        )}
                        {pesoIdealLive!=null && (
                          <div className="sv-input-card">
                            <div className="sv-input-icon"><i className="ti ti-target-arrow" aria-hidden="true"></i></div>
                            <div className="sv-input-body">
                              <div className="sv-input-lbl">Peso ideal (calc.)</div>
                              <div className="sv-input-fld" style={{cursor:'default'}}>{pesoIdealLive.toFixed(1)}</div>
                              <div className="sv-input-unit">kg</div>
                            </div>
                          </div>
                        )}
                        {deficitLive!=null && (
                          <div className="sv-input-card">
                            <div className="sv-input-icon"><i className={`ti ${deficitLive>=0?'ti-trending-up':'ti-trending-down'}`} aria-hidden="true"></i></div>
                            <div className="sv-input-body">
                              <div className="sv-input-lbl">{deficitLive>=0?'Excedente':'Déficit'} (calc.)</div>
                              <div className="sv-input-fld" style={{cursor:'default'}}>{deficitLive>=0?'+':''}{deficitLive.toFixed(1)}</div>
                              <div className="sv-input-unit">kg</div>
                            </div>
                          </div>
                        )}
                      </>);
                    })()}
                  </div>
                </div>

                {fmtConsulta==='002'&&(<>
                {/* B. Motivo — texto + Primera/Subsecuente a la derecha */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">B. Motivo de consulta</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll">
                    <div style={{display:'flex',gap:10,alignItems:'flex-start',flexWrap:'wrap'}}>
                      <textarea className="form-control" rows={2} style={{flex:1,minWidth:200}} placeholder="Motivo de la consulta..."
                        value={form.MOTIVO_CONSULTA||''} onChange={e=>setForm(f=>({...f,MOTIVO_CONSULTA:e.target.value}))}/>
                      <div className="tipo-btn-row" style={{flexShrink:0,marginLeft:'auto'}}>
                        <button type="button" className={`tipo-btn${form002.C_TIPO_CONSULTA==='PRI'?' act':''}`} onClick={()=>setForm002(f=>({...f,C_TIPO_CONSULTA:'PRI'}))}>✓ Primera</button>
                        <button type="button" className={`tipo-btn${form002.C_TIPO_CONSULTA==='SUB'?' act':''}`} onClick={()=>setForm002(f=>({...f,C_TIPO_CONSULTA:'SUB'}))}>Subsecuente</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* C. Antecedentes Personales */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">C. Antecedentes patológicos personales</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll-lg">
                    <div className="chk002-grid">
                      {['CARDIOPATIA','HIPERTENSION','ENF_VALVULAR','ENDOCRINOLOGICO','CANCER','TUBERCULOSIS','ENF_INFECCIOSA','ENF_HEREDITARIA','QUIRURGICO','GINECO_OBS','OTROS'].map(k=>(
                        <div key={k} className={`chk002-item${form002.ANT_PER_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,ANT_PER_FLAGS:{...f.ANT_PER_FLAGS,[k]:!f.ANT_PER_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.ANT_PER_FLAGS[k]?' on':''}`}>{form002.ANT_PER_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{CARDIOPATIA:'Cardiopatía',HIPERTENSION:'Hipertensión',ENF_VALVULAR:'Enf. Valvular',ENDOCRINOLOGICO:'Endocrinológico',CANCER:'Cáncer',TUBERCULOSIS:'Tuberculosis',ENF_INFECCIOSA:'Enf. Infecciosa',ENF_HEREDITARIA:'Enf. Hereditaria',QUIRURGICO:'Quirúrgico',GINECO_OBS:'Gineco-Obst.',OTROS:'Otros'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <textarea className="form-control" rows={2} placeholder="Describa antecedentes personales..."
                      value={form002.ANT_PER_DETALLE} onChange={e=>setForm002(f=>({...f,ANT_PER_DETALLE:e.target.value}))}/>
                  </div>
                </div>

                {/* D. Antecedentes Familiares */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">D. Antecedentes patológicos familiares</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll-lg">
                    <div className="chk002-grid">
                      {['CARDIOPATIA','HIPERTENSION','ENF_VALVULAR','ENDOCRINOLOGICO','CANCER','TUBERCULOSIS','ENF_INFECCIOSA','ENF_HEREDITARIA','OTROS'].map(k=>(
                        <div key={k} className={`chk002-item${form002.ANT_FAM_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,ANT_FAM_FLAGS:{...f.ANT_FAM_FLAGS,[k]:!f.ANT_FAM_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.ANT_FAM_FLAGS[k]?' on':''}`}>{form002.ANT_FAM_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{CARDIOPATIA:'Cardiopatía',HIPERTENSION:'Hipertensión',ENF_VALVULAR:'Enf. Valvular',ENDOCRINOLOGICO:'Endocrinológico',CANCER:'Cáncer',TUBERCULOSIS:'Tuberculosis',ENF_INFECCIOSA:'Enf. Infecciosa',ENF_HEREDITARIA:'Enf. Hereditaria',OTROS:'Otros'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <textarea className="form-control" rows={2} placeholder="Describa antecedentes familiares..."
                      value={form002.ANT_FAM_DETALLE} onChange={e=>setForm002(f=>({...f,ANT_FAM_DETALLE:e.target.value}))}/>
                  </div>
                </div>

                </>)}
                {fmtConsulta==='libre'&&(
                  <div className="form-group">
                    <div className="mo-sec">Motivo de consulta</div>
                    <input className="form-control" placeholder="Ej: Control oncológico, dolor abdominal..."
                      value={form.MOTIVO_CONSULTA||''} onChange={e=>setForm(f=>({...f,MOTIVO_CONSULTA:e.target.value}))}/>
                  </div>
                )}

                {/* IA */}
                {iaActivo&&(
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',
                    background:grabando?'var(--red-lt)':'var(--surface2)',
                    border:`1px solid ${grabando?'var(--red)':'var(--border)'}`,borderRadius:9}}>
                    <button type="button" disabled={iaProcesando}
                      style={{width:36,height:36,borderRadius:'50%',border:'none',cursor:'pointer',flexShrink:0,fontSize:16,
                        display:'flex',alignItems:'center',justifyContent:'center',
                        background:grabando?'var(--red)':iaProcesando?'var(--amber-lt)':'var(--blue-lt)',
                        color:grabando?'#fff':iaProcesando?'var(--amber)':'var(--navy)'}}
                      onClick={async()=>{
                        if(!grabando){
                          try{
                            const stream=await navigator.mediaDevices.getUserMedia({audio:true});
                            const mr=new MediaRecorder(stream); const chunks=[];
                            mr.ondataavailable=e=>chunks.push(e.data);
                            mr.onstop=async()=>{
                              stream.getTracks().forEach(t=>t.stop()); setGrabando(false); setIaProc(true);
                              clearInterval(timerRef.current); setTimerSeg(0);
                              try{
                                const blob=new Blob(chunks,{type:'audio/webm'}); const fd=new FormData();
                                fd.append('audio',blob,'consulta.webm');
                                fd.append('paciente',[paciente?.APELLIDO_PATERNO,paciente?.APELLIDO_MATERNO,paciente?.PRIMER_NOMBRE].filter(Boolean).join(' '));
                                fd.append('especialidad',usuario?.especialidad||'');
                                const token=localStorage.getItem('token');
                                const resp=await fetch(`${process.env.REACT_APP_API_URL||'http://localhost:3001/api'}/historia/ia-transcribir`,{method:'POST',headers:{Authorization:`Bearer ${token}`},body:fd});
                                const data=await resp.json(); if(data.error)throw new Error(data.error);
                                if(typeof data.borrador==='object'){
                                  const ev=data.borrador;
                                  data.borrador=[ev.motivo?'Motivo: '+ev.motivo:'',ev.examen_fisico?'Examen físico: '+ev.examen_fisico:'',ev.plan_terapeutico?'Plan: '+ev.plan_terapeutico:''].filter(Boolean).join('\n\n');
                                }
                                setIaBorrador(data);
                                if(data.signos_vitales&&Object.keys(data.signos_vitales).length>0){
                                  setForm(f=>{ const svN={...f.signosVitales}; const sv=data.signos_vitales;
                                    if(sv.PRESION_ARTERIAL)svN.PRESION_ARTERIAL=sv.PRESION_ARTERIAL;
                                    if(sv.PULSO)svN.PULSO=String(sv.PULSO); if(sv.RESPIRATORIA)svN.RESPIRATORIA=String(sv.RESPIRATORIA); if(sv.PESO)svN.PESO=String(sv.PESO);
                                    if(sv.TALLA)svN.TALLA=String(sv.TALLA); if(sv.TEMP_AXILAR)svN.TEMP_AXILAR=String(sv.TEMP_AXILAR);
                                    if(sv.SATURACION_OXIGENO||sv.SATURACION_O2)svN.SATURACION_OXIGENO=String(sv.SATURACION_OXIGENO||sv.SATURACION_O2);
                                    return{...f,signosVitales:svN}; });
                                }
                              }catch(e){alert('Error IA: '+e.message);} setIaProc(false);
                            };
                            mr.start(); setMediaRec(mr); setGrabando(true); setTimerSeg(0);
                            timerRef.current=setInterval(()=>setTimerSeg(s=>s+1),1000);
                          }catch(e){alert('No se pudo acceder al micrófono: '+e.message);}
                        }else{mediaRec?.stop();}
                      }}>
                      {iaProcesando?'⏳':grabando?'⏹':'🎤'}
                    </button>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>{iaProcesando?'Procesando con IA...':grabando?'Grabando consulta...':'Asistente IA'}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{iaProcesando?'Whisper + GPT-4 analizando...':grabando?`${Math.floor(timerSeg/60)}:${String(timerSeg%60).padStart(2,'0')} — clic ⏹ para detener`:'Graba la consulta y la IA genera el borrador'}</div>
                    </div>
                    <span className={`badge ${grabando?'badge-red':iaProcesando?'badge-amber':'badge-blue'}`}>{grabando?'Grabando':iaProcesando?'Procesando':'IA lista'}</span>
                  </div>
                )}

                {/* Borrador IA */}
                {iaBorrador&&(
                  <div style={{border:`1px solid var(--navy-lt)`,borderRadius:9,padding:12,background:'var(--blue-lt)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--navy)',marginBottom:6}}>✨ Borrador generado por IA — revisa antes de aplicar</div>
                    <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.7,maxHeight:100,overflowY:'auto',marginBottom:8,whiteSpace:'pre-wrap'}}>{iaBorrador.borrador||iaBorrador.evolucion}</div>
                    {iaBorrador.diagnosticos?.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>{iaBorrador.diagnosticos.map((d,i)=><span key={i} className="badge badge-blue">{d.cod_cie10} — {d.descripcion}</span>)}</div>}
                    {iaBorrador.signos_vitales&&Object.keys(iaBorrador.signos_vitales).length>0&&(
                      <div style={{marginBottom:8,padding:'6px 10px',background:'var(--green-lt)',border:'1px solid var(--green)',borderRadius:6}}>
                        <div style={{fontSize:10,fontWeight:700,color:'var(--green)',marginBottom:4}}>📊 Signos vitales detectados — se llenaron automáticamente</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:5}}>{Object.entries(iaBorrador.signos_vitales).map(([k,v])=><span key={k} className="badge badge-green">{k.replace(/_/g,' ')}: {v}</span>)}</div>
                      </div>
                    )}
                    <div style={{display:'flex',gap:6}}>
                      <button type="button" className="btn-mo-pri" style={{fontSize:11,padding:'4px 12px'}}
                        onClick={()=>{ setForm(f=>({...f,EVOLUCION:iaBorrador.borrador||iaBorrador.evolucion||'',
                          diagnosticos:[...(f.diagnosticos||[]),...(iaBorrador.diagnosticos||[]).map(x=>({COD_CIE10:x.cod_cie10,POR_CONFIRMARSE:'V',CIE10_DESC:x.descripcion}))]}));
                          setIaBorrador(null); }}>Aplicar borrador</button>
                      <button type="button" className="btn-mo-sec" style={{fontSize:11,padding:'4px 12px'}} onClick={()=>setIaBorrador(null)}>Descartar</button>
                    </div>
                  </div>
                )}

                {/* Evolución */}
                <div className="form-group">
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,flexWrap:'wrap',gap:6}}>
                    <div className="mo-sec" style={{margin:0}}>Evolución *</div>
                    <div style={{display:'flex',gap:4,alignItems:'center'}}>
                      {!editCod && registros.length>0 && (
                        <button type="button" className="btn-mo-sec" style={{fontSize:11,padding:'3px 10px'}}
                          onClick={()=>{
                            const ultima = registros[0];
                            if (!ultima?.EVOLUCION) { setMsg('La consulta anterior no tiene evolución guardada'); return; }
                            setTipoEvol('libre');
                            setForm(f=>({...f, EVOLUCION: ultima.EVOLUCION}));
                          }}>
                          <i className="ti ti-copy" aria-hidden="true"></i> Copiar evolución anterior
                        </button>
                      )}
                      <button type="button" className={tipoEvol==='libre'?'btn-mo-pri':'btn-mo-sec'} style={{fontSize:11,padding:'3px 10px'}} onClick={()=>setTipoEvol('libre')}>Libre</button>
                      <button type="button" className={tipoEvol==='soap'?'btn-mo-pri':'btn-mo-sec'} style={{fontSize:11,padding:'3px 10px'}} onClick={()=>setTipoEvol('soap')}>S.O.A.P.</button>
                    </div>
                  </div>
                  {tipoEvol==='libre'
                    ?<div style={{position:'relative'}}>
                        <textarea className="form-control" style={{height:evoHeight,resize:'none',width:'100%'}} placeholder="Motivo de consulta, examen físico, plan terapéutico..." value={form.EVOLUCION||''} onChange={e=>setForm(f=>({...f,EVOLUCION:e.target.value}))}/>
                        <div
                          onPointerDown={onEvoResizeStart}
                          onPointerMove={onEvoResizeMove}
                          onPointerUp={onEvoResizeEnd}
                          onPointerCancel={onEvoResizeEnd}
                          style={{position:'absolute',right:2,bottom:2,width:18,height:18,cursor:'ns-resize',touchAction:'none',display:'flex',alignItems:'flex-end',justifyContent:'flex-end',padding:2}}
                          aria-label="Redimensionar"
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M10 0 L10 10 L0 10 Z" fill="#9aa5b1"/></svg>
                        </div>
                      </div>
                    :<div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {[['S','Subjetivo — lo que el paciente refiere'],['O','Objetivo — examen físico y signos'],['A','Análisis — diagnóstico e impresión'],['P','Plan — tratamiento y próximos pasos']].map(([k,ph])=>(
                        <div key={k} style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                          <div style={{width:26,height:26,borderRadius:7,background:'var(--navy)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0,marginTop:6}}>{k}</div>
                          <textarea className="form-control" rows={3} placeholder={ph} value={soapForm[k]||''} onChange={e=>setSoapForm(f=>({...f,[k]:e.target.value}))}/>
                        </div>
                      ))}
                    </div>}
                </div>

                {fmtConsulta!=='002'&&(<>
                {/* Objetivo · Examen físico — visible solo en modo libre (en 002 se usa la sección H detallada) */}
                <div className="form-group">
                  <div className="mo-sec">Objetivo · Examen físico</div>
                  <textarea className="form-control" rows={4} placeholder="Hallazgos del examen físico por sistemas..."
                    value={form002.EXAM_FISICO_DET} onChange={e=>setForm002(f=>({...f,EXAM_FISICO_DET:e.target.value}))}/>
                </div>
                </>)}

                {fmtConsulta!=='002'&&(<>
                {/* RAS — Revisión de aparatos y sistemas — visible solo en modo libre (en 002 se usa la sección G detallada) */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">RAS · Revisión de aparatos y sistemas</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll-lg">
                    <div className="chk002-grid">
                      {['PIEL_ANEXOS','RESPIRATORIO','DIGESTIVO','MUSCULO_ESQ','HEMO_LINFATICO','ORGANOS_SENTIDOS','CARDIO_VASCULAR','GENITO_URINARIO','ENDOCRINO','NERVIOSO'].map(k=>(
                        <div key={k} className={`chk002-item${form002.REV_ORGANOS_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,REV_ORGANOS_FLAGS:{...f.REV_ORGANOS_FLAGS,[k]:!f.REV_ORGANOS_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.REV_ORGANOS_FLAGS[k]?' on':''}`}>{form002.REV_ORGANOS_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{PIEL_ANEXOS:'Piel-Anexos',RESPIRATORIO:'Respiratorio',DIGESTIVO:'Digestivo',MUSCULO_ESQ:'Músculo-Esq.',HEMO_LINFATICO:'Hemo-Linfático',ORGANOS_SENTIDOS:'Órg. Sentidos',CARDIO_VASCULAR:'Cardio-Vasc.',GENITO_URINARIO:'Génito-Urin.',ENDOCRINO:'Endocrino',NERVIOSO:'Nervioso'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <textarea className="form-control" rows={2} placeholder="Describa hallazgos por sistema..."
                      value={form002.REV_ORGANOS_DET} onChange={e=>setForm002(f=>({...f,REV_ORGANOS_DET:e.target.value}))}/>
                  </div>
                </div>
                </>)}

                {/* Análisis — visible solo en modo libre y fuera del Formulario 002 MSP (SOAP y el 002 ya tienen su propio campo A) */}
                {tipoEvol==='libre'&&fmtConsulta!=='002'&&(
                  <div className="form-group">
                    <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                      <div style={{width:26,height:26,borderRadius:7,background:'var(--navy)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0,marginTop:6}}>A</div>
                      <div style={{flex:1}}>
                        <div className="mo-sec" style={{marginBottom:4}}>Análisis</div>
                        <textarea className="form-control" rows={3} placeholder="Diagnóstico e impresión clínica..." value={libreAP.A||''} onChange={e=>setLibreAP(f=>({...f,A:e.target.value}))}/>
                      </div>
                    </div>
                  </div>
                )}

                {fmtConsulta!=='002'&&(<>
                {/* Diagnósticos CIE-10 con PRE/DEF */}
                <div>
                  <div className="mo-sec">Diagnósticos CIE-10</div>
                  <input className="form-control" placeholder="Buscar por nombre o código CIE-10..." value={cie10Q} onChange={e=>onCie10(e.target.value)}/>
                  {cie10S.length>0&&<div className="cie-box">{cie10S.map(d=><div key={d.COD} className="cie-it" onMouseDown={()=>addDiag(d)}><strong style={{color:'var(--navy)'}}>{d.COD_CIE10||d.COD}</strong> — {d.DIAGNOSTICO}</div>)}</div>}
                  {form.diagnosticos.length>0&&(
                    <div style={{display:'flex',flexDirection:'column',gap:5,marginTop:8}}>
                      {form.diagnosticos.map((d,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--border)'}}>
                          <span className="dx-cod">{d.COD_CIE10}</span>
                          <span className="dx-desc" style={{flex:1}}>{d.desc||d.CIE10_DESC}</span>
                          <span className={`dx-pill-pre${d.C_TIPO_DIAGNOSTICO==='PRE'?' sel':''}`}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.map((x,j)=>j===i?{...x,C_TIPO_DIAGNOSTICO:'PRE'}:x)}))}>PRE</span>
                          <span className={`dx-pill-def${d.C_TIPO_DIAGNOSTICO!=='PRE'?' sel':''}`}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.map((x,j)=>j===i?{...x,C_TIPO_DIAGNOSTICO:'DEF'}:x)}))}>DEF</span>
                          <span style={{color:'var(--red)',cursor:'pointer',fontSize:13,marginLeft:2}}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.filter((_,j)=>j!==i)}))}>✕</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </>)}

                {/* Plan — visible solo en modo libre y fuera del Formulario 002 MSP (SOAP y el 002 ya tienen su propio campo P) */}
                {tipoEvol==='libre'&&fmtConsulta!=='002'&&(
                  <div className="form-group">
                    <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                      <div style={{width:26,height:26,borderRadius:7,background:'var(--navy)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0,marginTop:6}}>P</div>
                      <div style={{flex:1}}>
                        <div className="mo-sec" style={{marginBottom:4}}>Plan</div>
                        <textarea className="form-control" rows={3} placeholder="Tratamiento y próximos pasos..." value={libreAP.P||''} onChange={e=>setLibreAP(f=>({...f,P:e.target.value}))}/>
                      </div>
                    </div>
                  </div>
                )}

                {fmtConsulta==='libre'&&(<>
                  <div className="form-group">
                    <div className="mo-sec">Signos de alarma</div>
                    <input className="form-control" placeholder="Ej: fiebre, sangrado, dolor intenso..."
                      value={form.SIGNOS_ALARMA||''} onChange={e=>setForm(f=>({...f,SIGNOS_ALARMA:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <div className="mo-sec">Observaciones</div>
                    <input className="form-control" placeholder="Observaciones adicionales..."
                      value={form.OBSERVACIONES||''} onChange={e=>setForm(f=>({...f,OBSERVACIONES:e.target.value}))}/>
                  </div>
                </>)}

              {/* ── SECCIONES 002 MSP (solo si fmtConsulta==='002') ── */}
              {modalTab==='consulta'&&fmtConsulta==='002'&&(<>

                {/* G. Revisión de órganos */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">G. Revisión actual de órganos y sistemas</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll-lg">
                    <div className="chk002-grid">
                      {['PIEL_ANEXOS','RESPIRATORIO','DIGESTIVO','MUSCULO_ESQ','HEMO_LINFATICO','ORGANOS_SENTIDOS','CARDIO_VASCULAR','GENITO_URINARIO','ENDOCRINO','NERVIOSO'].map(k=>(
                        <div key={k} className={`chk002-item${form002.REV_ORGANOS_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,REV_ORGANOS_FLAGS:{...f.REV_ORGANOS_FLAGS,[k]:!f.REV_ORGANOS_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.REV_ORGANOS_FLAGS[k]?' on':''}`}>{form002.REV_ORGANOS_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{PIEL_ANEXOS:'Piel-Anexos',RESPIRATORIO:'Respiratorio',DIGESTIVO:'Digestivo',MUSCULO_ESQ:'Músculo-Esq.',HEMO_LINFATICO:'Hemo-Linfático',ORGANOS_SENTIDOS:'Órg. Sentidos',CARDIO_VASCULAR:'Cardio-Vasc.',GENITO_URINARIO:'Génito-Urin.',ENDOCRINO:'Endocrino',NERVIOSO:'Nervioso'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <textarea className="form-control" rows={2} placeholder="Describa hallazgos por sistema..."
                      value={form002.REV_ORGANOS_DET} onChange={e=>setForm002(f=>({...f,REV_ORGANOS_DET:e.target.value}))}/>
                  </div>
                </div>

                {/* H. Examen físico */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">H. Examen físico regional y sistémico</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll-lg">
                    <div className="sub-hdr002">Regional</div>
                    <div className="chk002-grid">
                      {['PIEL_FANERAS','CABEZA','OJOS','OIDOS','NARIZ','BOCA','OROFARINGE','CUELLO','AXILAS_MAMAS','TORAX','ABDOMEN','INGLE_PERINE','MIEMBROS_SUP','MIEMBROS_INF','COLUMNA'].map(k=>(
                        <div key={k} className={`chk002-item${form002.EXAM_REGIONAL_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,EXAM_REGIONAL_FLAGS:{...f.EXAM_REGIONAL_FLAGS,[k]:!f.EXAM_REGIONAL_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.EXAM_REGIONAL_FLAGS[k]?' on':''}`}>{form002.EXAM_REGIONAL_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{PIEL_FANERAS:'Piel-Faneras',CABEZA:'Cabeza',OJOS:'Ojos',OIDOS:'Oídos',NARIZ:'Nariz',BOCA:'Boca',OROFARINGE:'Orofaringe',CUELLO:'Cuello',AXILAS_MAMAS:'Axilas-Mamas',TORAX:'Tórax',ABDOMEN:'Abdomen',INGLE_PERINE:'Ingle-Periné',MIEMBROS_SUP:'Miembros Sup.',MIEMBROS_INF:'Miembros Inf.',COLUMNA:'Columna'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <div className="sub-hdr002">Sistémico</div>
                    <div className="chk002-grid">
                      {['URINARIO','RESPIRATORIO','MUSCULO_ESQ','HEMO_LINFATICO','VASCULAR','DIGESTIVO','ENDOCRINO','NEUROLOGICO'].map(k=>(
                        <div key={k} className={`chk002-item${form002.EXAM_SISTEMICO_FLAGS[k]?' on':''}`}
                          onClick={()=>setForm002(f=>({...f,EXAM_SISTEMICO_FLAGS:{...f.EXAM_SISTEMICO_FLAGS,[k]:!f.EXAM_SISTEMICO_FLAGS[k]}}))}>
                          <div className={`chk002-box${form002.EXAM_SISTEMICO_FLAGS[k]?' on':''}`}>{form002.EXAM_SISTEMICO_FLAGS[k]?'✓':''}</div>
                          <span className="chk002-lbl">{{URINARIO:'Urinario',RESPIRATORIO:'Respiratorio',MUSCULO_ESQ:'Músculo-Esq.',HEMO_LINFATICO:'Hemo-Linfático',VASCULAR:'Vascular',DIGESTIVO:'Digestivo',ENDOCRINO:'Endocrino',NEUROLOGICO:'Neurológico'}[k]}</span>
                        </div>
                      ))}
                    </div>
                    <textarea className="form-control" rows={2} placeholder="Describa hallazgos del examen físico..." style={{marginTop:7}}
                      value={form002.EXAM_FISICO_DET} onChange={e=>setForm002(f=>({...f,EXAM_FISICO_DET:e.target.value}))}/>
                  </div>
                </div>

                {/* I. Diagnósticos CIE-10 con PRE/DEF */}
                <div>
                  <div className="mo-sec">Diagnósticos CIE-10</div>
                  <input className="form-control" placeholder="Buscar por nombre o código CIE-10..." value={cie10Q} onChange={e=>onCie10(e.target.value)}/>
                  {cie10S.length>0&&<div className="cie-box">{cie10S.map(d=><div key={d.COD} className="cie-it" onMouseDown={()=>addDiag(d)}><strong style={{color:'var(--navy)'}}>{d.COD_CIE10||d.COD}</strong> — {d.DIAGNOSTICO}</div>)}</div>}
                  {form.diagnosticos.length>0&&(
                    <div style={{display:'flex',flexDirection:'column',gap:5,marginTop:8}}>
                      {form.diagnosticos.map((d,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--border)'}}>
                          <span className="dx-cod">{d.COD_CIE10}</span>
                          <span className="dx-desc" style={{flex:1}}>{d.desc||d.CIE10_DESC}</span>
                          <span className={`dx-pill-pre${d.C_TIPO_DIAGNOSTICO==='PRE'?' sel':''}`}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.map((x,j)=>j===i?{...x,C_TIPO_DIAGNOSTICO:'PRE'}:x)}))}>PRE</span>
                          <span className={`dx-pill-def${d.C_TIPO_DIAGNOSTICO!=='PRE'?' sel':''}`}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.map((x,j)=>j===i?{...x,C_TIPO_DIAGNOSTICO:'DEF'}:x)}))}>DEF</span>
                          <span style={{color:'var(--red)',cursor:'pointer',fontSize:13,marginLeft:2}}
                            onClick={()=>setForm(f=>({...f,diagnosticos:f.diagnosticos.filter((_,j)=>j!==i)}))}>✕</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* J. Plan de tratamiento */}
                <div className="sec002">
                  <div className="sec002-hdr"><span className="sec002-ttl">J. Plan de tratamiento</span><span className="sec002-ind">↕</span></div>
                  <div className="sec002-scroll">
                    <textarea className="form-control" rows={3} placeholder="Diagnóstico, terapéutico y educacional..."
                      value={form002.PLAN_TRATAMIENTO} onChange={e=>setForm002(f=>({...f,PLAN_TRATAMIENTO:e.target.value}))}/>
                  </div>
                </div>
              </>)}

              </>)}

              {modalTab==='receta'&&(
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={()=>setRecItems(r=>[...r,{MEDICAMENTO:'',CANTIDAD:'',MANANA:'',MEDIODIA:'',NOCHE:'',DIAS:'',INDICACION:''}])}>+ Medicamento</button>
                    {recetasDet.length>0&&(
                      <button type="button" className="btn-mo-sec btn-sm" onClick={()=>{
                        const ultima = recetasDet.find(r => (r.FECHA||'').slice(0,10) <= hoyLocalISO()) || recetasDet[0];
                        if (!ultima?.items?.length) { setMsg('La última receta no tiene medicamentos guardados'); return; }
                        setRecItems(ultima.items.map(it=>({
                          MEDICAMENTO: it.MEDICAMENTO, COD_ITEM: it.COD_ITEM||null, CANTIDAD: it.CANTIDAD||'',
                          MANANA: it.MANANA??'', MEDIODIA: it.MEDIODIA??'', NOCHE: it.NOCHE??'', DIAS: it.DIAS??'',
                          INDICACION: it.INDICACION||''
                        })).map(x=>({...x,CANTIDAD_MANUAL:cantidadEsManual(x)})));
                      }}>
                        <i className="ti ti-copy" aria-hidden="true"></i> Copiar última receta
                      </button>
                    )}
                    <button type="button" className="btn btn-sm" onClick={()=>setRmAbierto(s=>!s)}>
                      <i className="ti ti-calendar-repeat" aria-hidden="true"></i> Receta mensual
                    </button>
                    <button type="button" className="btn btn-sm" onClick={()=>setShowPl(s=>!s)}>📋 Plantillas {plantillas.length>0&&`(${plantillas.length})`}</button>
                    {recetaItems.filter(r=>r.MEDICAMENTO?.trim()).length>0&&(<>
                      <button type="button" className="btn btn-sm" onClick={imprimirReceta}>🖨️ Imprimir</button>
                      <div style={{flex:1}}/>
                      <input className="form-control" style={{width:160,fontSize:11}} placeholder="Nombre plantilla..." value={nombrePlantilla} onChange={e=>setNomPl(e.target.value)}/>
                      <button type="button" className="btn btn-sm" disabled={savingPl} onClick={guardarComoPlantilla}>💾 Guardar plantilla</button>
                    </>)}
                  </div>
                  {rmAbierto&&(
                    <div style={{background:'var(--bg)',borderRadius:9,padding:14,border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                        <div style={{fontSize:13,fontWeight:700}}><i className="ti ti-calendar-repeat" aria-hidden="true"></i> Receta mensual</div>
                        <button type="button" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)'}} onClick={()=>setRmAbierto(false)} aria-label="Cerrar">
                          <i className="ti ti-x" aria-hidden="true"></i>
                        </button>
                      </div>
                      <div className="form-group" style={{position:'relative',marginBottom:8}}>
                        <label className="form-label">Medicamento *</label>
                        <input className="form-control" placeholder="Buscar o escribir..." value={rm.MEDICAMENTO}
                          onChange={e=>{setRm(f=>({...f,MEDICAMENTO:e.target.value,COD_ITEM:null}));buscarMed('rm',e.target.value);}}
                          onBlur={()=>setTimeout(()=>setMedSugs(s=>({...s,rm:[]})),200)}/>
                        {medSugs.rm?.length>0&&(
                          <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:500,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:7,maxHeight:140,overflowY:'auto'}}>
                            {medSugs.rm.map(m=>(
                              <div key={m.COD} style={{padding:'7px 10px',cursor:'pointer',fontSize:12,borderBottom:'1px solid var(--bg)'}}
                                onMouseDown={()=>{const nombreCompleto=m.MEDICAMENTO+(m.NOMBRE_COMERCIAL?` (${m.NOMBRE_COMERCIAL})`:'');setRm(f=>({...f,MEDICAMENTO:nombreCompleto,COD_ITEM:m.COD}));setMedSugs(s=>({...s,rm:[]}))}}>
                                {m.MEDICAMENTO}{m.NOMBRE_COMERCIAL ? ` (${m.NOMBRE_COMERCIAL})` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8,marginBottom:8}}>
                        <div className="form-group">
                          <label className="form-label">Cantidad por receta</label>
                          <input type="number" min="1" step="1" className="form-control" value={rm.CANTIDAD} onChange={e=>setRm(f=>({...f,CANTIDAD:e.target.value}))}/>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Primera receta</label>
                          <input type="date" className="form-control" value={rm.INICIO} onChange={e=>setRm(f=>({...f,INICIO:e.target.value}))}/>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Repetir cada</label>
                          <select className="form-control" value={rm.CADA} onChange={e=>setRm(f=>({...f,CADA:e.target.value}))}>
                            <option value="1">1 mes</option><option value="2">2 meses</option><option value="3">3 meses</option><option value="6">6 meses</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Cuántas recetas</label>
                          <input type="number" min="1" max="24" step="1" className="form-control" value={rm.N} onChange={e=>setRm(f=>({...f,N:e.target.value}))}/>
                        </div>
                      </div>
                      <div className="form-group" style={{marginBottom:10}}>
                        <label className="form-label">Indicación</label>
                        <input className="form-control" placeholder="Ej: UNA CÁPSULA AL MES" value={rm.INDICACION} onChange={e=>setRm(f=>({...f,INDICACION:e.target.value}))}/>
                      </div>
                      {rmFechas.length===0
                        ?<div style={{fontSize:12,color:'var(--text3)'}}>Elige la fecha de la primera receta.</div>
                        :<>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                            <span style={{fontSize:12,fontWeight:700}}>Se generarán {rmFechas.length} receta{rmFechas.length>1?'s':''}</span>
                            <span style={{fontSize:11,color:'var(--text3)'}}>Vigencia de {VIGENCIA_RECETA_DIAS} días desde cada fecha</span>
                          </div>
                          {rmFechas.map((f,i)=>{
                            const dom = f && isoADate(f).getDay()===0;
                            return (
                              <div key={i} style={{display:'grid',gridTemplateColumns:'28px minmax(0,1.6fr) minmax(0,.8fr) minmax(0,.8fr)',gap:8,alignItems:'center',padding:'6px 0',borderTop:'1px solid var(--border)',fontSize:12}}>
                                <span style={{color:'var(--text3)'}}>{i+1}</span>
                                <div>
                                  <input type="date" className="form-control" style={{padding:'3px 6px',fontSize:12}} value={f}
                                    onChange={e=>setRmFechas(a=>a.map((x,j)=>j===i?e.target.value:x))}/>
                                  <div style={{fontSize:11,color:dom?'var(--amber)':'var(--text3)',marginTop:2}}>
                                    {fmtFechaCorta(f)}{dom?' · domingo, fuera de horario':''}
                                  </div>
                                </div>
                                <span># {rm.CANTIDAD||'—'}</span>
                                <span style={{color:'var(--text3)'}}>hasta {f?fmtFechaCorta(sumarDiasISO(f,VIGENCIA_RECETA_DIAS)).replace(/^[^,]*,?\s*/,''):'—'}</span>
                              </div>
                            );
                          })}
                        </>}
                      <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
                        <button type="button" className="btn btn-primary btn-sm" disabled={rmGenerando||rmFechas.length===0} onClick={generarRecetaMensual}>
                          <i className="ti ti-file-text" aria-hidden="true"></i> {rmGenerando?'Generando...':`Generar ${rmFechas.length||''} receta${rmFechas.length===1?'':'s'} en un PDF`}
                        </button>
                      </div>
                    </div>
                  )}
                  {showPlantillas&&(
                    <div style={{background:'var(--bg)',borderRadius:9,padding:12,border:'1px solid var(--border)'}}>
                      <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>Mis plantillas</div>
                      {plantillas.length===0?<div style={{fontSize:12,color:'var(--text3)',textAlign:'center',padding:12}}>No tienes plantillas guardadas</div>
                        :<div style={{display:'flex',flexDirection:'column',gap:6}}>
                          {plantillas.map(pl=>(
                            <div key={pl.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--surface)',borderRadius:8,border:'1px solid var(--border)'}}>
                              <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600}}>{pl.NOMBRE}</div><div style={{fontSize:11,color:'var(--text3)'}}>{pl.total_items} medicamento(s)</div></div>
                              <button type="button" className="btn btn-primary btn-sm" onClick={()=>aplicarPlantilla(pl)}>Aplicar</button>
                              <button type="button" style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:13}}
                                onClick={async()=>{await api.delete(`/historia/plantillas-receta/${pl.COD}`);cargarPlantillas();}}>✕</button>
                            </div>
                          ))}
                        </div>}
                    </div>
                  )}
                  {recetaItems.length===0&&!showPlantillas&&(
                    <div style={{textAlign:'center',padding:'30px 20px',color:'var(--text3)',fontSize:13,border:'1px dashed var(--border)',borderRadius:9}}>
                      ℞ No hay medicamentos en esta receta<br/><span style={{fontSize:11}}>Usa "+ Medicamento" o carga una plantilla</span>
                    </div>
                  )}
                  {recetaItems.map((it,i)=>(
                    <div key={i} style={{background:'var(--bg)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--navy)'}}>℞ {i+1}</span>
                        <button type="button" style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:12}} onClick={()=>setRecItems(r=>r.filter((_,j)=>j!==i))}>✕ Quitar</button>
                      </div>
                        <div className="form-group" style={{position:'relative',marginBottom:8}}>
                          <label className="form-label">Medicamento *</label>
                          <div style={{display:'flex',gap:4}}>
                            <input className="form-control" placeholder="Buscar o escribir..." value={it.MEDICAMENTO}
                              onChange={e=>{setRecItems(r=>r.map((x,j)=>j===i?{...x,MEDICAMENTO:e.target.value,COD_ITEM:null}:x));buscarMed(i,e.target.value);}}
                              onBlur={()=>setTimeout(()=>setMedSugs(s=>({...s,[i]:[]})),200)}/>
                            {it.MEDICAMENTO?.trim()&&(!medSugs[i]||!medSugs[i].find(m=>m.MEDICAMENTO===it.MEDICAMENTO))&&(
                              <button type="button" className="btn btn-sm" style={{fontSize:10,padding:'4px 6px',flexShrink:0}} title="Guardar en vademécum" onClick={()=>guardarEnVademecum(it.MEDICAMENTO)}>💾</button>
                            )}
                          </div>
                          {medSugs[i]?.length>0&&(
                            <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:500,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:7,maxHeight:140,overflowY:'auto'}}>
                              {medSugs[i].map(m=>(
                                <div key={m.COD} style={{padding:'7px 10px',cursor:'pointer',fontSize:12,borderBottom:'1px solid var(--bg)'}}
                                  onMouseDown={()=>{const nombreCompleto=m.MEDICAMENTO+(m.NOMBRE_COMERCIAL?` (${m.NOMBRE_COMERCIAL})`:'');setRecItems(r=>r.map((x,j)=>j===i?{...x,MEDICAMENTO:nombreCompleto,COD_ITEM:m.COD}:x));setMedSugs(s=>({...s,[i]:[]}))}}>
                                  {m.MEDICAMENTO}{m.NOMBRE_COMERCIAL ? ` (${m.NOMBRE_COMERCIAL})` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1.2fr',gap:8,marginBottom:8}}>
                          <div className="form-group">
                            <label className="form-label">Mañana</label>
                            <input type="number" min="0" step="0.5" className="form-control" placeholder="0" value={it.MANANA}
                              onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,MANANA:e.target.value}:x))}/>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Medio día</label>
                            <input type="number" min="0" step="0.5" className="form-control" placeholder="0" value={it.MEDIODIA}
                              onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,MEDIODIA:e.target.value}:x))}/>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Noche</label>
                            <input type="number" min="0" step="0.5" className="form-control" placeholder="0" value={it.NOCHE}
                              onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,NOCHE:e.target.value}:x))}/>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Días</label>
                            <input type="number" min="0" step="1" className="form-control" placeholder="0" value={it.DIAS}
                              onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,DIAS:e.target.value}:x))}/>
                          </div>
                          <div className="form-group">
                            <label className="form-label"># Total{it.CANTIDAD_MANUAL&&<span style={{marginLeft:4,fontWeight:400,color:'var(--text3)'}}>(manual)</span>}</label>
                            <input type="number" min="0" step="1" className="form-control"
                              title="Se calcula solo con la dosis. Escríbelo tú para esquemas como 1 al mes o cada 3 meses (ej. 6). Bórralo para volver al cálculo automático."
                              placeholder={String(totalPorDosis(it)||'')}
                              value={it.CANTIDAD_MANUAL ? (it.CANTIDAD??'') : (totalPorDosis(it)||'')}
                              onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,CANTIDAD:e.target.value,CANTIDAD_MANUAL:e.target.value!==''}:x))}/>
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Indicación</label>
                          <input className="form-control" placeholder="Ej: UNA TABLETA 8AM Y 8PM" value={it.INDICACION}
                            onChange={e=>setRecItems(r=>r.map((x,j)=>j===i?{...x,INDICACION:e.target.value}:x))}/>
                        </div>
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">Indicaciones generales</label>
                    <textarea className="form-control" rows={2}
                      placeholder="Dosis, frecuencia y vía de cada medicamento indicado..."
                      value={recetaIndicaciones} onChange={e=>setRecetaIndicaciones(e.target.value)}/>
                  </div>
                  {recetaItems.filter(r=>r.MEDICAMENTO?.trim()).length>0&&(
                    <div style={{fontSize:11,color:'var(--text3)',textAlign:'center'}}>{recetaItems.filter(r=>r.MEDICAMENTO?.trim()).length} medicamento(s) · se guardarán al confirmar</div>
                  )}
                </div>
              )}

              {modalTab==='lab'&&(
                <div>
                  <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>Para solicitar exámenes usa el módulo de Laboratorio.</div>
                  {labDet.length>0&&(<div style={{marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:700,marginBottom:6}}>Solicitudes recientes:</div>
                    {labDet.slice(0,3).map(sol=>(
                      <div key={sol.COD} style={{padding:'6px 10px',border:'1px solid var(--border)',borderRadius:7,marginBottom:4,fontSize:11}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                          <div style={{fontWeight:600}}>{sol.TIPO_DETERMINACION} — {fmtD(sol.FECHA_SOLICITUD)}</div>
                          {['MEDICO','ADMINISTRADOR'].includes(usuario?.tipo_usuario) && !(sol.detalles||[]).some(d=>d.RESULTADO) && (
                            <div style={{display:'flex',gap:4,flexShrink:0}}>
                              <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px'}}
                                onClick={()=>{setNueva(false);navigate(`/laboratorio?paciente=${codPac}&editar=${sol.COD}`);}}>
                                ✎
                              </button>
                              <button className="btn btn-sm" style={{fontSize:9,padding:'1px 6px',background:'var(--red-lt,#fdecea)',color:'var(--red,#c0392b)',border:'1px solid var(--red,#c0392b)'}}
                                onClick={async ()=>{
                                  if(!window.confirm('¿Anular este pedido de laboratorio?')) return;
                                  try{ await api.delete(`/laboratorio/solicitudes/${sol.COD}`); cargarLabDet(codPac); }
                                  catch(e){ alert(e.response?.data?.error||e.message); }
                                }}>
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>)}
                  <button className="btn btn-primary" style={{width:'100%'}} onClick={()=>{setNueva(false);navigate(`/laboratorio?paciente=${codPac}`);}}>🧪 Ir a solicitar exámenes</button>
                </div>
              )}

              {modalTab==='antecedente'&&(
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <div className="tipo-btn-row" style={{flexWrap:'wrap'}}>
                    {[['PAT','Patológicos'],['NPAT','No patológicos'],['FAM','Familiares'],['QRX','Quirúrgicos'],['GIN','Gineco-obstétrico'],['OTR','Otros']].map(([k,l])=>(
                      <button key={k} type="button" className={`tipo-btn${antecSub===k?' act':''}`} onClick={()=>setAntecSub(k)}>{l}</button>
                    ))}
                  </div>

                  {(antecSub==='PAT'||antecSub==='NPAT'||antecSub==='FAM')&&(<>
                    {antecSub==='FAM'&&<div className="form-group">
                      <label className="form-label">Parentesco (aplica a lo que marques ahora)</label>
                      <input className="form-control" placeholder="Ej: Madre, Padre, Hermano..." value={famParentesco} onChange={e=>setFamParentesco(e.target.value)}/>
                    </div>}
                    <div className="sub-hdr002">Condiciones frecuentes — clic para marcar/desmarcar</div>
                    <div className="chk002-grid">
                      {CATALOGO_ANTEC[antecSub].map(nombre=>{
                        const on = antecChecked(antecSub,nombre);
                        return (
                          <div key={nombre} className={`chk002-item${on?' on':''}`} onClick={()=>toggleAntecChip(antecSub,nombre)}>
                            <div className={`chk002-box${on?' on':''}`}>{on?'✓':''}</div>
                            <span className="chk002-lbl">{nombre}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Otro {antecSub==='PAT'?'patológico':antecSub==='NPAT'?'no patológico':'antecedente familiar'} no listado</label>
                      <textarea className="form-control" rows={2} placeholder="Describe el antecedente..." value={otrosAntec[antecSub]} onChange={e=>setOtrosAntec(o=>({...o,[antecSub]:e.target.value}))}/>
                    </div>
                  </>)}

                  {antecSub==='QRX'&&(<>
                    <div className="sub-hdr002">Cirugías previas</div>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {qrxItems.map((it,i)=>(
                        <div key={i} style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',background:'var(--bg)',borderRadius:8,padding:'8px 10px'}}>
                          <input className="form-control" style={{flex:2,minWidth:160}} placeholder="Procedimiento (ej: Apendicectomía)" value={it.PROCEDIMIENTO}
                            onChange={e=>setQrxItems(r=>r.map((x,j)=>j===i?{...x,PROCEDIMIENTO:e.target.value}:x))}/>
                          <input type="date" className="form-control" style={{width:140}} value={it.FECHA_EVENTO}
                            onChange={e=>setQrxItems(r=>r.map((x,j)=>j===i?{...x,FECHA_EVENTO:e.target.value}:x))}/>
                          <input className="form-control" style={{flex:2,minWidth:140}} placeholder="Observación (opcional)" value={it.OBSERVACION}
                            onChange={e=>setQrxItems(r=>r.map((x,j)=>j===i?{...x,OBSERVACION:e.target.value}:x))}/>
                          {qrxItems.length>1&&<button type="button" style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:13}}
                            onClick={()=>setQrxItems(r=>r.filter((_,j)=>j!==i))}>✕</button>}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="btn btn-sm" style={{alignSelf:'flex-start',fontSize:11}}
                      onClick={()=>setQrxItems(r=>[...r,{PROCEDIMIENTO:'',FECHA_EVENTO:'',OBSERVACION:''}])}>+ Agregar cirugía</button>
                  </>)}

                  {antecSub==='GIN'&&(
                    <div style={{background:'var(--blue-lt)',borderRadius:8,padding:'10px 12px',fontSize:11.5,color:'var(--navy)',display:'flex',gap:8,alignItems:'flex-start'}}>
                      <span>ℹ️</span>
                      <div>
                        Los antecedentes gineco-obstétricos tienen su propia pestaña completa (menarquia, gestas/partos/abortos, FUM, método anticonceptivo, PAP, VPH).
                        <div style={{marginTop:8}}><button type="button" className="btn-mo-pri" style={{fontSize:11.5}} onClick={()=>setModalTab('gineco')}>Ir a Antecedentes ginecológicos →</button></div>
                      </div>
                    </div>
                  )}

                  {antecSub==='OTR'&&(
                    <div className="form-group">
                      <div className="mo-sec">Cualquier otro antecedente relevante</div>
                      <textarea className="form-control" rows={5} placeholder="Describe el antecedente..." value={otrosAntec.OTR} onChange={e=>setOtrosAntec(o=>({...o,OTR:e.target.value}))}/>
                    </div>
                  )}

                  {antecs.length>0&&(<div>
                    <div className="mo-sec">Registrados</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                      {antecs.map((a,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:20,padding:'5px 6px 5px 11px',fontSize:11}}>
                          <span style={{fontWeight:600,color:'var(--navy)'}}>{TIPO_ICON[a.C_TIPO]}</span>
                          <span>{a.ANTECEDENTE}</span>
                          {a.C_PARENTESCO&&<span style={{color:'var(--text3)',fontSize:9.5}}>({a.C_PARENTESCO})</span>}
                          <button style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:12}}
                            onClick={async()=>{await api.delete(`/historia/antecedentes/${a.COD}`);cargarAntecs(codPac);}}>✕</button>
                        </div>
                      ))}
                    </div>
                  </div>)}
                </div>
              )}

              {modalTab==='alergia'&&(
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <div className="form-group">
                    <div className="mo-sec">Nueva alergia *</div>
                    <input className="form-control" placeholder="Ej: Penicilina, Polen, Mariscos..." value={formAlg.ALERGIA||''} onChange={e=>setFormAlg(f=>({...f,ALERGIA:e.target.value}))}/>
                  </div>
                  {alergias.length>0&&(<div>
                    <div className="mo-sec">Alergias registradas</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:7}}>
                      {alergias.map((a,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:6,background:'var(--red-lt)',borderRadius:8,padding:'5px 11px',border:'1px solid var(--border)'}}>
                          <span style={{fontSize:12,color:'var(--red)',fontWeight:600}}>⚠️ {a.ALERGIA}</span>
                          <button style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:13}}
                            onClick={async()=>{await api.delete(`/historia/alergias/${a.COD}`);cargarAlg(codPac);}}>✕</button>
                        </div>
                      ))}
                    </div>
                  </div>)}
                </div>
              )}

              {modalTab==='gineco'&&(
                <div style={{display:'flex',flexDirection:'column',gap:16}}>
                  <div style={{display:'flex',gap:14}}>
                    <div className="form-group" style={{flex:1}}>
                      <div className="mo-sec">Fecha última menstruación (FUM)</div>
                      <input type="date" className="form-control" value={formGineco.FUM||''} onChange={e=>setFormGineco(f=>({...f,FUM:e.target.value}))}/>
                    </div>
                    <div className="form-group" style={{flex:1}}>
                      <div className="mo-sec">Menarquia (edad)</div>
                      <input type="number" className="form-control" placeholder="Ej: 12" value={formGineco.MENARQUIA||''} onChange={e=>setFormGineco(f=>({...f,MENARQUIA:e.target.value}))}/>
                    </div>
                  </div>

                  <div>
                    <div className="mo-sec">Fórmula obstétrica</div>
                    <div style={{display:'flex',gap:14}}>
                      {[['GESTAS','Gestas'],['PARTOS','Partos'],['CESAREAS','Cesáreas'],['ABORTOS','Abortos']].map(([k,l])=>(
                        <div className="form-group" key={k} style={{flex:1}}>
                          <div style={{fontSize:9.5,color:'var(--text3)',textAlign:'center',marginBottom:4,textTransform:'uppercase'}}>{l}</div>
                          <input type="number" min="0" className="form-control" style={{textAlign:'center'}} value={formGineco[k]??''} onChange={e=>setFormGineco(f=>({...f,[k]:e.target.value}))}/>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="mo-sec">Método anticonceptivo actual</div>
                    <input className="form-control" placeholder="Ej: DIU hormonal, ninguno..." value={formGineco.METODO_ANTICONCEPTIVO||''} onChange={e=>setFormGineco(f=>({...f,METODO_ANTICONCEPTIVO:e.target.value}))}/>
                  </div>

                  <div style={{height:1,background:'var(--border)'}}/>
                  <div className="mo-sec" style={{fontSize:12}}>📋 Tamizaje cervical</div>

                  <div style={{display:'flex',gap:14}}>
                    <div className="form-group" style={{flex:1}}>
                      <div className="mo-sec">Último Papanicolaou — fecha</div>
                      <input type="date" className="form-control" value={formGineco.PAP_ULTIMA_FECHA||''} onChange={e=>setFormGineco(f=>({...f,PAP_ULTIMA_FECHA:e.target.value}))}/>
                    </div>
                    <div className="form-group" style={{flex:1}}>
                      <div className="mo-sec">Resultado</div>
                      <input className="form-control" placeholder="Ej: Normal, ASCUS..." value={formGineco.PAP_ULTIMO_RESULTADO||''} onChange={e=>setFormGineco(f=>({...f,PAP_ULTIMO_RESULTADO:e.target.value}))}/>
                    </div>
                    <div className="form-group" style={{flex:1}}>
                      <div className="mo-sec">Último VPH</div>
                      <input className="form-control" placeholder="Ej: Negativo, Positivo..." value={formGineco.VPH_ULTIMO_RESULTADO||''} onChange={e=>setFormGineco(f=>({...f,VPH_ULTIMO_RESULTADO:e.target.value}))}/>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="mo-sec">Observaciones</div>
                    <textarea className="form-control" rows={2} value={formGineco.OBSERVACIONES||''} onChange={e=>setFormGineco(f=>({...f,OBSERVACIONES:e.target.value}))}/>
                  </div>
                </div>
              )}
            </div>

            <div className="mo-ftr">
              {(modalTab==='consulta'||modalTab==='receta')&&autoguardadoTexto&&
                <span style={{flex:1,fontSize:11,color:'var(--text3)',alignSelf:'center'}}>💾 {autoguardadoTexto}</span>}
              <button className="btn-mo-sec" onClick={()=>{setNueva(false);setMsg('');setAntecToggle(new Set());setFamParentesco('');setOtrosAntec({PAT:'',NPAT:'',FAM:'',OTR:''});setQrxItems([{PROCEDIMIENTO:'',FECHA_EVENTO:'',OBSERVACION:''}]);}}>Cancelar</button>
              {(modalTab==='consulta'||modalTab==='receta')&&<button className="btn-mo-pri" disabled={saving} onClick={guardar}>{saving?'Guardando...':'Guardar consulta'}</button>}
              {modalTab==='antecedente'&&antecSub!=='GIN'&&<button className="btn-mo-pri" disabled={savingAntec} onClick={guardarAntecedentes}>{savingAntec?'Guardando...':'Guardar antecedentes'}</button>}
              {modalTab==='alergia'&&<button className="btn-mo-pri" disabled={saving} onClick={async()=>{
                if(!formAlg.ALERGIA?.trim()){setMsg('El nombre de la alergia es obligatorio');return;}
                setSaving(true);
                try{await api.post('/historia/alergias',{COD_PACIENTE:codPac,...formAlg});setMsg('✓ Alergia guardada');setFormAlg({});cargarAlg(codPac);}
                catch(e){setMsg('Error: '+(e.response?.data?.error||e.message));}finally{setSaving(false);}
              }}>{saving?'Guardando...':'Guardar alergia'}</button>}
              {modalTab==='gineco'&&<button className="btn-mo-pri" disabled={saving} onClick={async()=>{
                setSaving(true);
                try{await api.put(`/historia/antecedentes-ginecologicos/${codPac}`,formGineco);setMsg('✓ Antecedentes gineco-obstétricos guardados');cargarGineco(codPac);}
                catch(e){setMsg('Error: '+(e.response?.data?.error||e.message));}finally{setSaving(false);}
              }}>{saving?'Guardando...':'Guardar antecedentes'}</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
